import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getPreferredLanguage, setPreferredLanguage } from '../utils/userPreferences';

export type Language = 'cs' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const translations: Record<'en' | 'cs', { [key: string]: string }> = {
  en: {
    'settings.currency.selection': 'Currency Selection',
    'settings.currency.description': 'Choose your preferred currency for credit purchases and billing.',
    'settings.currency.czk': 'CZK (Czech Koruna)',
    'settings.currency.eur': 'EUR (Euro)',
    'settings.company.logo': 'Company Logo',
    'settings.company.logo.description': 'Upload your company logo. It appears on the job board and job listings.',
    'settings.company.logo.upload': 'Upload Logo',
    'settings.company.logo.hint': 'JPG, PNG or GIF · max 5 MB',
    'settings.company.logo.updated': 'Company logo updated.',
    'settings.company.logo.failed': 'Failed to upload company logo.',
    'settings.company.logo.owner.only': 'Only the business owner can change the company logo.',
    'settings.business.info': 'Business information',
    'settings.business.info.description': 'View and update all company details stored for your business.',
    'settings.business.name': 'Business name',
    'settings.business.name.placeholder': 'Enter company name',
    'settings.business.name.required': 'Business name is required.',
    'settings.business.updated': 'Business information updated.',
    'settings.business.update.failed': 'Failed to update business information.',
    'settings.business.id': 'Business ID',
    'settings.business.created': 'Created',
    'settings.business.owner': 'Owner',
    'settings.business.section.basic': 'Basic details',
    'settings.business.section.contact': 'Contact',
    'settings.business.section.legal': 'Legal identifiers',
    'settings.business.section.address': 'Registered address',
    'settings.business.description': 'Description',
    'settings.business.description.placeholder': 'Short description of your company',
    'settings.business.email': 'Company email',
    'settings.business.phone': 'Company phone',
    'settings.business.website': 'Website',
    'settings.business.companyName': 'Registered company name',
    'settings.business.companyName.placeholder': 'Start typing company name…',
    'settings.business.ico': 'Company ID (IČO)',
    'settings.business.dic': 'Tax ID (DIČ)',
    'settings.business.ares.hint': 'Suggestions from ARES. When you save with an IČO, all three fields are verified and the address is filled from the registry.',
    'settings.business.ares.company.required': 'Registered company name is required when IČO is set.',
    'settings.business.ares.mismatch': 'Company identifiers do not match ARES.',
    'profile.information': 'Profile Information',
    'first.name': 'First Name',
    'last.name': 'Last Name',
    'email.address': 'Email Address',
    'username': 'Username',
    'role': 'Role',
    'update.profile': 'Update Profile',
    'change.password': 'Change Password',
    'current.password': 'Current Password',
    'new.password': 'New Password',
    'password.min.length': 'Must be at least 6 characters long',
    'confirm.password': 'Confirm New Password',
    'contracts': 'Contracts',
    'enable.contracts': 'Enable Contracts',
    'contract.cycle.day': 'Contract Cycle Day',
    'default.contract.duration': 'Default Contract Duration',
    'monthly': 'Monthly',
    'yearly': 'Yearly',
    'unlimited': 'Unlimited',
    'custom': 'Custom',
    'save': 'Save',
    'saving': 'Saving...',
    'settings.saved': 'Settings saved!',
    'failed.save.settings': 'Failed to save settings',
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your account settings and preferences',
    'settings.tab.general': 'General',
    'settings.tab.preferences': 'Preferences',
    'settings.tab.personal': 'Personal',
    'settings.tab.features': 'Features',
    'settings.tab.billing': 'Billing',
    'settings.preferences.shifts.view': 'Default shifts view',
    'settings.preferences.shifts.view.description':
      'Your personal choice - whether Shifts opens in list or calendar view. This does not affect other users in the company.',
    'settings.preferences.employee.rate': 'Use employee hourly rate by default',
    'settings.preferences.employee.rate.description':
      'Business setting for owners and managers. When creating a shift, pre-check “Use employee hourly rate when set”. You can still change it on each shift. Each business has its own value.',
    'settings.preferences.saved': 'Preference saved.',
    'settings.preferences.save.failed': 'Failed to save preference.',
    'settings.calendar.title': 'Calendar sync',
    'settings.calendar.description':
      'Connect Google, Microsoft, Apple, and/or a generic ICS feed. Conflicts on Shifts use all of them. Approved shifts are written to Google and Microsoft (reconnect those if you connected before write support). Apple/ICS are read-only.',
    'settings.calendar.status.connected': 'Calendar connected',
    'settings.calendar.status.not.connected': 'Not connected',
    'settings.calendar.multi.hint': '{count} calendars synced - events are merged on Shifts.',
    'settings.calendar.disconnect': 'Disconnect',
    'settings.calendar.disconnected': 'Calendar disconnected.',
    'settings.calendar.disconnect.failed': 'Failed to disconnect calendar.',
    'settings.calendar.connected': 'Calendar connected successfully.',
    'settings.calendar.connect.failed': 'Failed to connect calendar.',
    'settings.calendar.connect': 'Connect',
    'settings.calendar.provider.ics': 'ICS',
    'settings.calendar.oauth.not.configured':
      'Google/Microsoft OAuth is not configured on the server. You can still connect via Apple or ICS URL.',
    'settings.calendar.oauth.setup.hint':
      'Add GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET and MICROSOFT_CLIENT_ID / MICROSOFT_CLIENT_SECRET to .env (see docs/calendar-oauth-setup.md), then restart the backend.',
    'settings.calendar.apple.title': 'Connect Apple Calendar',
    'settings.calendar.apple.help':
      'In Apple Calendar: Calendar → Share Calendar / Public Calendar → copy the subscribe URL (webcal:// or https://) and paste it here.',
    'settings.calendar.ics.title': 'Connect ICS feed',
    'settings.calendar.ics.help':
      'Paste any private or public ICS subscribe URL (Google secret address, Outlook ICS, other calendar apps).',
    'settings.ai.tokens.title': 'AI assistant access',
    'settings.ai.tokens.description':
      'Create a personal read-only token for Cursor, Claude, ChatGPT, or another MCP-compatible assistant.',
    'settings.ai.tokens.server.url': 'Remote MCP server URL',
    'settings.ai.tokens.server.url.claude': 'MCP server URL (paste into Claude / Cursor)',
    'settings.ai.tokens.claude.https':
      'Claude requires HTTPS and an OAuth consent screen. For local use run make mcp-tunnel, paste https://…/mcp, leave OAuth Client ID empty, then paste your ompli_pat_… on the Ompli authorize page.',
    'settings.ai.tokens.tunnel.missing.title': 'HTTPS tunnel is not running',
    'settings.ai.tokens.tunnel.missing.body':
      'Claude cannot use http://localhost. In a terminal run make mcp-tunnel, wait for the trycloudflare.com URL, then reopen this form — Ompli will show the tunnel /mcp address automatically.',
    'settings.ai.tokens.help.how.title': 'How it works',
    'settings.ai.tokens.help.how.body':
      'The token is personal and read-only. The assistant can only read data you already see in Ompli, with the same role permissions. It cannot create, edit, approve, or delete anything.',
    'settings.ai.tokens.help.steps.title': 'What to do',
    'settings.ai.tokens.help.steps.body':
      '1) Run make mcp-tunnel. 2) Create a token — copy the HTTPS MCP URL from the form. 3) Add it in Claude (custom connector; leave Client ID empty) or Cursor. 4) Paste the token on the Ompli OAuth screen / into config.',
    'settings.ai.tokens.one.time.hint':
      'Each token is personal and read-only. The secret value is shown only once after creation.',
    'settings.ai.tokens.one.time.warning':
      'Save this token now. For security reasons, Ompli will not show it again after you close this window.',
    'settings.ai.tokens.list.title': 'Personal access tokens',
    'settings.ai.tokens.list.description': 'Revoke any token at any time if you no longer trust that assistant or device.',
    'settings.ai.tokens.load.failed': 'Failed to load AI access tokens.',
    'settings.ai.tokens.empty': 'You have not created any AI access token yet.',
    'settings.ai.tokens.create': 'Create token',
    'settings.ai.tokens.created': 'AI access token created.',
    'settings.ai.tokens.create.failed': 'Failed to create AI access token.',
    'settings.ai.tokens.created.title': 'Copy your new token',
    'settings.ai.tokens.created.description':
      'Paste this token into your MCP client together with the server URL below.',
    'settings.ai.tokens.created.token.label': 'Your personal token',
    'settings.ai.tokens.modal.title': 'Create AI access token',
    'settings.ai.tokens.modal.description':
      'The token will use the same permissions as your Ompli account and can only read data.',
    'settings.ai.tokens.name': 'Token name',
    'settings.ai.tokens.name.placeholder': 'Example: Cursor on MacBook',
    'settings.ai.tokens.name.required': 'Token name is required.',
    'settings.ai.tokens.business': 'Business',
    'settings.ai.tokens.business.placeholder': 'Choose a business',
    'settings.ai.tokens.business.required': 'Please choose which business this token should use.',
    'settings.ai.tokens.copy': 'Copy token',
    'settings.ai.tokens.copy.url': 'Copy URL',
    'settings.ai.tokens.copied': 'Token copied.',
    'settings.ai.tokens.copy.failed': 'Failed to copy token.',
    'settings.ai.tokens.url.copied': 'MCP URL copied.',
    'settings.ai.tokens.url.copy.failed': 'Failed to copy MCP URL.',
    'settings.ai.tokens.expires': 'Expires',
    'settings.ai.tokens.last.used': 'Last used',
    'settings.ai.tokens.never.used': 'Never used',
    'settings.ai.tokens.revoke': 'Revoke',
    'settings.ai.tokens.revoked': 'AI access token revoked.',
    'settings.ai.tokens.revoke.failed': 'Failed to revoke AI access token.',
    'settings.ai.tokens.revoke.confirm': 'Revoke this AI access token immediately?',
    'shifts.calendar.conflict': 'Conflict',
    'shifts.calendar.free': 'Free',
    'shifts.calendar.conflict.with': 'Conflict: {title}',
    'shifts.calendar.busy': 'Busy',
    'shifts.calendar.personal': 'Personal calendar',
    'shifts.calendar.personal.allday': 'All day',
    'shifts.calendar.synced': 'Personal calendar synced · {count} events this month',
    'shifts.calendar.personal.hide': 'Hide calendar events',
    'shifts.calendar.personal.show': 'Show calendar events',
    'shifts.calendar.personal.hidden': 'Personal calendar events are hidden',
    'shifts.leave.request.submitted': 'Unregistration request submitted.',
    'shifts.leave.left': 'You left the shift.',
    'recent.activity.leave_request': 'Unregistration request',
    'settings.profile.description': 'Update your personal information',
    'settings.password.description': 'Change your account password',
    'settings.owner.only': 'Only the business owner can manage this setting.',
    'settings.managers.jobs.title': 'Manager job access',
    'settings.managers.jobs.tooltip': 'Control whether managers can post jobs and review applications. When off, managers will not see Job Board or Job Applications.',
    'settings.managers.jobs.enable': 'Allow managers to post and accept job offers',
    'settings.managers.announcements.title': 'Manager announcements',
    'settings.managers.announcements.tooltip': 'When enabled, managers can compose and send announcements to people in your business.',
    'settings.managers.announcements.enable': 'Allow managers to send announcements',
    'announcements': 'Announcements',
    'announcements.subtitle': 'Company and platform messages for you',
    'announcements.tab.inbox': 'Inbox',
    'announcements.tab.compose': 'Compose',
    'announcements.tab.sent': 'Sent',
    'announcements.compose.title': 'New announcement',
    'announcements.detail.title': 'Announcement',
    'announcements.search.placeholder': 'Search announcements…',
    'announcements.search.empty': 'No announcements match your search',
    'announcements.empty.title': 'No announcements yet',
    'announcements.empty.description': 'When someone sends you an announcement, it will show up here.',
    'announcements.sent.empty.title': 'No sent announcements',
    'announcements.sent.empty.description': 'Announcements you send will appear here.',
    'announcements.field.title': 'Title',
    'announcements.field.summary': 'Short description',
    'announcements.field.body': 'Full message',
    'announcements.field.body.placeholder': 'Optional rich text shown after opening…',
    'announcements.field.channels': 'Deliver to',
    'announcements.field.audience': 'Audience',
    'announcements.channel.both': 'App and web',
    'announcements.channel.app': 'App only',
    'announcements.channel.web': 'Web only',
    'announcements.audience.all': 'Everyone in the business',
    'announcements.audience.roles': 'By role',
    'announcements.audience.users': 'Specific people',
    'announcements.audience.users.empty': 'No people available',
    'announcements.audience.users.search': 'Search users…',
    'announcements.audience.platform.all': 'All users on Ompli',
    'announcements.audience.businesses': 'Selected businesses (+ roles)',
    'announcements.audience.roles.hint': 'Applies the selected roles across all businesses (or leave businesses empty).',
    'announcements.role.employee': 'Employees',
    'announcements.role.manager': 'Managers',
    'announcements.role.owner': 'Owners',
    'announcements.send': 'Send announcement',
    'announcements.sending': 'Sending…',
    'announcements.send.success': 'Announcement sent.',
    'announcements.send.error': 'Failed to send announcement.',
    'announcements.load.error': 'Failed to load announcements.',
    'announcements.no.body': 'No additional message.',
    'announcements.from': 'From {name}',
    'announcements.from.ompli': 'Ompli Team',
    'announcements.from.system': 'System',
    'announcements.from.unknown': 'Unknown',
    'announcements.feed.sent': 'sent',
    'announcements.feed.you.sent': 'You sent',
    'announcements.feed.system.sent': 'System sent',
    'announcements.feed.auto': 'Automatic',
    'announcements.feed.to.recipients': 'to {count} recipients',
    'announcements.recipients': '{count} recipients',
    'announcements.delete.confirm': 'Delete this announcement for everyone?',
    'announcements.delete.success': 'Announcement deleted.',
    'announcements.delete.error': 'Failed to delete announcement.',
    'admin.announcements.title': 'Announcements',
    'admin.announcements.subtitle':
      'Send platform messages to everyone, selected businesses, roles, or specific people',
    'admin.announcements.sent.empty.description':
      'Platform announcements you send and automatic system notifications will appear here.',
    'admin.announcements.compose': 'New announcement',
    'admin.announcements.auto.detail.title': 'Automatic notification',
    'admin.announcements.auto.changes': 'What changed',
    'admin.announcements.auto.shift': 'Related shift',
    'admin.announcements.auto.shift.missing': 'Shift #{id} is no longer available',
    'admin.announcements.auto.action.assigned': 'Assigned to a shift',
    'admin.announcements.auto.action.updated': 'Shift updated',
    'admin.announcements.auto.action.removed': 'Removed from shift',
    'admin.announcements.auto.action.denied': 'Shift request denied',
    'admin.announcements.auto.action.cancelled': 'Shift cancelled',
    'admin.announcements.auto.action.unknown': 'System notification',
    'admin.announcements.auto.change.title': 'Title changed',
    'admin.announcements.auto.change.description': 'Description changed',
    'admin.announcements.auto.change.location': 'Workplace changed',
    'admin.announcements.auto.change.position': 'Position changed',
    'admin.announcements.auto.change.time': 'Time changed',
    'admin.announcements.auto.change.pay.increased': 'Pay increased',
    'admin.announcements.auto.change.pay.decreased': 'Pay decreased',
    'admin.announcements.auto.change.pay': 'Pay changed',
    'admin.announcements.auto.change.employee.rate.on': 'Employee hourly rate enabled',
    'admin.announcements.auto.change.employee.rate.off': 'Employee hourly rate disabled',
    'admin.announcements.auto.change.capacity.increased': 'Capacity increased',
    'admin.announcements.auto.change.capacity.decreased': 'Capacity decreased',
    'admin.announcements.auto.change.approval': 'Approval rules changed',
    'admin.announcements.auto.change.leave': 'Leave policy changed',
    'admin.nav.announcements': 'Announcements',
    'admin.flag.managers.announcements': 'Managers can send announcements',
    'settings.managers.workplaces.title': 'Manager workplace access',
    'settings.managers.workplaces.tooltip': 'Choose what managers can do with workplaces. Delete stays owner-only.',
    'settings.managers.workplaces.edit.assigned': 'Edit workplaces they are assigned to',
    'settings.managers.workplaces.edit.all': 'Edit all workplaces',
    'settings.managers.workplaces.create': 'Create new workplaces',
    'settings.managers.workplaces.scope': 'Who can use these permissions',
    'settings.managers.workplaces.scope.all': 'All managers',
    'settings.managers.workplaces.scope.selected': 'Only selected managers',
    'settings.managers.workplaces.scope.pick': 'Select managers',
    'settings.managers.workplaces.scope.empty': 'No managers in this business yet.',
    'settings.credits.description': 'Manage your credit balance and view usage',
    'profile.upload.photo': 'Upload Photo',
    'profile.photo.hint': 'JPG, PNG or GIF · max 5 MB',
    'profile.photo.updated': 'Profile photo updated.',
    'profile.photo.failed': 'Failed to upload photo.',
    'profile.address.placeholder': 'Street, City, ZIP',
    'address.country': 'Country',
    'address.region': 'Region',
    'address.city': 'City',
    'address.street': 'Street',
    'address.zip': 'ZIP Code',
    'join.invited': 'You\'ve been invited!',
    'join.invited.to': 'You\'ve been invited to join',
    'join.login.and.join': 'Log in & Join',
    'join.create.account': 'Create Account & Join',
    'join.invalid.code': 'Invalid invite link',
    'join.go.to.login': 'Go to Login',
    'join.joining': 'Joining...',
    'join.loading': 'Checking invite...',
    'join.success': 'You\'re in!',
    'join.success.sub': 'You\'ve successfully joined',
    'join.redirecting': 'Redirecting to dashboard...',
    'join.registering.via.invite': 'Registering via invite link',
    'language.selection': 'Language Selection',
    'language.description': 'Your personal choice - this only changes the language for your account, not for other users.',
    'language.czech': 'Czech',
    'language.english': 'English',
    'language.saved': 'Language preference saved.',
    'language.save.failed': 'Failed to save language preference.',
    'settings.credits.management': 'Credits Management',
    'credits.balance': 'Credit Balance',
    'credits.available': 'Available',
    'credits.employees': 'Employees',
    'credits.managers': 'Managers',
    'credits.total.users': 'Total Users',
    'credits.daily.cost': 'Daily Cost',
    'credits.days.remaining': 'Days Remaining',
    'credits.consider.purchasing': 'Consider purchasing more credits',
    'credits.days.remaining.message': 'days remaining. Consider purchasing more credits to continue using the service.',
    'credits.purchase.credits': 'Purchase Credits',
    'credits.no.discount': 'No discount',
    'credits.savings': 'savings',
    'credits.custom.amount': 'Custom Amount',
    'credits.purchase.button': 'Purchase',
    'credits.credits': 'credits',
    'credits.for': 'for',
    'credits.deducted.daily': 'Credits are deducted daily based on active users',
    'credits.free.tier.info': 'Free tier available for businesses with 5 or fewer users',
    'credits.recent.transactions': 'Recent Transactions',
    'credits.purchase.type': 'Credit purchase',
    'credits.daily.deduction.type': 'Daily deduction',
    'credits.refund.type': 'Credit refund',
    'credits.purchase.description': 'Credit purchase: {amount} credits for {price}',
    'credits.daily.deduction.description': 'Daily deduction: {amount} credits',
    'credits.refund.description': 'Credit refund: {amount} credits',
    'updating': 'Updating...',
    'profile.updated': 'Profile updated successfully!',
    'failed.update.profile': 'Failed to update profile',
    'network.error': 'Network error occurred',
    'passwords.not.match': 'Passwords do not match',
    'password.too.short': 'Password must be at least 6 characters long',
    'password.updated': 'Password updated successfully!',
    'failed.update.password': 'Failed to update password',
    'contracts.tooltip': 'Enable contract management features for your business',
    'dashboard': 'Dashboard',
    'reports': 'Reports',
    'shifts': 'Shifts',
    'attendance.requests': 'Attendance Requests',
    'employees': 'Employees',
    'messages': 'Messages',
    'reports.title': 'Reports',
    'reports.subtitle': 'Business insights, hours, occupancy and payroll exports',
    'reports.cat.hours': 'Worked time',
    'reports.cat.payroll': 'Payroll data',
    'reports.cat.shifts': 'Shifts & occupancy',
    'reports.cat.leave': 'Absences',
    'reports.cat.contracts': 'Contracts & compliance',
    'reports.hours.earnings': 'Hours & earnings',
    'reports.occupancy': 'Shift occupancy',
    'reports.leave': 'Unregistration requests',
    'reports.contracts': 'Contracts & limits',
    'reports.attendance.sheets': 'Attendance sheets',
    'reports.export.csv': 'Export CSV',
    'reports.export.done': 'CSV exported.',
    'reports.export.empty': 'Nothing to export.',
    'reports.load.error': 'Failed to load report.',
    'reports.empty.title': 'No data for this period',
    'reports.empty.desc': 'Try a different date range or filters.',
    'reports.kpi.people': 'People',
    'reports.kpi.shifts': 'Shifts',
    'reports.kpi.hours': 'Hours',
    'reports.kpi.earnings': 'Earnings',
    'reports.kpi.rows': 'Rows',
    'reports.kpi.fill.rate': 'Fill rate',
    'reports.kpi.open.slots': 'Open slots',
    'reports.kpi.understaffed': 'Understaffed',
    'reports.kpi.total': 'Total',
    'reports.kpi.pending': 'Pending',
    'reports.kpi.approved': 'Approved',
    'reports.kpi.rejected': 'Rejected',
    'reports.kpi.signed': 'Signed',
    'reports.kpi.near.limit': 'Near limit',
    'reports.kpi.cssz.missing': 'CSSZ missing',
    'reports.col.employee': 'Employee',
    'reports.col.avg.rate': 'Avg. rate',
    'reports.col.locations': 'Workplaces',
    'reports.col.date': 'Date',
    'reports.col.shift': 'Shift',
    'reports.col.when': 'When',
    'reports.col.filled': 'Filled',
    'reports.col.status': 'Status',
    'reports.col.requested': 'Requested',
    'reports.col.type': 'Type',
    'reports.col.compliance': 'Compliance',
    'reports.unit.people': 'people',
    'reports.cat.workplaces': 'Workplaces',
    'reports.cat.people': 'People',
    'reports.cat.positions': 'Positions',
    'reports.cat.jobs': 'Job board',
    'reports.workplaces': 'Workplace overview',
    'reports.employees': 'Employees',
    'reports.managers': 'Managers',
    'reports.shifts': 'Shifts detail',
    'reports.positions': 'Positions',
    'reports.jobs': 'Job listings',
    'reports.kpi.to.pay': 'To pay',
    'reports.kpi.workplaces': 'Workplaces',
    'reports.kpi.visits': 'Visits',
    'reports.kpi.active': 'Active',
    'reports.kpi.inactive': 'Inactive',
    'reports.kpi.managers': 'Managers',
    'reports.kpi.positions': 'Positions',
    'reports.kpi.listings': 'Listings',
    'reports.kpi.applications': 'Applications',
    'reports.kpi.accepted': 'Accepted',
    'reports.kpi.workers': 'Workers',
    'reports.col.manager': 'Manager',
    'reports.col.workers': 'Workers',
    'reports.col.job': 'Job',
    'reports.col.created': 'Created',
    'reports.workplaces.people.detail': 'Who worked where',
    'reports.workplaces.people.detail.desc': 'Visits, hours and amount to pay per employee at each workplace',
    'workplaces': 'Workplaces',
    'job.board': 'Job Board',
    'job.applications': 'Job Applications',
    'positions': 'Positions',
    'recent.activity': 'Recent Activity',
    'managers': 'Managers',
    'settings': 'Settings',
    'logout': 'Logout',
    'my.shifts': 'My Shifts',
    'create.shift.compliance.blocked': 'This shift violates labour rules. Fix the highlighted issues before saving.',
    'create.shift.compliance.structure.title': 'Shift cannot be saved (workplace rules)',
    'create.shift.compliance.assignees.title': 'Cannot assign these people',
    'create.shift.compliance.checking': 'checking…',
    'compliance.blocker.SHIFT_MAX_12H': 'Working time on this shift exceeds the 12-hour maximum.',
    'compliance.blocker.BREAK_REQUIRED': 'Shifts longer than 6 h need a break of at least 30 minutes by the 6th hour (workplace break settings).',
    'compliance.blocker.DAILY_REST_11H': 'Less than 11 hours of rest between this person’s shifts.',
    'compliance.blocker.WEEKLY_REST_24H': 'No contiguous 24-hour weekly rest in a 7-day period including this shift.',
    'compliance.blocker.DPP_YEARLY_CAP': 'DPP yearly hour limit (300 h) would be exceeded.',
    'compliance.blocker.DPC_WEEKLY_AVG': 'DPČ average weekly hours would exceed 20 h.',
    'compliance.blocker.BOZP_REQUIRED': 'BOZP acknowledgement is required before attending shifts.',
    'vacation': 'Vacation',
    'vacation.subtitle': 'Statutory leave entitlement (Czech Labour Code). Separate from leaving a shift.',
    'vacation.entitlement': 'Entitlement',
    'vacation.used': 'Used / pending',
    'vacation.remaining': 'Remaining',
    'vacation.worked': 'Worked this year',
    'vacation.not.eligible': 'Not yet eligible for vacation this year.',
    'vacation.new.request': 'New vacation request',
    'vacation.from': 'From',
    'vacation.to': 'To',
    'vacation.hours': 'Hours',
    'vacation.reason': 'Reason (optional)',
    'vacation.submit': 'Submit request',
    'vacation.requests': 'Requests',
    'vacation.empty': 'No vacation requests',
    'vacation.empty.desc': 'Approved and pending leave requests will appear here.',
    'vacation.load.failed': 'Failed to load vacation data.',
    'vacation.request.failed': 'Failed to create vacation request.',
    'vacation.request.created': 'Vacation request submitted.',
    'vacation.update.failed': 'Failed to update vacation request.',
    'vacation.approved': 'Vacation approved.',
    'vacation.rejected': 'Vacation rejected.',
    'vacation.cancelled': 'Vacation cancelled.',
    'vacation.status.approved': 'Approved',
    'vacation.status.rejected': 'Rejected',
    'vacation.status.cancelled': 'Cancelled',
    'vacation.status.pending': 'Pending',
    'vacation.no.business': 'No business',
    'vacation.no.business.desc': 'Join or create a business to manage vacation.',
    'vacation.cz.only': 'Vacation tracking is for CZ workplaces',
    'vacation.cz.only.desc': 'Statutory leave entitlement applies when the workplace country is Czech Republic.',
    'vacation.annual.weeks': 'Annual leave entitlement (weeks)',
    'vacation.annual.weeks.hint': 'Legal minimum is 4 weeks. Used for HPP and DPP/DPČ calculations.',
    'my.contracts': 'My Contracts',
    'my.jobs': 'My Jobs',
    'my.jobs.subtitle': 'Workplaces you belong to and invite codes to join more.',
    'my.jobs.join.title': 'Join with invite code',
    'my.jobs.join.hint': 'Ask your employer for a code, then paste it here.',
    'my.jobs.join.placeholder': 'Enter invite code',
    'my.jobs.join': 'Join',
    'my.jobs.your': 'Your jobs',
    'my.jobs.count': 'jobs',
    'my.jobs.empty': 'No jobs yet',
    'my.jobs.empty.desc': 'Enter an invite code above to join your first workplace.',
    'my.jobs.empty.desc.owner': 'You don’t have any workplaces yet.',
    'my.jobs.leave': 'Leave',
    'my.jobs.leaving': 'Leaving…',
    'my.jobs.leave.confirm': 'Leave {name}? You will lose access to its shifts and data.',
    'my.jobs.joined': 'Joined successfully.',
    'my.jobs.left': 'Left the job.',
    'my.jobs.error.load': 'Failed to load jobs.',
    'my.jobs.error.join': 'Failed to join.',
    'my.jobs.error.leave': 'Failed to leave job.',
    'my.jobs.role.owner': 'Owner',
    'my.jobs.role.manager': 'Manager',
    'my.jobs.role.employee': 'Employee',
    'cv.manager': 'CV Manager',
    'dashboard.welcome.back': 'Welcome back',
    'dashboard.whats.happening': "What's happening",
    'dashboard.business': 'Business',
    'dashboard.today': 'Today',
    'dashboard.total.shifts': 'Total Shifts',
    'dashboard.active.employees': 'Active Employees',
    'dashboard.workplaces': 'Workplaces',
    'dashboard.fill.rate': 'Fill Rate',
    'dashboard.total.users': 'Total Users',
    'dashboard.recent.activity': 'Recent Activity',
    'dashboard.new.shift.scheduled': 'New shift scheduled',
    'dashboard.hours.ago': 'hours ago',
    'dashboard.upcoming.shifts': 'Upcoming Shifts',
    'dashboard.morning.shift': 'Morning Shift',
    'dashboard.tomorrow': 'Tomorrow',
    'dashboard.view.details': 'View Details',
    'dashboard.view.all': 'View all',
    'dashboard.you': 'You',
    'dashboard.activity.assigned_to': 'was assigned to',
    'dashboard.activity.denied_from': 'was denied from',
    'dashboard.activity.requested': 'requested',
    'dashboard.activity.leave_requested': 'requested unregistration from',
    'dashboard.activity.leave_approved': 'unregistration approved from',
    'dashboard.activity.leave_rejected': 'unregistration rejected from',
    'dashboard.no.activity.title': 'No recent activity',
    'dashboard.no.activity.desc': 'Activity will appear here as your team starts using the app.',
    'dashboard.no.shifts.title': 'No upcoming shifts',
    'dashboard.no.shifts.desc': 'Upcoming scheduled shifts will appear here.',
    'dashboard.filled': 'filled',
    'dashboard.filter.date.range': 'Select date range',
    'dashboard.filter.date.pick.end': 'Now pick the end date',
    'dashboard.filter.shift.status': 'Shift status',
    'dashboard.filter.workplace': 'Workplace',
    'dashboard.filter.person': 'Employee / manager',
    'dashboard.filter.all.people': 'All people',
    'dashboard.filter.role.employee': 'Employee',
    'dashboard.filter.role.manager': 'Manager',
    'add': 'Add',
    'edit': 'Edit',
    'delete': 'Delete',
    'cancel': 'Cancel',
    'close': 'Close',
    'submit': 'Submit',
    'loading': 'Loading...',
    'no.data': 'No data available',
    'error': 'Error',
    'success': 'Success',
    'warning': 'Warning',
    'info': 'Information',
    'confirm': 'Confirm',
    'back': 'Back',
    'next': 'Next',
    'previous': 'Previous',
    'search': 'Search',
    'filter': 'Filter',
    'sort': 'Sort',
    'refresh': 'Refresh',
    'view': 'View',
    'create': 'Create',
    'update': 'Update',
    'remove': 'Remove',
    'select': 'Select',
    'all': 'All',
    'none': 'None',
    'yes': 'Yes',
    'no': 'No',
    'ok': 'OK',
    'apply': 'Apply',
    'reset': 'Reset',
    'clear': 'Clear',
    'show': 'Show',
    'hide': 'Hide',
    'more': 'More',
    'less': 'Less',
    'details': 'Details',
    'summary': 'Summary',
    'overview': 'Overview',
    'status': 'Status',
    'date': 'Date',
    'time': 'Time',
    'name': 'Name',
    'description': 'Description',
    'type': 'Type',
    'category': 'Category',
    'priority': 'Priority',
    'assigned': 'Assigned',
    'created': 'Created',
    'updated': 'Updated',
    'actions': 'Actions',
    'options': 'Options',
    'preferences': 'Preferences',
    'profile': 'Profile',
    'account': 'Account',
    'security': 'Security',
    'notifications': 'Notifications',
    'help': 'Help',
    'support': 'Support',
    'about': 'About',
    'contact': 'Contact',
    'privacy': 'Privacy',
    'terms': 'Terms',
    'conditions': 'Conditions',
    'policy': 'Policy',
    'legal': 'Legal',
    'copyright': 'Copyright',
    'version': 'Version',
    'language': 'Language',
    'currency': 'Currency',
    'timezone': 'Timezone',
    'format': 'Format',
    'display': 'Display',
    'theme': 'Theme',
    'color': 'Color',
    'size': 'Size',
    'position': 'Position',
    'location': 'Location',
    'address': 'Address',
    'phone': 'Phone',
    'email': 'Email',
    'website': 'Website',
    'social': 'Social',
    'media': 'Media',
    'file': 'File',
    'image': 'Image',
    'document': 'Document',
    'attachment': 'Attachment',
    'link': 'Link',
    'url': 'URL',
    'code': 'Code',
    'id': 'ID',
    'reference': 'Reference',
    'number': 'Number',
    'amount': 'Amount',
    'quantity': 'Quantity',
    'price': 'Price',
    'cost': 'Cost',
    'total': 'Total',
    'subtotal': 'Subtotal',
    'tax': 'Tax',
    'discount': 'Discount',
    'percentage': 'Percentage',
    'rate': 'Rate',
    'frequency': 'Frequency',
    'duration': 'Duration',
    'period': 'Period',
    'interval': 'Interval',
    'schedule': 'Schedule',
    'calendar': 'Calendar',
    'event': 'Event',
    'meeting': 'Meeting',
    'appointment': 'Appointment',
    'task': 'Task',
    'project': 'Project',
    'work': 'Work',
    'job': 'Job',
    'career': 'Career',
    'employment': 'Employment',
    'contract': 'Contract',
    'agreement': 'Agreement',
    'deal': 'Deal',
    'offer': 'Offer',
    'proposal': 'Proposal',
    'request': 'Request',
    'application': 'Application',
    'form': 'Form',
    'survey': 'Survey',
    'feedback': 'Feedback',
    'review': 'Review',
    'rating': 'Rating',
    'comment': 'Comment',
    'message': 'Message',
    'notification': 'Notification',
    'alert': 'Alert',
    'reminder': 'Reminder',
    'invitation': 'Invitation',
    'invite': 'Invite',
    'share': 'Share',
    'send': 'Send',
    'receive': 'Receive',
    'read': 'Read',
    'write': 'Write',
    'reply': 'Reply',
    'forward': 'Forward',
    'archive': 'Archive',
    'trash': 'Trash',
    'restore': 'Restore',
    'duplicate': 'Duplicate',
    'copy': 'Copy',
    'paste': 'Paste',
    'cut': 'Cut',
    'undo': 'Undo',
    'redo': 'Redo',
    'print': 'Print',
    'scan': 'Scan',
    'fax': 'Fax',
    'sms': 'SMS',
    'call': 'Call',
    'video': 'Video',
    'audio': 'Audio',
    'photo': 'Photo',
    'picture': 'Picture',
    'gallery': 'Gallery',
    'album': 'Album',
    'folder': 'Folder',
    'directory': 'Directory',
    'path': 'Path',
    'route': 'Route',
    'page': 'Page',
    'section': 'Section',
    'chapter': 'Chapter',
    'part': 'Part',
    'piece': 'Piece',
    'item': 'Item',
    'element': 'Element',
    'component': 'Component',
    'module': 'Module',
    'system': 'System',
    'service': 'Service',
    'function': 'Function',
    'feature': 'Feature',
    'tool': 'Tool',
    'utility': 'Utility',
    'resource': 'Resource',
    'asset': 'Asset',
    'material': 'Material',
    'product': 'Product',
    'goods': 'Goods',
    'merchandise': 'Merchandise',
    'inventory': 'Inventory',
    'stock': 'Stock',
    'supply': 'Supply',
    'demand': 'Demand',
    'market': 'Market',
    'industry': 'Industry',
    'sector': 'Sector',
    'field': 'Field',
    'city': 'City',
    'town': 'Town',
    'village': 'Village',
    'country': 'Country',
    'state': 'State',
    'province': 'Province',
    'county': 'County',
    'postal': 'Postal',
    'zip': 'ZIP',
    'shifts.title': 'Shifts',
    'shifts.subtitle': 'Manage your work shifts and schedules',
    'shifts.presets': 'Shift Presets',
    'shifts.create.shift': 'Create Shift',
    'shifts.list.view': 'List View',
    'shifts.calendar.view': 'Calendar View',
    'shifts.month.view': 'Month',
    'shifts.week.view': 'Week',
    'shifts.calendar.more': '+{count} more',
    'shifts.calendar.show.less': 'Show less',
    'shifts.calendar.prev': 'Previous month',
    'shifts.calendar.next': 'Next month',
    'shifts.calendar.prev.week': 'Previous week',
    'shifts.calendar.next.week': 'Next week',
    'shifts.calendar.select.day': 'Select a date',
    'shifts.calendar.select.day.desc': 'Click on a date in the calendar to see shifts.',
    'shifts.no.shifts.for.date': 'No shifts for this date',
    'shifts.create.for.this.day': 'Create a shift for this day.',
    'shifts.reschedule.success': 'Shift moved.',
    'shifts.reschedule.failed': 'Could not move shift.',
    'shifts.bulk.selected': '{count} selected',
    'shifts.bulk.duplicate.week': 'Duplicate +1 week',
    'shifts.bulk.copy.dates': 'Copy to date…',
    'shifts.bulk.copy.apply': 'Copy',
    'shifts.bulk.copy.pick.date': 'Pick a date',
    'shifts.bulk.delete': 'Delete',
    'shifts.bulk.clear': 'Clear',
    'shifts.bulk.delete.confirm': 'Delete {count} selected shift(s)?',
    'shifts.bulk.delete.owner.only': 'Only the owner can delete shifts.',
    'shifts.bulk.failed': 'Bulk action failed.',
    'shifts.bulk.deleted': 'Deleted {count} shift(s).',
    'shifts.bulk.created': 'Created {count} shift(s).',
    'create.shift.notify.eligible': 'Notify eligible employees (push + email)',
    'create.shift.recurrence': 'Repeat',
    'create.shift.recurrence.none': 'Does not repeat',
    'create.shift.recurrence.daily': 'Daily',
    'create.shift.recurrence.weekly': 'Weekly',
    'create.shift.recurrence.weekdays': 'Weekdays (Mon–Fri)',
    'create.shift.recurrence.interval': 'Custom interval',
    'create.shift.recurrence.until': 'Repeat until',
    'workplaces.color': 'Color',
    'workplaces.color.custom': 'Custom color',
    'workplaces.parent': 'Parent workplace',
    'workplaces.parent.none': 'None (top level)',
    'workplaces.parent.hint': 'Max 3 levels. Cannot select this workplace or its sub-locations.',
    'workplaces.add.child': 'Add sub-location',
    'workplaces.search.placeholder': 'Search workplaces…',
    'workplaces.tree.expand': 'Expand',
    'workplaces.tree.collapse': 'Collapse',
    'workplaces.tree.expand.all': 'Expand all',
    'workplaces.tree.collapse.all': 'Collapse all',
    'workplaces.employees.count': '{count} employees',
    'workplaces.delete.has.children': 'Move or delete sub-locations first before deleting this workplace.',
    'workplaces.filter.include.children': 'Include sub-locations',
    'vacation.leave.kind': 'Leave type',
    'vacation.kind.vacation': 'Vacation',
    'vacation.kind.timeoff': 'Time off',
    'groups.title': 'User groups',
    'groups.subtitle': 'Labels for bulk edits and targeting - not the same as job positions.',
    'groups.create': 'Create group',
    'groups.edit': 'Edit group',
    'groups.name': 'Group name',
    'groups.empty': 'No groups yet',
    'groups.empty.desc': 'Create a group to target announcements, shift notifications, or apply settings to several people at once.',
    'groups.created': 'Group created.',
    'groups.updated': 'Group updated.',
    'groups.create.failed': 'Failed to create group.',
    'groups.save.failed': 'Failed to save group.',
    'groups.delete.confirm': 'Delete this group?',
    'groups.deleted': 'Group deleted.',
    'groups.delete.failed': 'Failed to delete group.',
    'groups.load.failed': 'Failed to load groups.',
    'groups.members.count': '{count} members',
    'groups.members.none': 'No members yet',
    'groups.members.add': 'Add people',
    'groups.members.hint': 'Select people who belong to this group.',
    'groups.members.search': 'Type to search',
    'groups.members.empty': 'No people available to add.',
    'groups.members.no.match': 'No matching people.',
    'groups.bulk.open': 'Bulk edit members',
    'groups.bulk.title': 'Bulk edit: {name}',
    'groups.bulk.hint': 'Changes apply to all active members of this group.',
    'groups.bulk.workplaces': 'Workplaces',
    'groups.bulk.mode': 'Workplace update mode',
    'groups.bulk.mode.replace': 'Replace current workplaces',
    'groups.bulk.mode.add': 'Add to existing workplaces',
    'groups.bulk.pay': 'Set personal hourly rate',
    'groups.bulk.pay.rate': 'Hourly rate',
    'groups.bulk.pay.placeholder': 'Leave empty to clear',
    'groups.bulk.pay.hint': 'Leave empty to clear the personal rate.',
    'groups.bulk.apply': 'Apply to members',
    'groups.bulk.nothing': 'Choose workplaces and/or hourly rate first.',
    'groups.bulk.done': 'Updated {count} member(s).',
    'groups.bulk.failed': 'Bulk update failed.',
    'announcements.audience.groups': 'User groups',
    'announcements.audience.groups.empty': 'No groups yet - create some under User groups.',
    'announcements.audience.groups.required': 'Select at least one group.',
    'create.shift.notify.groups': 'Limit to groups (optional)',
    'create.shift.notify.groups.hint': 'If none selected, everyone eligible for the shift location is notified.',
    'reports.export.server': 'Download from server',
    'reports.export.server.done': 'Server export downloaded.',
    'reports.export.failed': 'Export failed.',
    'shift.series.edit.scope.prompt': 'Series edit scope: THIS | THIS_AND_FUTURE | ALL',
    'shifts.create.for.upcoming': 'Create a shift for upcoming days.',
    'shifts.status.all': 'All Status',
    'shifts.status.all.shifts': 'All Shifts',
    'shifts.status.active': 'Active',
    'shifts.status.drafts': 'Drafts',
    'shifts.status.draft': 'Draft',
    'shifts.status.upcoming': 'Upcoming',
    'auto.plan.cta': 'Propose shifts',
    'auto.plan.title': 'Propose shifts',
    'auto.plan.step.of': 'Step {step} of {total}',
    'auto.plan.step.source': 'Source',
    'auto.plan.step.period': 'Period & templates',
    'auto.plan.step.fill': 'Staffing',
    'auto.plan.source.hint': 'Choose how to build the draft schedule.',
    'auto.plan.source.presets': 'From shift presets',
    'auto.plan.source.presets.desc': 'Expand selected templates across the target period.',
    'auto.plan.source.copy': 'Copy a previous period',
    'auto.plan.source.copy.desc': 'Clone an existing week/month onto new dates.',
    'auto.plan.period.start': 'Period start',
    'auto.plan.period.end': 'Period end',
    'auto.plan.locations': 'Workplace filter',
    'auto.plan.locations.all': 'All workplaces',
    'auto.plan.weekdays': 'Days of week',
    'auto.plan.weekday.mon': 'Mon',
    'auto.plan.weekday.tue': 'Tue',
    'auto.plan.weekday.wed': 'Wed',
    'auto.plan.weekday.thu': 'Thu',
    'auto.plan.weekday.fri': 'Fri',
    'auto.plan.weekday.sat': 'Sat',
    'auto.plan.weekday.sun': 'Sun',
    'auto.plan.presets.select': 'Select presets',
    'auto.plan.presets.empty': 'No presets available for this filter.',
    'auto.plan.source.start': 'Source start',
    'auto.plan.source.end': 'Source end',
    'auto.plan.copy.hint': 'Shifts from the source range are copied into the target period (structure only; assignees are re-planned if auto-assign is on).',
    'auto.plan.fill.hint': 'Draft shifts stay hidden from employees until you publish.',
    'auto.plan.fill.open': 'Open slots (no people)',
    'auto.plan.fill.open.desc': 'Create capacity only — staff signup or assign later.',
    'auto.plan.fill.auto': 'Auto-assign people',
    'auto.plan.fill.auto.desc': 'Fill seats using workplace roster and compliance rules.',
    'auto.plan.slots.hint': 'Add one or more presets for each weekday in the target period.',
    'auto.plan.slots.empty': 'No shifts on this day yet.',
    'auto.plan.slot.add': 'Add shift from preset',
    'auto.plan.slot.remove': 'Remove slot',
    'auto.plan.capacity': 'Capacity {max}',
    'auto.plan.eligible': '{count} eligible',
    'auto.plan.eligible.min': '≥ {count} eligible',
    'auto.plan.capacity.shortage': 'Cannot fill: need {max}, only {eligible} eligible',
    'auto.plan.conflict.summary': '{count} slot(s) cannot be fully staffed',
    'auto.plan.conflict.ok': 'All {count} planned slot(s) look fillable',
    'auto.plan.preflight.checking': 'Checking availability…',
    'auto.plan.propose.partial': 'Created {count} draft(s). {unfillable} slot(s) may stay underfilled.',
    'auto.plan.copy.cards.hint': 'Select which shifts to copy into the new period.',
    'auto.plan.copy.empty': 'No shifts found in the source period.',
    'auto.plan.prefer.position': 'Prefer position (optional)',
    'auto.plan.prefer.position.any': 'Any position',
    'auto.plan.draft.notice': 'Result is a draft proposal. Review, edit, then publish or discard.',
    'auto.plan.back': 'Back',
    'auto.plan.next': 'Next',
    'auto.plan.submit': 'Create draft',
    'auto.plan.proposing': 'Creating…',
    'auto.plan.load.error': 'Failed to load planner data.',
    'auto.plan.propose.success': 'Created {count} draft shift(s).',
    'auto.plan.propose.error': 'Failed to create draft plan.',
    'auto.plan.publish.success': 'Published {count} shift(s).',
    'auto.plan.publish.error': 'Failed to publish plan.',
    'auto.plan.discard.confirm': 'Discard all draft shifts (all proposals and manual drafts)?',
    'auto.plan.discard.success': 'Draft proposal discarded.',
    'auto.plan.discard.error': 'Failed to discard plan.',
    'auto.plan.banner.title': 'Draft shifts · {count}',
    'auto.plan.banner.hint': 'Employees cannot see these until you publish. Related drafts share a border color.',
    'auto.plan.banner.review': 'Review drafts',
    'auto.plan.banner.hide': 'Hide drafts',
    'auto.plan.banner.publish': 'Publish all',
    'auto.plan.banner.discard': 'Discard all',
    'auto.plan.min.hours.title': 'Minimum hours with these drafts',
    'auto.plan.min.hours.hint':
      'Includes draft assignments for each employee’s week or month. Counts officially after you publish.',
    'auto.plan.min.hours.empty': 'No employees have minimum hours enabled.',
    'auto.plan.min.hours.draft.delta': '+{hours} h drafts',
    'auto.plan.min.hours.draft.none': 'No draft hours',
    'auto.plan.min.hours.published': 'Published {hours} h',
    'auto.plan.min.hours.prev': 'Previous',
    'auto.plan.min.hours.next': 'Next',
    'shifts.status.attending': 'Attending',
    'shifts.status.attended': 'Attended',
    'shifts.status.request.denied': 'Request Denied',
    'shifts.status.request.pending': 'Request Pending',
    'shifts.status.leave.requested': 'Unregistration requested',
    'shifts.status.attendance.requested': 'Attendance Requested',
    'shifts.all.locations': 'All Locations',
    'shifts.search.placeholder': 'Search shifts...',
    'attendance.requests.go.back': 'Go Back',
    'attendance.requests.title': 'Attendance Requests',
    'attendance.requests.subtitle': 'Manage attendance and unregistration requests',
    'attendance.requests.attendance.requests': 'Attendance Requests',
    'attendance.requests.leave.requests': 'Unregistration requests',
    'attendance.requests.no.pending.attendance': 'No pending attendance requests',
    'employees.title': 'Employees',
    'employees.subtitle': 'Manage your team members and their access',
    'employees.subtitle.manager': 'Manage employees assigned to your workplaces',
    'employees.generate.invite': 'Generate Invite',
    'employees.users': 'users',
    'employees.active.invite.codes': 'Active Invite Codes',
    'employees.no.active.codes': 'No active invite codes',
    'employees.total.employees': 'Total Employees',
    'employees.active.employees': 'Active Employees',
    'employees.current.employees': 'Current Employees',
    'employees.no.employees': 'No employees yet',
    'employees.inactive': 'Inactive',
    'employees.contracts.active.hint': 'To activate this employee, they need an active signed contract.',
    'employees.edit': 'Edit',
    'employees.remove': 'Remove',
    'messages.title': 'Messages',
    'messages.subtitle': 'Chat with your team and employers from your applications',
    'messages.search.contacts': 'Search contacts...',
    'messages.filter.all': 'All',
    'messages.filter.owners': 'Owners',
    'messages.filter.managers': 'Managers',
    'messages.filter.employees': 'Employees',
    'messages.filter.team': 'Team',
    'messages.filter.applications': 'Applications',
    'messages.empty.title': 'No contacts yet',
    'messages.empty.desc': 'Colleagues from your workplaces appear here. After you apply for a job, you can also message that company’s owner and managers.',
    'messages.empty.search': 'No contacts match your search.',
    'messages.empty.cta.jobs': 'Browse jobs',
    'messages.badge.application': 'Application',
    'messages.badge.pending': 'Pending application',
    'messages.badge.approved': 'Hired / approved',
    'messages.badge.rejected': 'Application closed',
    'messages.for.job': 'Re: {title}',
    'messages.message.employer': 'Message employer',
    'messages.message.applicant': 'Message applicant',
    'messages.job.context': 'Responding to job listing',
    'messages.job.context.open': 'View listing',
    'messages.view.cv': 'View CV',
    'messages.no.cv': 'No CV uploaded',
    'job.details.message.employer': 'Message employer',
    'job.details.message.employer.hint': 'Ask the company about this role after you apply.',
    'job.applications.message': 'Message',
    'messages.role.owner': 'Owner',
    'messages.role.manager': 'Manager',
    'messages.role.employee': 'Employee',
    'messages.at': 'at',
    'messages.select.contact': 'Select a contact',
    'messages.select.contact.subtitle': 'Choose a contact to start messaging',
    'messages.swap.offer': 'Offer shift',
    'messages.swap.modal.title': 'Offer a shift to cover',
    'messages.swap.modal.select.shift': 'Select your shift',
    'messages.swap.modal.select.colleague': 'Select colleague',
    'messages.swap.modal.note': 'Note (optional)',
    'messages.swap.modal.note.placeholder': 'Add a short message…',
    'messages.swap.modal.send': 'Send offer',
    'messages.swap.modal.cancel': 'Cancel',
    'messages.swap.modal.no.shifts': 'You have no upcoming approved shifts to offer.',
    'messages.swap.modal.no.colleagues': 'No eligible colleagues for this shift.',
    'messages.swap.modal.loading': 'Loading…',
    'messages.swap.card.title': 'Shift cover offer',
    'messages.swap.accept': 'Accept',
    'messages.swap.decline': 'Decline',
    'messages.swap.cancel': 'Cancel offer',
    'messages.swap.status.PENDING': 'Waiting for response',
    'messages.swap.status.DECLINED': 'Declined',
    'messages.swap.status.CANCELLED': 'Cancelled',
    'messages.swap.status.PENDING_MANAGER': 'Waiting for manager',
    'messages.swap.status.COMPLETED': 'Completed',
    'messages.swap.status.REJECTED_MANAGER': 'Rejected by manager',
    'messages.swap.toast.sent': 'Shift offer sent.',
    'messages.swap.toast.accepted': 'Offer accepted.',
    'messages.swap.toast.accepted.manager': 'Accepted - waiting for manager approval.',
    'messages.swap.toast.declined': 'Offer declined.',
    'messages.swap.toast.cancelled': 'Offer cancelled.',
    'messages.swap.toast.error': 'Something went wrong.',
    'messages.swap.toast.link.created': 'Shareable swap link created.',
    'messages.swap.modal.mode.colleague': 'Colleague',
    'messages.swap.modal.mode.link': 'Share link',
    'messages.swap.modal.create.link': 'Create & copy link',
    'messages.swap.modal.link.hint':
      'Anyone eligible for this shift can take it over via the link. The first person to accept wins.',
    'messages.swap.modal.link.ready': 'Link is ready — share it with colleagues (works on web and in the app).',
    'messages.swap.modal.done': 'Done',
    'swap.link.title': 'Shift swap offer',
    'swap.link.from': 'Offered by',
    'swap.link.claim': 'Take over this shift',
    'swap.link.claiming': 'Taking over…',
    'swap.link.claimed': 'Shift taken over.',
    'swap.link.cancel': 'Cancel link',
    'swap.link.cancelled': 'Swap link cancelled.',
    'swap.link.copy': 'Copy link',
    'swap.link.copied': 'Link copied.',
    'swap.link.copy.failed': 'Could not copy link.',
    'swap.link.login.required': 'Log in to view and accept this shift swap.',
    'swap.link.login.cta': 'Log in',
    'swap.link.back': 'Back',
    'swap.link.open.app': 'Open in Ompli app',
    'swap.link.owner.hint': 'This is your shareable link. Copy it and send it to colleagues.',
    'swap.link.not.eligible': 'You are not eligible to take over this shift.',
    'swap.link.unavailable': 'This swap link is no longer available.',
    'swap.link.error': 'Could not load swap link.',
    'swap.link.status.OPEN': 'Open',
    'swap.link.status.CLAIMED': 'Claimed',
    'swap.link.status.CANCELLED': 'Cancelled',
    'attendance.requests.swap.requests': 'Shift swaps',
    'attendance.requests.no.pending.swap': 'No pending shift swaps',
    'attendance.requests.swap.from': 'From',
    'attendance.requests.swap.to': 'To',
    'attendance.requests.swap.approved': 'Shift swap approved.',
    'attendance.requests.swap.rejected': 'Shift swap rejected.',
    'attendance.requests.swap.empty.desc': 'Colleague swap requests awaiting your approval will appear here.',
    'workplaces.title': 'Workplaces',
    'workplaces.subtitle.owner': 'Manage your business locations and workplaces',
    'workplaces.add.location': 'Add Location',
    'contracts.title': 'Contracts',
    'contracts.send.contract': 'Send Contract',
    'contracts.contract': 'Contract',
    'contracts.duration': 'Duration',
    'contracts.download': 'Download',
    'contracts.waiting.sign': 'Waiting for signature',
    'contracts.terminate.contract': 'Terminate Contract',
    'contracts.contract.history': 'Contract History',
    'contracts.terminated': 'Terminated',
    'contracts.download.signed': 'Download Signed',
    'contracts.status.draft': 'Draft',
    'contracts.status.sent': 'Sent',
    'contracts.status.signed': 'Signed',
    'contracts.status.awaiting': 'Awaiting signature',
    'contracts.status.rejected': 'Rejected',
    'contracts.status.inactive': 'Inactive',
    'contracts.duration.monthly': 'Monthly',
    'contracts.duration.yearly': 'Yearly',
    'contracts.duration.unlimited': 'Unlimited',
    'contracts.duration.custom': 'Custom',
    'job.board.title': 'Job Board',
    'job.board.subtitle': 'Post and manage job opportunities',
    'job.board.post.job': 'Post Job',
    'job.board.list.view': 'List View',
    'job.board.grid.view': 'Grid View',
    'job.board.my.jobs': 'My Jobs',
    'job.board.all.jobs': 'All Jobs',
    'job.board.search.jobs': 'Search jobs...',
    'positions.title': 'Positions',
    'positions.subtitle': 'Manage job positions and roles',
    'positions.add.position': 'Add Position',
    'positions.edit.position': 'Edit Position',
    'positions.add.new.position': 'Add New Position',
    'positions.form.subtitle': 'Fill in the details below to set up this position',
    'positions.section.basic': 'Basic Information',
    'positions.section.workplaces': 'Workplaces',
    'positions.section.workplaces.hint': 'Leave empty to allow this position at all workplaces. Otherwise select where it applies.',
    'positions.all.workplaces': 'All workplaces',
    'positions.no.workplaces.available': 'No workplaces available yet.',
    'positions.add.workplace.search': 'Search workplaces…',
    'positions.add.workplace.no.match': 'No matching workplaces',
    'positions.remove.workplace': 'Remove workplace',
    'positions.empty.desc': 'Create your first position to start assigning employees to roles.',
    'positions.save.changes': 'Save Changes',
    'create.shift.position': 'Position',
    'create.shift.select.position': 'Select position (optional)',
    'create.shift.position.none': 'No position',
    'create.shift.position.select.location.first': 'Select a workplace first to see available positions',
    'positions.created': 'Position created.',
    'positions.updated': 'Position updated.',
    'positions.deleted': 'Position deleted.',
    'positions.no.limit': 'No limit',
    'positions.any.gender': 'Any gender',
    'positions.max.people': 'Max people',
    'positions.gender': 'Gender',
    'recent.activity.title': 'Recent Activity',
    'recent.activity.subtitle': 'Track all business activities and changes',
    'recent.activity.filters': 'Filters',
    'recent.activity.activities': 'activities',
    'recent.activity.shift.assignment': 'shift assignment',
    'recent.activity.shift.created': 'Shift Created',
    'recent.activity.requested': 'Requested',
    'recent.activity.business.event': 'business event',
    'recent.activity.employee.rejoined': 'Employee Rejoined',
    'recent.activity.rejoined.as': 'rejoined',
    'recent.activity.left': 'Left',
    'recent.activity.inactive': 'Inactive',
    'recent.activity.changes': 'Changes',
    'recent.activity.change.created': 'Created',
    'recent.activity.change.updated': 'Updated',
    'recent.activity.change.deleted': 'Deleted',
    'recent.activity.date': 'Date',
    'recent.activity.business.events': 'Business Events',
    'recent.activity.no.found': 'No activities found',
    'recent.activity.no.found.for': 'No activities found for {type}',
    'recent.activity.count': '{shown} of {total} activities',
    'recent.activity.on.date': 'Activities on {date}',
    'recent.activity.type.all': 'All',
    'recent.activity.type.change': 'Change',
    'recent.activity.type.shift_assignment': 'Shift assignment',
    'recent.activity.type.leave_request': 'Unregistration request',
    'recent.activity.type.job_application': 'Job application',
    'recent.activity.type.cv_update': 'CV update',
    'recent.activity.type.business_event': 'Business event',
    'recent.activity.type.contract': 'Contract',
    'activity.summary.created': 'Created {entity}',
    'activity.summary.created.named': 'Created {entity}: {name}',
    'activity.summary.updated': 'Updated {entity}: {changes}',
    'activity.summary.updated.named': 'Updated {entity} ({name}): {changes}',
    'activity.summary.updated.plain': 'Updated {entity}',
    'activity.summary.updated.plain.named': 'Updated {entity} ({name})',
    'activity.summary.deleted': 'Deleted {entity}',
    'activity.summary.deleted.named': 'Deleted {entity}: {name}',
    'activity.summary.more': '+{count} more',
    'activity.val.empty': 'empty',
    'activity.val.on': 'on',
    'activity.val.off': 'off',
    'activity.entity.unknown': 'item',
    'activity.entity.User': 'user',
    'activity.entity.Business': 'business',
    'activity.entity.Location': 'workplace',
    'activity.entity.Position': 'position',
    'activity.entity.Shift': 'shift',
    'activity.entity.ShiftPreset': 'shift preset',
    'activity.entity.ShiftAssignment': 'shift assignment',
    'activity.entity.LeaveRequest': 'unregistration request',
    'activity.entity.JobListing': 'job listing',
    'activity.entity.JobApplication': 'job application',
    'activity.entity.UserBusiness': 'employee',
    'activity.entity.ManagerBusiness': 'manager',
    'activity.entity.ManagerWorkplaceAssignment': 'manager workplace',
    'activity.entity.Contract': 'contract',
    'activity.entity.CV': 'CV',
    'activity.entity.Invite': 'invite',
    'activity.entity.Worker': 'worker',
    'activity.entity.UserBusinessPosition': 'employee position',
    'activity.entity.CalendarExternalEvent': 'calendar event',
    'activity.entity.WorkedHoursEntry': 'worked hours entry',
    'activity.field.active': 'active',
    'activity.field.payRate': 'pay rate',
    'activity.field.payAmount': 'pay amount',
    'activity.field.payType': 'pay type',
    'activity.field.notes': 'notes',
    'activity.field.phone': 'phone',
    'activity.field.address': 'address',
    'activity.field.startDate': 'start date',
    'activity.field.endDate': 'end date',
    'activity.field.startTime': 'start time',
    'activity.field.endTime': 'end time',
    'activity.field.position': 'position',
    'activity.field.name': 'name',
    'activity.field.title': 'title',
    'activity.field.description': 'description',
    'activity.field.email': 'email',
    'activity.field.maxAttendees': 'max attendees',
    'activity.field.approvalType': 'approval type',
    'activity.field.allowLeave': 'allow leave',
    'activity.field.locationId': 'workplace',
    'activity.field.positionId': 'position',
    'activity.field.status': 'status',
    'activity.field.firstName': 'first name',
    'activity.field.lastName': 'last name',
    'activity.field.username': 'username',
    'activity.field.hourlyPayRate': 'hourly pay',
    'activity.field.contractsEnabled': 'contracts enabled',
    'managers.title': 'Managers',
    'managers.subtitle': 'Manage your business managers and their access',
    'managers.invite.manager': 'Invite Manager',
    'managers.generate.invite': 'Generate Invite',
    'managers.generating': 'Generating...',
    'managers.active.invites': 'Active Invites',
    'managers.active.invite.codes': 'Active Invite Codes',
    'managers.no.active.invites': 'No active invites',
    'managers.current.managers': 'Current Managers',
    'managers.no.managers': 'No managers yet',
    'managers.total.managers': 'Total Managers',
    'managers.active.managers': 'Active Managers',
    'managers.remove.manager': 'Remove Manager',
    'managers.remove': 'Remove',
    'managers.edit': 'Edit',
    'managers.users': 'Users',
    'managers.no.workplaces': 'No workplaces assigned',
    'sent': 'Sent',
    'signed': 'Signed',
    'daily': 'Daily',
    'hourly': 'Hourly',
    'attendance': 'Attendance',
    'terminate': 'Terminate',
    'history': 'History',
    'post': 'Post',
    'no.limit': 'No limit',
    'any.gender': 'Any gender',
    'max.people': 'Max people',
    'shift.assignment': 'shift assignment',
    'shift.created': 'Shift Created',
    'requested': 'Requested',
    'business.event': 'business event',
    'employee.rejoined': 'Employee Rejoined',
    'rejoined.as': 'rejoined',
    'left': 'Left',
    'invite.manager': 'Invite Manager',
    'active.invites': 'Active Invites',
    'no.active.invites': 'No active invites',
    'total.managers': 'Total Managers',
    'active.managers': 'Active Managers',
    'remove.manager': 'Remove Manager',
    'login.welcome.back': 'Welcome back',
    'login.sign.in.account': 'Sign in to your account',
    'login.employer': 'Employer',
    'login.employee': 'Employee',
    'login.manager': 'Manager',
    'login.email.address': 'Email address',
    'login.password': 'Password',
    'login.remember.me': 'Remember me',
    'login.forgot.password': 'Forgot password?',
    'login.sign.in': 'Sign in',
    'login.quick.login.owner': 'Quick Login - Owner',
    'login.quick.login.employee': 'Quick Login - Employee',
    'login.quick.login.manager': 'Quick Login - Manager',
    'login.or.continue.with': 'Or continue with',
    'login.oauth.error': 'Sign-in with Google/Microsoft/Apple failed',
    'login.oauth.success': 'Signed in successfully.',
    'login.oauth.apple.unavailable': 'Sign in with Apple is not configured on the server.',
    'login.dont.have.account': "Don't have an account?",
    'login.register.here': 'Register here',
    'register.join.ompli': 'Join Ompli',
    'register.choose.role': 'Choose your role',
    'register.im.employer': 'I am an employer',
    'register.im.employee': 'I am an employee',
    'register.im.manager': 'I am a manager',
    'register.next': 'Next',
    'register.already.have.account': 'Already have an account?',
    'register.sign.in': 'Sign in',
    'active': 'Active',
    'all.activities': 'All Activities',
    'attendance.requests.approve': 'Approve',
    'attendance.requests.cancel': 'Cancel',
    'attendance.requests.confirm.denial': 'Confirm Denial',
    'attendance.requests.deny': 'Deny',
    'attendance.requests.deny.leave.request': 'Deny unregistration request',
    'attendance.requests.deny.reason': 'Deny Reason',
    'attendance.requests.enter.denial.reason': 'Enter Denial Reason',
    'attendance.requests.no.pending.leave': 'No pending unregistration requests',
    'attendance.requests.leave.approved': 'Unregistration request approved.',
    'attendance.requests.leave.denied': 'Unregistration request denied.',
    'attendance.requests.leave.empty.desc': 'Unregistration requests from your team will appear here.',
    'request_leave': 'Request unregistration',
    'request_leave_shift_message': 'You are requesting to unregister from',
    'reason_for_leaving': 'Reason for unregistration',
    'please_provide_reason': 'Please provide a reason (optional)',
    'attendance.requests.pending': 'Pending',
    'contracts.all': 'All',
    'contracts.approve': 'Approve',
    'contracts.choose.pdf': 'Choose PDF',
    'contracts.contract.sent': 'Contract Sent',
    'contracts.custom.end.date': 'Custom End Date',
    'contracts.enter.rejection.reason': 'Enter Rejection Reason',
    'contracts.enter.termination.reason': 'Enter Termination Reason',
    'contracts.expiry': 'Expiry',
    'contracts.failed.download': 'Failed to Download',
    'contracts.failed.send': 'Failed to Send',
    'contracts.failed.terminate': 'Failed to Terminate',
    'contracts.failed.upload': 'Failed to Upload',
    'contracts.no.history': 'No History',
    'contracts.please.download.sign': 'Please Download and Sign',
    'contracts.reason.optional': 'Reason (Optional)',
    'contracts.reject': 'Reject',
    'contracts.reject.contract': 'Reject Contract',
    'contracts.rejecting': 'Rejecting…',
    'contracts.rejected': 'Contract rejected.',
    'contracts.failed.reject': 'Failed to reject contract.',
    'contracts.select.employees': 'Select Employees',
    'contracts.send': 'Send',
    'contracts.send.signed': 'Send Signed',
    'contracts.sending.signed': 'Sending Signed',
    'contracts.signed.uploaded': 'Signed Contract Uploaded',
    'contracts.terminate': 'Terminate',
    'contracts.terminate.contract.for': 'Terminate Contract for',
    'contracts.until': 'Until',
    'contracts.upload.pdf': 'Upload PDF',
    'employment.type': 'Employment type',
    'employment.type.DPP': 'DPP (agreement to complete a job)',
    'employment.type.DPC': 'DPČ (agreement to perform work)',
    'employment.type.HPP': 'HPP (employment contract)',
    'employment.workplace': 'Workplace',
    'employment.select.workplace': 'Select workplace',
    'employment.job.description': 'Job description',
    'employment.job.description.hint': 'Concrete description of the work to be performed',
    'employment.start.date': 'Start date',
    'employment.end.date': 'End date (optional)',
    'employment.pay.type': 'Pay type',
    'employment.pay.hourly': 'Hourly',
    'employment.pay.monthly': 'Monthly',
    'employment.pay.amount': 'Pay amount',
    'employment.weekly.hours': 'Agreed weekly hours',
    'employment.send.missing.basic': 'Please select at least one employee.',
    'employment.send.missing.fields': 'Fill in workplace, start date, job description and pay.',
    'employment.send.missing.weekly': 'Agreed weekly hours are required for DPČ/HPP.',
    'employment.cssz.mark': 'Mark ČSSZ reported',
    'employment.cssz.marked': 'ČSSZ registration marked as done.',
    'employment.cssz.failed': 'Failed to mark ČSSZ registration.',
    'employment.cssz.done': 'ČSSZ reported',
    'employment.bozp.mark': 'Mark BOZP done',
    'employment.bozp.marked': 'BOZP acknowledgement saved.',
    'employment.bozp.failed': 'Failed to mark BOZP.',
    'employment.bozp.done': 'BOZP done',
    'employment.checklist.title': 'Monthly ČSSZ statement',
    'employment.checklist.desc': 'In-app checklist - confirm you submitted the monthly statement',
    'employment.checklist.due': 'due by',
    'employment.checklist.mark': 'Mark as submitted',
    'employment.checklist.marked': 'Submitted this month',
    'employment.checklist.done': 'Monthly statement marked as done.',
    'employment.checklist.undone': 'Monthly statement unmarked.',
    'employment.checklist.failed': 'Failed to update checklist.',
    'employment.compliance.title': 'Hours & compliance',
    'employment.compliance.no.contract': 'No active signed contract for this employee.',
    'employment.hours.ytd': 'Hours this year',
    'employment.earnings.month': 'Earnings this month',
    'employment.dpp.remaining': 'DPP hours remaining',
    'employment.suggest.switch': 'Consider switching to',
    'employment.term': 'Contract term',
    'employment.term.fixed': 'Fixed term',
    'employment.term.until.task': 'Until task completion',
    'employment.term.indefinite': 'Indefinite term',
    'employment.term.from': 'from',
    'employment.term.to': 'to',
    'employment.term.quick': 'Quick length',
    'employment.term.quick.2w': '2 weeks',
    'employment.term.quick.1m': '1 month',
    'employment.term.quick.3m': '3 months',
    'employment.term.quick.6m': '6 months',
    'employment.term.quick.1y': '1 year',
    'employment.term.dpp.long.warn': 'This period is 6+ months. At full-time hours the worker could exceed the DPP 300 h/year limit - informational only; adjust if the real workload is higher.',
    'employment.term.task.desc': 'Description of the specific work',
    'employment.term.task.placeholder': 'e.g. help with warehouse relocation 5–8 August',
    'employment.term.task.hint': 'Avoid vague phrases like “general assistance” - inspectors may treat that as a disguised employment relationship. Describe a concrete task.',
    'employment.term.indefinite.info': 'The contract has no end date and continues until either party ends it.',
    'employment.term.termination': 'Termination method',
    'employment.term.termination.required': 'Select how the indefinite contract can be terminated.',
    'employment.term.termination.notice': 'By notice (15-day period)',
    'employment.term.termination.notice.hint': 'Either party may terminate with 15 days’ notice.',
    'employment.term.termination.agreement': 'By mutual agreement',
    'employment.term.termination.agreement.hint': 'Ends when both parties agree in writing.',
    'employment.term.end.required': 'Please set an end date for a fixed-term contract.',
    'employment.term.end.after.start': 'End date must be after the start date.',
    'employment.term.until.task.dpp.only': '“Until task completion” is only available for DPP.',
    'employment.term.summary': 'Summary',
    'employment.weekly.hours.dpc.hint': 'DPČ average must not exceed 20 hours per week over the agreed period.',
    'signing.title': 'Contract',
    'signing.subtitle': 'Read the full text, then sign electronically in the app. PDF is created only after both parties sign.',
    'signing.hash': 'Document fingerprint',
    'signing.open': 'Open & sign',
    'signing.send.for.signature': 'Sign & send to worker',
    'signing.sent.toast': 'Contract sent for in-app signature.',
    'bozp.title': 'BOZP training document',
    'bozp.hint': 'Attach a PDF or write the training text. The worker must download it and confirm before signing.',
    'bozp.mode.write': 'Write in editor',
    'bozp.mode.upload': 'Upload PDF',
    'bozp.write.placeholder': 'Write BOZP / occupational safety training content…',
    'bozp.write.required': 'Write at least a short BOZP text (min. 20 characters), or upload a PDF.',
    'bozp.upload.required': 'Upload a BOZP PDF, or switch to the editor and write the text.',
    'bozp.employee.title': 'BOZP training (required)',
    'bozp.employee.hint': 'Download the BOZP PDF first, then confirm you have read it. Only then can you sign the contract.',
    'bozp.download': 'Download BOZP PDF',
    'bozp.download.again': 'Download again',
    'bozp.downloading': 'Downloading…',
    'bozp.download.done': 'BOZP PDF downloaded.',
    'bozp.download.failed': 'Failed to download BOZP PDF.',
    'bozp.download.first': 'Download the BOZP PDF before confirming.',
    'bozp.ack.text': 'I have downloaded and read the BOZP training document and agree with its contents.',
    'bozp.ack.done': 'BOZP acknowledgement saved.',
    'bozp.ack.failed': 'Failed to acknowledge BOZP.',
    'bozp.ack.required': 'Acknowledge the BOZP document before signing.',
    'bozp.sign.locked': 'Complete the BOZP steps above before signing the contract.',
    'term.title': 'End contract',
    'term.back': 'Back',
    'term.failed': 'Action failed.',
    'term.ended': 'Ended',
    'term.fixed.hint': 'If you do nothing, the contract ends automatically on the end date.',
    'term.reminder.expiry': 'This contract ends on',
    'term.reminder.task': 'This task agreement has been running over 60 days - is it still active?',
    'term.action.extend': 'Extend end date',
    'term.action.task.done': 'Mark task completed',
    'term.action.notice': 'Give notice (15 days)',
    'term.action.agreement': 'Propose mutual termination',
    'term.action.immediate': 'Immediate cancellation',
    'term.extend.hint': 'Current end date:',
    'term.extend.required': 'Choose a new end date.',
    'term.extend.submit': 'Propose amendment',
    'term.extend.done': 'Contract extended.',
    'term.extend.proposed': 'Extension amendment sent - waiting for the worker to sign.',
    'term.extend.signed': 'Amendment signed.',
    'term.extend.waiting': 'Extension amendment awaiting worker signature. New end date:',
    'term.extend.amendment.hint': 'Extension creates a written amendment (dodatek). Both parties must sign it before the end date changes.',
    'term.extend.amendment.title': 'Extension amendment',
    'term.extend.sign': 'Sign amendment',
    'term.attention.amendment': 'Your employer proposed extending the contract. Read the amendment and sign.',
    'term.task.date': 'Completion date',
    'term.task.hours': 'Hours worked (optional)',
    'term.task.note': 'Note (optional)',
    'term.task.submit': 'Complete & close',
    'term.task.done': 'Task marked done - contract closed.',
    'term.notice.hint': 'Filing notice starts delivery tracking. The 15-day period begins when the other party acknowledges delivery (or enter delivery date if already delivered outside the app).',
    'term.notice.delivered.optional': 'Delivery date (optional, if already delivered)',
    'term.notice.submit': 'File notice',
    'term.notice.filed': 'Notice filed.',
    'term.notice.ack.hint': 'Confirm you received the notice. The 15-day notice period starts today.',
    'term.notice.ack': 'Confirm delivery',
    'term.notice.acked': 'Delivery confirmed - notice period running.',
    'term.notice.running': 'Notice period ends',
    'term.agreement.hint': 'Propose an end date. The other party must confirm before the contract closes.',
    'term.agreement.note': 'Note (optional)',
    'term.agreement.submit': 'Propose agreement',
    'term.agreement.proposed': 'Termination agreement proposed.',
    'term.agreement.end': 'Agreed end date',
    'term.agreement.confirm': 'I agree to terminate the contract on the proposed date.',
    'term.agreement.sign': 'Confirm agreement',
    'term.agreement.signed': 'Agreement signature saved.',
    'term.agreement.waiting': 'You have confirmed. Waiting for the other party.',
    'term.immediate.warning': 'Immediate cancellation is exceptional and only for legally defined reasons. Delivery to the other party will be recorded.',
    'term.immediate.reason': 'Legal reason / description',
    'term.immediate.submit': 'File immediate cancellation',
    'term.immediate.filed': 'Immediate cancellation filed - awaiting delivery confirmation.',
    'term.immediate.ack.hint': 'Confirm you received the immediate cancellation notice.',
    'term.immediate.ack': 'Confirm delivery & close',
    'term.immediate.acked': 'Contract closed by immediate cancellation.',
    'term.certificate': 'Generate termination certificate',
    'term.certificate.ready': 'Termination certificate ready.',
    'term.cssz.offboard': 'Mark ČSSZ deregistration done',
    'term.cssz.offboard.done': 'ČSSZ deregistration marked done.',
    'term.reason.EXPIRED': 'End of fixed term',
    'term.reason.TASK_DONE': 'Task completed',
    'term.reason.NOTICE': 'Notice period',
    'term.reason.AGREEMENT': 'Mutual agreement',
    'term.reason.IMMEDIATE': 'Immediate cancellation',
    'term.manage': 'Manage ending',
    'term.attention.title': 'Action required',
    'term.attention.hint': 'Your employer started ending a contract. Open it and confirm delivery or agreement.',
    'term.attention.badge': 'Needs your action',
    'term.attention.cta': 'Open & respond',
    'term.attention.notice': 'Employer filed a notice. Confirm you received it - the 15-day period starts from your confirmation.',
    'term.attention.immediate': 'Employer filed an immediate cancellation. Confirm you received the notice.',
    'term.attention.agreement': 'Employer proposed ending the contract by agreement. Review and confirm.',
    'term.sign.title': 'Your electronic signature',
    'term.sign.clickwrap': 'I confirm this termination and sign it electronically.',
    'signing.editor.title': 'Contract text',
    'signing.editor.placeholder': 'Contract draft will appear here…',
    'signing.editor.hint': 'Edit before sending. PDF is not used for signing - it is generated after both signatures.',
    'signing.editor.regenerate': 'Regenerate from data',
    'signing.editor.generating': 'Generating…',
    'signing.body.required': 'Contract text is required.',
    'signing.employer.sign': 'Your electronic signature (employer)',
    'signing.method.clickwrap': 'I agree & sign',
    'signing.method.drawn': 'Draw signature',
    'signing.clickwrap.text': 'I have read the contract and sign it electronically as the employer.',
    'signing.confirm.required': 'Please confirm that you agree and want to sign.',
    'signing.draw.required': 'Please draw your signature.',
    'signing.draw.hint': 'Sign with mouse or finger',
    'signing.clear': 'Clear',
    'signing.submit': 'Sign contract',
    'signing.submitting': 'Signing…',
    'signing.signed': 'Signature saved.',
    'signing.fully.signed': 'Both parties signed - archive PDF is ready.',
    'signing.failed': 'Signing failed.',
    'signing.load.failed': 'Failed to load contract.',
    'signing.waiting.other': 'You have signed. Waiting for the other party.',
    'signing.waiting.employee': 'Waiting for worker signature',
    'signing.waiting.employer': 'Waiting for your countersignature',
    'signing.party.employer': 'Employer',
    'signing.party.employee': 'Worker',
    'signing.download.archive': 'Download archive PDF',
    'signing.identity.optional': 'Require identity check before worker signs',
    'signing.identity.optional.hint': 'Optional e-mail/SMS code. Recommended for longer contracts; skip for short gigs.',
    'signing.identity.hint': 'Enter the verification code sent to you before signing.',
    'signing.identity.request': 'Send code',
    'signing.identity.code': 'Verification code',
    'signing.identity.sent': 'Verification code sent.',
    'signing.identity.failed': 'Failed to send code.',
    'signing.identity.dev': 'Dev code',
    'create.shift.allow.leave': 'Allow unregistration',
    'create.shift.approval.type': 'Approval Type',
    'create.shift.automatic': 'Automatic',
    'create.shift.create.from.preset': 'Create from Preset',
    'create.shift.create.shift': 'Create Shift',
    'create.shift.description': 'Description',
    'create.shift.disabled': 'Disabled',
    'create.shift.end.date': 'End Date',
    'create.shift.enter.preset.name': 'Enter Preset Name',
    'create.shift.fixed': 'Fixed',
    'create.shift.location': 'Location',
    'create.shift.manual': 'Manual',
    'create.shift.max.attendees': 'Max Attendees',
    'create.shift.payment.amount': 'Payment Amount',
    'create.shift.per.hour': 'Per Hour',
    'create.shift.preset.name': 'Preset Name',
    'create.shift.save.as.preset': 'Save as Preset',
    'create.shift.save.as.preset.title': 'Save as Preset',
    'create.shift.save.preset': 'Save Preset',
    'create.shift.select.location': 'Select Location',
    'create.shift.select.preset': 'Select Preset',
    'create.shift.start.date': 'Start Date',
    'create.shift.title': 'Create a new shift',
    'create.shift.back.to.presets': 'Back to presets',
    'create.shift.title.label': 'Write a title here',
    'create.shift.description.placeholder': 'Write here…',
    'create.shift.created': 'Shift created.',
    'create.shift.created.with.assignees': 'Shift created and people assigned.',
    'create.shift.created.draft': 'Shift saved as draft. Publish when ready.',
    'create.shift.save.as.draft': 'Save as draft',
    'create.shift.save.as.draft.hint': 'Hidden from employees until you publish. No notifications yet.',
    'create.shift.save.draft': 'Save draft',
    'create.shift.saving.draft': 'Saving draft…',
    'create.shift.draft.no.recurrence': 'Draft cannot be a recurring series. Turn off recurrence or publish instead.',
    'create.shift.preset.saved': 'Preset saved.',
    'create.shift.add.people': 'Add people',
    'create.shift.add.people.search': 'Type to search',
    'create.shift.add.people.hint': 'Select a workplace to filter people assigned there.',
    'create.shift.add.people.empty': 'No people available to assign.',
    'create.shift.add.people.no.match': 'No matching people.',
    'create.shift.add.group': 'Add from group',
    'create.shift.add.group.search': 'Type to search',
    'create.shift.add.group.hint': 'Select a workplace to filter which group members can be assigned.',
    'create.shift.add.group.hint.filtered': 'Only members who match the selected workplace (and position) are added.',
    'create.shift.add.group.empty': 'No user groups yet.',
    'create.shift.add.group.no.match': 'No matching groups.',
    'create.shift.add.group.no.members': 'No assignable members in this group for the current filters.',
    'create.shift.add.group.members': '{count} of {total} can be assigned',
    'create.shift.remove.group': 'Remove group',
    'create.shift.remove.person': 'Remove',
    'create.shift.pay.min.hourly': 'Minimum hourly pay is 134.40 Kč / hour.',
    'create.shift.pay.min.hourly.hint': 'Minimum 134.40 Kč / hour',
    'create.shift.pay.fixed.effective': '≈ {rate} Kč / hour',
    'create.shift.pay.fixed.too.low': 'Fixed pay must equal at least 134.40 Kč / hour for this shift length.',
    'create.shift.use.employee.rate': 'Use employee hourly rate when set',
    'create.shift.use.employee.rate.hint': 'If the employee has their own rate, that rate is used. Otherwise the shift rate above applies. The rate is locked when they join the shift.',
    'create.shift.capacity.unreachable':
      'This shift can never be fully filled - only {available} employees match the selected workplace/position filters, but max attendees is {max}.',
    'create.shift.capacity.assignees.exceed':
      'You assigned {assigned} people, but max attendees is only {max}.',
    'create.shift.time.invalid': 'End must be after start.',
    'create.shift.with.approval': 'With Approval',
    'create.shift.without.approval': 'Without Approval',
    'cv.manager.cancel': 'Cancel',
    'cv.manager.click.to.upload': 'Click to Upload',
    'cv.manager.delete': 'Delete',
    'cv.manager.delete.confirm.message': 'Are you sure you want to delete your CV?',
    'cv.manager.delete.confirm.title': 'Delete CV',
    'cv.manager.delete.cv': 'Delete CV',
    'cv.manager.drag.and.drop': 'Drag and drop your CV here',
    'cv.manager.havent.uploaded': "You haven't uploaded a CV yet",
    'cv.manager.last.updated': 'Last Updated',
    'cv.manager.open.pdf.new.tab': 'Open PDF in New Tab',
    'cv.manager.pdf.only': 'PDF files only',
    'cv.manager.selected.file': 'Selected File',
    'cv.manager.subtitle': 'Manage your CV and job applications',
    'cv.manager.title': 'CV Manager',
    'cv.manager.upload': 'Upload',
    'cv.manager.upload.cv': 'Upload CV',
    'cv.manager.upload.description': 'Upload your CV to apply for jobs',
    'cv.manager.upload.new.cv': 'Upload New CV',
    'cv.manager.upload.title': 'Upload CV',
    'cv.manager.your.cv': 'Your CV',
    'cv.updates': 'CV Updates',
    'dashboard.hours.this.month': 'Hours This Month',
    'dashboard.monthly.earnings': 'Monthly Earnings',
    'dashboard.shifts.this.month': 'Shifts This Month',
    'edit.shift.title': 'Edit Shift',
    'edit.shift.update.shift': 'Update Shift',
    'employees.active': 'Active',
    'employees.address': 'Address',
    'employees.cancel': 'Cancel',
    'employees.copied': 'Copied!',
    'employees.copy': 'Copy',
    'employees.delete.code': 'Delete Code',
    'employees.edit.employee': 'Edit Employee',
    'employees.email': 'Email',
    'employees.expires': 'Expires',
    'employees.hourly.pay.rate': 'Hourly Pay Rate',
    'employees.not.eligible': 'Not Eligible',
    'employees.notes': 'Notes',
    'employees.personal.info': 'Personal Info',
    'employees.phone': 'Phone',
    'employees.positions': 'Positions',
    'employees.workplaces': 'Workplaces',
    'employees.no.workplaces': 'No workplaces available.',
    'employees.save': 'Save',
    'employees.sex': 'Sex',
    'employees.start.date': 'Start Date',
    'employees.no.position': 'No position',
    'employees.pay.rate': 'Pay Rate',
    'pay.per.hour': '/hr',
    'employees.status': 'Status',
    'employees.hired.date': 'Hired Date',
    'employees.min.hours.title': 'Minimum hours',
    'employees.min.hours.enable': 'Track minimum scheduled hours',
    'employees.min.hours.amount': 'Minimum hours',
    'employees.min.hours.period.week': 'Per week',
    'employees.min.hours.period.month': 'Per month',
    'employees.min.hours.week.short': 'week',
    'employees.min.hours.save': 'Save minimum hours',
    'employees.min.hours.saved': 'Minimum hours saved.',
    'employees.min.hours.save.failed': 'Failed to save minimum hours.',
    'employees.min.hours.current': 'Scheduled this period',
    'employees.min.hours.history': 'Period history',
    'employees.min.hours.open': 'open',
    'employees.min.hours.card': 'Min. hours',
    'employees.min.hours.disclaimer':
      'Ompli only sees hours at this employer. Hours at other employers are not included — legal compliance remains your responsibility.',
    'employees.min.hours.need.contract':
      'Set an active signed contract (type and agreed hours where required) before configuring minimum hours.',
    'employees.min.hours.go.contracts': 'Go to Contracts',
    'employees.min.hours.contract.type': 'Contract type',
    'employees.min.hours.agreed': 'Agreed',
    'employees.min.hours.confirm.title': 'Projected hours exceed DPP yearly cap',
    'employees.min.hours.confirm.body':
      'Fully meeting this minimum for the rest of the year would exceed the 300 h DPP limit. Save anyway?',
    'employees.min.hours.confirm.save': 'Save anyway',
    'dashboard.min.hours.under.title': 'Employees under minimum',
    'dashboard.min.hours.under.empty.title': 'Everyone is on track',
    'dashboard.min.hours.under.empty.desc':
      'No employees with a minimum-hours target are under scheduled hours right now.',
    'settings.min.hours.title': 'Minimum hours',
    'settings.min.hours.tooltip':
      'When enabled, owners and managers can set per-employee minimum scheduled hours and see under-minimum alerts on the dashboard.',
    'settings.min.hours.enable': 'Enable minimum hours tracking for this business',
    'employees.min.hours.feature.off':
      'Minimum hours tracking is turned off for this business. Enable it in Settings → Features.',
    'employees.min.hours.go.settings': 'Open Settings → Features',
    'filters': 'Filters',
    'inactive': 'Inactive',
    'job.board.current.cv': 'Current CV',
    'job.board.cv.description': 'CV Description',
    'job.board.daily.attendance': 'Daily Attendance',
    'job.board.last.updated': 'Last Updated',
    'job.board.manage.cv': 'Manage CV',
    'job.board.no.cv.uploaded': 'No CV Uploaded',
    'job.board.remove.job.listing': 'Remove Job Listing',
    'job.board.view.cv': 'View CV',
    'job.board.view.details': 'View Details',
    'job.board.your.cv': 'Your CV',
    'job.board.flexible.hours': 'Flexible Hours',
    'job.board.hiring.role': 'Hiring for',
    'job.board.edit.job': 'Edit job listing',
    'job.board.save.changes': 'Save changes',
    'job.board.saving': 'Saving...',
    'job.board.posting': 'Posting...',
    'job.board.cancel': 'Cancel',
    'job.board.create.success': 'Job listing created.',
    'job.board.edit.success': 'Job listing updated.',
    'job.board.edit.failed': 'Failed to update job listing.',
    'job.board.delete.success': 'Job listing removed.',
    'job.board.form.title': 'Job title',
    'job.board.form.image': 'Image',
    'job.board.form.image.placeholder': 'https://example.com/image.jpg',
    'job.board.form.image.upload': 'Upload image',
    'job.board.form.image.or.url': 'Or paste image URL',
    'job.board.form.image.hint': 'JPG, PNG, GIF or WebP · max 5 MB',
    'job.board.form.image.uploading': 'Uploading...',
    'job.board.form.image.uploaded': 'Image uploaded.',
    'job.board.form.image.upload.failed': 'Failed to upload image.',
    'job.board.form.image.remove': 'Remove image',
    'job.board.form.location': 'Workplace',
    'job.board.form.start.date': 'Possible start date',
    'job.board.form.hourly.pay': 'Hourly pay rate',
    'job.board.form.min.age': 'Minimum age',
    'job.board.form.required': 'Required people',
    'job.board.form.required.hint': 'How many people you want to hire for this listing.',
    'job.board.form.employment.type': 'Employment type',
    'job.board.form.no.business': 'No business found.',
    'job.board.form.create.failed': 'Failed to create job listing.',
    'job.board.details.min.age': 'Minimum age: {age}',
    'job.details.edit.subtitle': 'Update title, pay, location and other listing details.',
    'job.details.edit.title': 'Job title',
    'job.details.edit.image': 'Image URL',
    'job.details.edit.start.date': 'Possible start date',
    'job.details.edit.required': 'Required people',
    'job.details.edit.location.required': 'Please select a workplace.',
    'job.details.description.required': 'Please enter a job description.',
    'job.details.back': 'Back to Job Board',
    'job.details.not.found': 'Job not found',
    'job.details.meta.location': 'Location',
    'job.details.meta.type': 'Employment type',
    'job.details.meta.min.age': 'Minimum age',
    'job.details.meta.pay': 'Hourly pay rate',
    'job.details.meta.hiring': 'Looking for',
    'job.details.hiring.employee': 'Employee',
    'job.details.hiring.manager': 'Manager',
    'job.details.description': 'Job description',
    'job.details.posted.on': 'Posted on',
    'job.details.apply.title': 'Apply now',
    'job.details.apply.subtitle': 'Submit your application and we will get back to you shortly.',
    'job.details.apply.now': 'Apply now',
    'job.details.apply.again': 'Second chance: Apply again',
    'job.details.applying': 'Applying...',
    'job.details.apply.blocked': 'You cannot apply for this job again.',
    'job.details.apply.success': 'Application submitted.',
    'job.details.apply.failed': 'Failed to apply for this job.',
    'job.details.status.pending': 'Application pending',
    'job.details.status.approved': 'Application approved',
    'job.details.status.rejected': 'Application rejected',
    'job.details.about.company': 'About {name}',
    'job.details.about.fallback': 'Learn more about this company and what it is like to work with them.',
    'job.details.view.company': 'View company',
    'job.details.hiring.employee.option': 'Employee',
    'job.details.hiring.manager.option': 'Manager',
    'job.details.publisher':
      'This listing is published by {companyName}, Company ID (IČO): {ico}, with its registered office at: {street}, {city}, {zip}, {region}, {country}.',
    'job.details.publisher.contact':
      'If interested, you can contact us by phone: {phone}, email: {email}, or visit our website: {website}.',
    'job.details.publisher.na': '-',
    'login.login.failed': 'Login Failed',
    'login.network.error': 'Network Error',
    'login.show.password': 'Show Password',
    'managers.cancel': 'Cancel',
    'managers.copied': 'Copied!',
    'managers.delete.invite': 'Delete Invite',
    'managers.edit.manager': 'Edit Manager',
    'managers.expires': 'Expires',
    'managers.hourly.pay.rate': 'Hourly Pay Rate',
    'managers.save': 'Save',
    'messages.failed.fetch.contacts': 'Failed to Fetch Contacts',
    'messages.failed.send': 'Failed to Send',
    'messages.media': 'Media',
    'messages.media.selected': 'Media Selected',
    'messages.replying.to': 'Replying to',
    'messages.send': 'Send',
    'messages.type.message': 'Type your message...',
    'messages.your.browser.no.video': 'Your browser does not support video',
    'my.contracts.active': 'Active',
    'my.contracts.all': 'All',
    'my.contracts.awaiting.approval': 'Awaiting Approval',
    'my.contracts.choose.pdf': 'Choose PDF',
    'my.contracts.contract': 'Contract',
    'my.contracts.contract.history': 'Contract History',
    'my.contracts.download': 'Download',
    'my.contracts.download.signed': 'Download Signed',
    'my.contracts.duration': 'Duration',
    'my.contracts.expiry': 'Expiry',
    'my.contracts.failed.download': 'Failed to Download',
    'my.contracts.failed.upload': 'Failed to Upload',
    'my.contracts.from': 'From',
    'my.contracts.inactive': 'Inactive',
    'my.contracts.no.history': 'No contract history',
    'my.contracts.no.history.desc': 'Signed and closed contracts will appear here.',
    'my.contracts.no.unsigned': 'No unsigned contracts',
    'my.contracts.no.unsigned.desc': 'When your employer sends a contract, it will show up here for you to sign.',
    'my.contracts.failed.load': 'Failed to load contracts.',
    'my.contracts.please.download.sign': 'Please Download and Sign',
    'my.contracts.reason': 'Reason',
    'my.contracts.sending.signed': 'Sending Signed',
    'my.contracts.signed.uploaded': 'Signed Contract Uploaded',
    'my.contracts.terminated.by.owner': 'Terminated by Owner',
    'my.contracts.title': 'My Contracts',
    'my.contracts.until': 'Until',
    'my.shifts.subtitle': 'Manage your work shifts',
    'my.shifts.title': 'My Shifts',
    'positions.any': 'Any',
    'positions.confirm.delete': 'Confirm Delete',
    'positions.female': 'Female',
    'positions.gender.optional': 'Gender (Optional)',
    'positions.male': 'Male',
    'positions.max.people.optional': 'Max People (Optional)',
    'positions.no.positions': 'No Positions',
    'positions.other': 'Other',
    'positions.position.name': 'Position Name',
    'positions.save': 'Save',
    'presets.cancel': 'Cancel',
    'presets.create.preset': 'Create Preset',
    'presets.create.shift.preset': 'Create Shift Preset',
    'presets.end.hour': 'End Hour',
    'presets.example.cashier': 'Example: Cashier',
    'presets.example.morning.shift': 'Example: Morning Shift',
    'presets.failed.create.preset': 'Failed to Create Preset',
    'presets.created': 'Preset created.',
    'presets.updated': 'Preset updated.',
    'presets.deleted': 'Preset deleted.',
    'presets.delete.confirm': 'Are you sure you want to delete this preset?',
    'presets.empty.title': 'No shift presets yet',
    'presets.empty.description':
      'Create a preset to quickly fill in shift details when creating new shifts.',
    'presets.edit.shift.preset': 'Edit shift preset',
    'presets.saving': 'Saving…',
    'presets.failed.update.preset': 'Failed to update preset',
    'presets.failed.fetch.preset': 'Failed to load preset',
    'presets.failed.delete.preset': 'Failed to Delete Preset',
    'presets.failed.fetch.locations': 'Failed to Fetch Locations',
    'presets.failed.fetch.presets': 'Failed to Fetch Presets',
    'presets.fixed': 'Fixed',
    'presets.go.back': 'Go Back',
    'presets.hourly': 'Hourly',
    'presets.location': 'Location',
    'presets.max.attendees': 'Max Attendees',
    'presets.no.business': 'No Business',
    'presets.pay.amount': 'Pay Amount',
    'presets.pay.type': 'Pay Type',
    'presets.preset.name': 'Preset Name',
    'presets.select.location': 'Select Location',
    'presets.shift.name': 'Shift Name',
    'presets.start.hour': 'Start Hour',
    'presets.subtitle': 'Create and manage shift presets',
    'presets.title': 'Shift Presets',
    'presets.use.preset': 'Use Preset',
    'register.basic.info': 'Basic Information',
    'register.confirm.password': 'Confirm Password',
    'register.details': 'Details',
    'register.email': 'Email',
    'register.first.name': 'First Name',
    'register.last.name': 'Last Name',
    'register.network.error': 'Network Error',
    'register.password': 'Password',
    'register.passwords.dont.match': 'Passwords do not match',
    'register.please.enter.company': 'Please enter company name',
    'register.please.fill.all.fields': 'Please fill all fields',
    'register.please.select.role': 'Please select a role',
    'register.registration.failed': 'Registration Failed',
    'register.registration.success': 'Account created.',
    'register.oauth.linked': 'Continue with your Google/Microsoft/Apple account',
    'register.oauth.calendar.hint': 'Google/Microsoft calendar syncs after registration. Apple Calendar can be linked via ICS in Settings.',
    'register.oauth.calendar.ready': 'Account created and calendar connected.',
    'register.oauth.expired': 'OAuth session expired. Please sign in with Google, Microsoft, or Apple again.',
    'register.review': 'Review',
    'register.select.role': 'Select Role',
    'register.username': 'Username',
    'register.back': 'Back',
    'register.registering': 'Registering…',
    'register.create.account': 'Create account',
    'register.details.employer': 'Personal & company details',
    'register.details.employee': 'Personal details',
    'register.details.manager': 'Personal details',
    'register.section.personal': 'Personal',
    'register.section.company': 'Company',
    'register.sex': 'Sex',
    'register.sex.select': 'Select…',
    'register.sex.male': 'Male',
    'register.sex.female': 'Female',
    'register.sex.other': 'Other',
    'register.phone': 'Phone',
    'register.phone.search': 'Search country…',
    'register.phone.search.none': 'No country found',
    'register.personal.address': 'Home address',
    'register.invite.section': 'Workplace invite',
    'register.invite.question': 'Do you already have an invite code for a company?',
    'register.invite.yes': 'Yes',
    'register.invite.no': 'No',
    'register.invite.code': 'Invite code',
    'register.invite.placeholder': 'Enter invite code',
    'register.invite.hint': 'Ask your employer for a code - you can also join later from My jobs.',
    'register.invite.from.link': 'Prefilled from your invite link',
    'register.invite.checking': 'Checking invite code…',
    'register.invite.invalid': 'Invalid or expired invite code',
    'register.invite.required': 'Please enter your invite code',
    'register.invite.joining': 'You will join {name}',
    'register.invite.joined': 'Joined {name}.',
    'register.invite.join.failed': 'Account created, but joining the workplace failed. You can join later with the invite code.',
    'register.avatar.upload': 'Upload profile photo',
    'register.avatar.hint': 'Optional · JPG or PNG',
    'register.logo.upload': 'Upload company logo',
    'register.logo.hint': 'Optional · JPG or PNG',
    'register.please.fill.personal': 'Please fill sex, phone and home address.',
    'register.please.fill.legal': 'Please fill registered company name and IČO (ARES).',
    'register.please.fill.business.address': 'Please fill the company registered address.',
    'register.review.role': 'Role',
    'register.review.name': 'Name',
    'register.review.selected': 'Selected',
    'register.panel.title.0': 'Welcome to Ompli',
    'register.panel.desc.0': 'Choose your role and set up your workspace in a few guided steps.',
    'register.panel.title.1': 'Your account, secured',
    'register.panel.desc.1': 'Create login details you will use across shifts, jobs, and team messaging.',
    'register.panel.title.2': 'Tell us about you',
    'register.panel.desc.2': 'Profile and company details keep scheduling, contracts, and payroll accurate.',
    'register.panel.title.3': 'Ready to start',
    'register.panel.desc.3': 'Review everything once - then jump straight into your Ompli dashboard.',
    'register.panel.footer': 'Shift scheduling made simple for modern teams',
    'shift.details.not.found': 'Shift Details Not Found',
    'shift.details.title': 'Shift details',
    'shift.details.attendees': 'Attendees',
    'shift.details.no.attendees': 'No attendees yet',
    'shift.details.start': 'Start',
    'shift.details.end': 'End',
    'shift.details.remove': 'Remove',
    'shift.details.remove.title': 'Remove employee',
    'shift.details.remove.confirm': 'Are you sure you want to remove this employee from the shift?',
    'shift.details.remove.success': 'Employee removed from shift.',
    'shift.details.remove.failed': 'Failed to remove employee.',
    'shift.details.delete.title': 'Delete shift',
    'shift.details.delete.confirm': 'Are you sure you want to delete this shift? This action cannot be undone.',
    'shift.details.delete.success': 'Shift deleted.',
    'shift.details.delete.failed': 'Failed to delete shift.',
    'shift.details.deleting': 'Deleting…',
    'shift.details.load.failed': 'Failed to load shift.',
    'shift.details.attend': 'Attend',
    'shift.details.request.attendance': 'Request attendance',
    'shift.details.attend.success': 'Attendance requested.',
    'shift.details.attend.failed': 'Failed to request attendance.',
    'shift.details.request.leave': 'Request unregistration',
    'shift.details.leave': 'Unregister',
    'shift.details.leave.failed': 'Failed to leave shift.',
    'shift.details.swap.title': 'Offer swap',
    'shift.details.swap.hint': 'Send the shift to a colleague in chat, or copy a shareable link.',
    'shift.details.swap.to.colleague': 'Send to chat',
    'shift.details.swap.copy.link': 'Copy link',
    'shift.details.leave.for': 'You are requesting to unregister from: {title}',
    'shift.details.leave.reason.placeholder': 'Reason (optional)',
    'shift.details.submit.leave': 'Submit request',
    'shift.details.processing': 'Processing…',
    'shift.details.save.success': 'Shift saved.',
    'shift.details.save.failed': 'Failed to save shift.',
    'shifts.attend': 'Attend',
    'shifts.cancelled': 'Cancelled',
    'shifts.checking.contracts': 'Checking Contracts',
    'shifts.contact.boss': 'Contact Boss',
    'shifts.contact.owner': 'Contact Owner',
    'shifts.contracts.enabled.message': 'Contracts are enabled for this business',
    'shifts.go.to.contracts': 'Go to Contracts',
    'shifts.no.active.contract': 'No Active Contract',
    'shifts.payment.fixed': 'Fixed Payment',
    'shifts.shifts.on': 'Shifts on',
    'shifts.side.month.toggle': 'This month',
    'shifts.side.month.title': 'Shifts this month',
    'shifts.no.shifts.for.month': 'No shifts this month',
    'shifts.side.week.toggle': 'This week',
    'shifts.side.week.title': 'Shifts this week',
    'shifts.no.shifts.for.week': 'No shifts this week',
    'shifts.status.approved': 'Approved',
    'shifts.status.full': 'Full',
    'shifts.status.ongoing': 'Ongoing',
    'shifts.status.open': 'Open',
    'shifts.status.past': 'Past',
    'shifts.status.pending': 'Pending',
    'shifts.status.rejected': 'Rejected',
    'shifts.view.details': 'View Details',
    'shifts.you.are.inactive': 'You are inactive',
    'workplaces.add.location.button': 'Add Location',
    'workplaces.add.new.location': 'Add New Location',
    'workplaces.address': 'Address',
    'workplaces.address.placeholder': 'Enter address',
    'workplaces.edit.location': 'Edit Location',
    'workplaces.location.name': 'Location Name',
    'workplaces.location.name.placeholder': 'Enter location name',
    'workplaces.no.locations': 'No Locations',
    'workplaces.save.changes': 'Save Changes',
    'workplaces.subtitle.employee': 'View your assigned workplaces',
    'workplaces.subtitle.manager': 'Manage workplaces for your team',
    'workplaces.created': 'Workplace created.',
    'workplaces.updated': 'Workplace updated.',
    'workplaces.deleted': 'Workplace deleted.',
    'workplaces.confirm.delete': 'Delete this workplace?',
    'workplaces.form.subtitle': 'Fill in the details below to set up your workplace',
    'workplaces.section.basic': 'Basic Information',
    'workplaces.section.attendance': 'Attendance',
    'workplaces.section.address': 'Address',
    'workplaces.section.employees': 'Employees',
    'workplaces.active': 'Active',
    'workplaces.active.sub': 'Employees can be assigned to this workplace',
    'workplaces.clock.in.before': 'Minutes before shift start (employee can clock in)',
    'workplaces.break.entitlement': 'Break entitlement',
    'workplaces.break.after.minutes': 'After (worked minutes)',
    'workplaces.break.duration.minutes': 'Break (total minutes)',
    'workplaces.early.arrival': 'Early arrival',
    'workplaces.late.arrival': 'Late arrival',
    'workplaces.early.departure': 'Early departure',
    'workplaces.late.departure': 'Late departure',
    'workplaces.record.exact.time': 'Record exact arrival time',
    'workplaces.record.exact.departure': 'Record exact departure time',
    'workplaces.round.breaks': 'Round breaks',
    'workplaces.no.employees.available': 'No employees found in this business.',
    'workplaces.add.people.search': 'Search people…',
    'workplaces.add.people.no.match': 'No matching people',
    'workplaces.remove.person': 'Remove person',
    'workplaces.form.clock.in': 'Clock-in before',
    'workplaces.form.break.after': 'Break after',
    'workplaces.form.break.duration': 'Break length',
    'workplaces.empty.desc': 'Add your first workplace location so employees can find their shifts.',
    'home.nav.features': 'Features',
    'home.nav.pricing': 'Pricing',
    'home.nav.about': 'About',
    'home.nav.contact': 'Contact',
    'home.nav.help': 'Help',
    'home.auth.sign.in': 'Sign In',
    'home.auth.get.started': 'Get Started',
    'home.hero.title.line1': 'Modern Shift Management',
    'home.hero.title.line2': 'For Growing Teams',
    'home.hero.subtitle': 'Streamline scheduling, contracts, and team communication - all in one intelligent platform built for employers and employees.',
    'home.hero.try.free': 'Try for Free',
    'home.hero.watch.demo': 'Watch Demo',
    'home.features.easy.shift': 'Easy shift planning',
    'home.features.ai.powered': 'AI-powered insights',
    'home.features.user.friendly': 'User-friendly interface',
    'home.features.multiple.languages': 'Multiple languages',
    'home.section.experience.title': 'Everything You Need',
    'home.section.experience.subtitle': 'Powerful features designed to simplify workforce management',
    'home.features.smart.scheduling.title': 'Smart Scheduling',
    'home.features.smart.scheduling.description': 'Create and manage shifts with presets, recurring schedules, and real-time updates.',
    'home.features.team.communication.title': 'Team Communication',
    'home.features.team.communication.description': 'Built-in messaging keeps your team connected and informed.',
    'home.features.analytics.dashboard.title': 'Analytics Dashboard',
    'home.features.analytics.dashboard.description': 'Track attendance, hours, and performance with intuitive dashboards.',
    'home.features.contract.management.title': 'Contract Management',
    'home.features.contract.management.description': 'Generate, send, and track employment contracts digitally.',
    'home.features.leave.management.title': 'Unregistration management',
    'home.features.leave.management.description': 'Handle unregistration requests and attendance approvals effortlessly.',
    'home.features.job.board.title': 'Job Board',
    'home.features.job.board.description': 'Post jobs and manage applications directly in the platform.',
    'home.features.customisable.title': 'Customisable',
    'home.features.customisable.description': 'Adapt the platform to your business needs and workflows.',
    'home.features.easy.to.use.title': 'Easy to Use',
    'home.features.easy.to.use.description': 'Intuitive interface that your team will love from day one.',
    'home.features.multiple.workplaces.title': 'Multiple Workplaces',
    'home.features.multiple.workplaces.description': 'Manage multiple locations from a single dashboard.',
    'home.features.employee.friendly.title': 'Employee Friendly',
    'home.features.employee.friendly.description': 'Employees can browse shifts, track earnings, and manage their schedule.',
    'home.section.testimonials.title': 'Trusted by Teams',
    'home.section.testimonials.subtitle': 'See what business owners and managers are saying about Ompli',
    'home.testimonials.track.hours.quote': 'Ompli made it incredibly easy to track hours and manage our team across multiple locations.',
    'home.testimonials.shift.presets.quote': 'Shift presets save us hours every week. Scheduling has never been this simple.',
    'home.testimonials.costs.less.quote': 'We cut our scheduling costs significantly while improving team satisfaction.',
    'home.section.all.work.title': 'All Your Work in One Place',
    'home.section.all.work.subtitle': 'From dashboards to contracts - everything your team needs',
    'home.features.dashboard.title': 'Dashboard',
    'home.features.dashboard.description': 'Get a complete overview of your business at a glance.',
    'home.features.team.management.title': 'Team Management',
    'home.features.team.management.description': 'Manage employees, managers, and roles with ease.',
    'home.features.schedule.view.title': 'Schedule View',
    'home.features.schedule.view.description': 'Visual calendar for all shifts and team availability.',
    'home.features.analytics.title': 'Analytics',
    'home.features.analytics.description': 'Data-driven insights to optimize your workforce.',
    'home.features.messages.title': 'Messages',
    'home.features.messages.description': 'Real-time communication between team members.',
    'home.features.contracts.title': 'Contracts',
    'home.features.contracts.description': 'Digital contract management from creation to signature.',
    'home.image.placeholder': 'Screenshot coming soon',
    'home.section.how.it.works.title': 'How It Works',
    'home.section.how.it.works.subtitle': 'Get started in minutes, not days',
    'home.steps.sign.up.title': 'Sign Up',
    'home.steps.sign.up.description': 'Create your business account in under 2 minutes.',
    'home.steps.invite.team.title': 'Invite Your Team',
    'home.steps.invite.team.description': 'Send invites to employees and managers via email.',
    'home.steps.configure.settings.title': 'Configure Settings',
    'home.steps.configure.settings.description': 'Set up workplaces, positions, and shift presets.',
    'home.steps.start.managing.title': 'Start Managing',
    'home.steps.start.managing.description': 'Create shifts, manage contracts, and grow your business.',
    'home.section.pricing.title': 'Simple, Transparent Pricing',
    'home.section.pricing.subtitle': 'Start free, scale as you grow',
    'home.pricing.free.name': 'Free',
    'home.pricing.free.description': 'Perfect for small teams getting started',
    'home.pricing.features.up.to.5.employees': 'Up to 5 employees',
    'home.pricing.features.basic.scheduling': 'Basic scheduling',
    'home.pricing.features.team.messaging': 'Team messaging',
    'home.pricing.features.email.support': 'Email support',
    'home.pricing.features.job.board': 'Job board access',
    'home.pricing.button.get.started': 'Get Started',
    'home.pricing.professional.name': 'Professional',
    'home.pricing.period.credits.user.month': 'credits/user/month',
    'home.pricing.professional.description': 'For growing businesses with more team members',
    'home.pricing.features.everything.from.free': 'Everything in Free',
    'home.pricing.features.unlimited.employees': 'Unlimited employees',
    'home.pricing.features.contract.management': 'Contract management',
    'home.pricing.features.priority.support': 'Priority support',
    'home.pricing.features.credit.based.billing': 'Credit-based billing',
    'home.pricing.features.referral.system': 'Referral system',
    'home.pricing.button.upgrade.to.pro': 'Upgrade to Pro',
    'home.pricing.enterprise.name': 'Enterprise',
    'home.pricing.period.contact.us': 'contact us',
    'home.pricing.enterprise.description': 'Custom solutions for large organizations',
    'home.pricing.features.everything.from.professional': 'Everything in Professional',
    'home.pricing.features.dedicated.support': 'Dedicated support',
    'home.pricing.features.bulk.discounts': 'Bulk discounts',
    'home.pricing.features.best.for.big.companies': 'Best for large companies',
    'home.pricing.features.your.logo.on.website': 'Your logo on our website',
    'home.pricing.button.contact.sales': 'Contact Sales',
    'home.pricing.most.popular': 'Most Popular',
    'home.pricing.ready.to.start': 'Ready to get started?',
    'home.pricing.try.ompli.free': 'Try Ompli free for your team today.',
    'home.section.contact.title': 'Get in Touch',
    'home.section.contact.subtitle': 'Have questions? We would love to hear from you.',
    'home.contact.form.first.name': 'First Name',
    'home.contact.form.placeholder.first.name': 'John',
    'home.contact.form.last.name': 'Last Name',
    'home.contact.form.placeholder.last.name': 'Doe',
    'home.contact.form.email': 'Email',
    'home.contact.form.placeholder.email': 'john@company.com',
    'home.contact.form.company.name.label': 'Company Name',
    'home.contact.form.company.name.placeholder': 'Your company',
    'home.contact.form.company.size.label': 'Company Size',
    'home.contact.form.company.size.placeholder': 'Select size',
    'home.contact.form.company.size.1.10': '1-10 employees',
    'home.contact.form.company.size.11.50': '11-50 employees',
    'home.contact.form.company.size.51.200': '51-200 employees',
    'home.contact.form.company.size.201.1000': '201-1000 employees',
    'home.contact.form.company.size.1000.plus': '1000+ employees',
    'home.contact.form.subject.label': 'Subject',
    'home.contact.form.subject.placeholder': 'Select a subject',
    'home.contact.form.subject.general': 'General Inquiry',
    'home.contact.form.subject.sales': 'Sales Question',
    'home.contact.form.subject.support': 'Technical Support',
    'home.contact.form.subject.partnership': 'Partnership',
    'home.contact.form.subject.other': 'Other',
    'home.contact.form.message.label': 'Message',
    'home.contact.form.message.placeholder': 'Tell us how we can help...',
    'home.contact.form.updates.checkbox': 'Send me product updates and news',
    'home.contact.form.success': 'Message sent successfully!',
    'home.contact.form.send': 'Send Message',
    'home.footer.description': 'Ompli is a modern shift management platform that helps businesses schedule, communicate, and grow.',
    'home.footer.company.title': 'Company',
    'home.footer.about.us': 'About Us',
    'home.footer.careers': 'Careers',
    'home.footer.blog': 'Blog',
    'home.footer.pricing': 'Pricing',
    'home.footer.product.title': 'Product',
    'home.footer.scheduling': 'Scheduling',
    'home.footer.contract': 'Contracts',
    'home.footer.analytics': 'Analytics',
    'home.footer.messaging': 'Messaging',
    'home.footer.resources.title': 'Resources',
    'home.footer.help.center': 'Help Center',
    'home.footer.api.docs': 'API Docs',
    'home.footer.tutorials': 'Tutorials',
    'home.footer.templates': 'Templates',
    'home.footer.contact.title': 'Contact',
    'home.footer.copyright': '© 2025 Ompli. All rights reserved.',
    'login.quick.login.admin': 'Quick login as Admin',
    'admin.badge': 'Platform Admin',
    'admin.nav.dashboard': 'Overview',
    'admin.nav.users': 'Users',
    'admin.nav.businesses': 'Businesses',
    'admin.nav.credits': 'Credits',
    'admin.nav.contacts': 'Contacts',
    'admin.nav.activity': 'Activity',
    'admin.nav.contracts': 'Contracts',
    'admin.nav.jobs': 'Jobs',
    'admin.nav.blog': 'Blog',
    'admin.blog.title': 'Blog',
    'admin.blog.subtitle': 'Write and publish Magazín articles (markdown, same renderer as Help)',
    'admin.blog.new': 'New article',
    'admin.blog.edit': 'Edit',
    'admin.blog.delete': 'Delete',
    'admin.blog.view': 'View',
    'admin.blog.empty.title': 'No articles yet',
    'admin.blog.empty.description': 'Create your first Magazín post for the public blog.',
    'admin.blog.search.empty': 'No articles match your search',
    'admin.blog.load.error': 'Failed to load articles',
    'admin.blog.save.success': 'Article saved as draft',
    'admin.blog.save.published': 'Article published',
    'admin.blog.save.error': 'Failed to save article',
    'admin.blog.delete.confirm': 'Delete this article permanently?',
    'admin.blog.delete.success': 'Article deleted',
    'admin.blog.delete.error': 'Failed to delete article',
    'admin.blog.validation.title': 'Title is required',
    'admin.blog.validation.body': 'Body is required',
    'admin.blog.status.draft': 'Draft',
    'admin.blog.status.published': 'Published',
    'admin.blog.editor.new': 'New article',
    'admin.blog.editor.edit': 'Edit article',
    'admin.blog.editor.preview': 'Preview',
    'admin.blog.editor.editMode': 'Edit',
    'admin.blog.field.title': 'Title',
    'admin.blog.field.slug': 'URL slug',
    'admin.blog.field.excerpt': 'Excerpt (card)',
    'admin.blog.field.lead': 'Lead',
    'admin.blog.field.coverUrl': 'Cover image URL',
    'admin.blog.field.coverCaption': 'Cover caption',
    'admin.blog.field.body': 'Body (markdown)',
    'admin.blog.action.draft': 'Save draft',
    'admin.blog.action.publish': 'Publish',
    'admin.dashboard.title': 'Platform overview',
    'admin.dashboard.subtitle': 'Cross-tenant metrics and ops shortcuts',
    'admin.dashboard.low.balance': 'Low credit balance',
    'admin.dashboard.low.balance.empty': 'No businesses with low credit balance.',
    'admin.stats.users': 'Users',
    'admin.stats.businesses': 'Businesses',
    'admin.stats.credits.purchased': 'Credits purchased',
    'admin.stats.credits.deducted': 'Credits deducted',
    'admin.stats.credits.balance': 'Total balance',
    'admin.stats.credits.admin': 'Admin adjustments',
    'admin.stats.contacts.new': 'New contacts',
    'admin.stats.suspended.users': 'Suspended users',
    'admin.stats.suspended.businesses': 'Suspended businesses',
    'admin.stats.deduction.today': 'Deductions today',
    'admin.stats.deduction.none': 'Not run',
    'admin.stats.employees': 'Employees',
    'admin.stats.managers': 'Managers',
    'admin.stats.locations': 'Workplaces',
    'admin.ops.deduction.run': 'Run daily deduction',
    'admin.ops.deduction.success': 'Daily credit deduction completed.',
    'admin.users.title': 'Users',
    'admin.users.detail.title': 'User details',
    'admin.users.subtitle': 'Search and manage every account on the platform',
    'admin.users.search.placeholder': 'Email, name, username…',
    'admin.users.empty': 'No users found',
    'admin.users.empty.desc': 'Try a different search or filter.',
    'admin.users.label': 'users',
    'admin.users.joined': 'Joined',
    'admin.users.businesses': 'Businesses',
    'admin.users.no.business': 'No business membership',
    'admin.users.more.businesses': '+{n} more',
    'admin.users.edit': 'Edit profile',
    'admin.filter.business': 'Business',
    'admin.users.stats.title': 'Work statistics',
    'admin.users.stats.earnings': 'Earnings',
    'admin.users.stats.earnings.info':
      'Total pay from approved shifts in the selected period (from the worked-hours ledger).',
    'admin.users.stats.shifts': 'Shifts',
    'admin.users.stats.shifts.info':
      'Number of approved shifts worked in the selected period.',
    'admin.users.stats.hours': 'Hours',
    'admin.users.stats.hours.info':
      'Total hours worked on approved shifts (breaks deducted where configured).',
    'admin.users.stats.vsPrevious': 'vs previous period',
    'admin.users.stats.avgRate': 'Avg {rate}/h',
    'admin.users.stats.trendTitle': 'Earnings & hours',
    'admin.users.stats.byBusiness': 'Earnings by business',
    'admin.users.stats.empty': 'No approved shifts in this period.',
    'admin.users.activity.title': 'App activity',
    'admin.users.activity.logins': 'Logins',
    'admin.users.activity.pageViews': 'Page views',
    'admin.users.activity.actions': 'Actions',
    'admin.users.activity.uniquePages': 'Unique pages',
    'admin.users.activity.events': 'Events',
    'admin.users.activity.trend': 'Logins, pages & actions',
    'admin.users.activity.byHour': 'Activity by hour',
    'admin.users.activity.byWeekday': 'Activity by weekday',
    'admin.users.activity.devices': 'Devices',
    'admin.users.activity.browsers': 'Browsers',
    'admin.users.activity.topPages': 'Most visited pages',
    'admin.users.activity.recentLogins': 'Recent logins',
    'admin.users.activity.recentActions': 'Recent actions in app',
    'admin.users.activity.empty': 'No login/device data in this period yet.',
    'admin.users.activity.emptyPages': 'No page views recorded yet. Data appears as the user browses the app.',
    'admin.users.activity.emptyActions': 'No audited actions in this period.',
    'admin.users.activity.weekday.0': 'Sun',
    'admin.users.activity.weekday.1': 'Mon',
    'admin.users.activity.weekday.2': 'Tue',
    'admin.users.activity.weekday.3': 'Wed',
    'admin.users.activity.weekday.4': 'Thu',
    'admin.users.activity.weekday.5': 'Fri',
    'admin.users.activity.weekday.6': 'Sat',
    'admin.users.actions': 'Account actions',
    'admin.users.saved': 'User saved.',
    'admin.users.suspended': 'User suspended.',
    'admin.users.unsuspended': 'User unsuspended.',
    'admin.users.suspend': 'Suspend user',
    'admin.users.unsuspend': 'Unsuspend user',
    'admin.users.reset.password': 'Reset password',
    'admin.users.password.reset': 'Temporary password generated.',
    'admin.users.temp.password': 'Temporary password',
    'admin.users.confirm.promote': 'Promote this user to SUPER_ADMIN?',
    'admin.users.memberships': 'Memberships',
    'admin.users.memberships.empty': 'No business memberships.',
    'admin.users.memberships.remove': 'Remove membership',
    'admin.users.memberships.remove.confirm':
      'Remove this user from {name}? They will lose access to that business.',
    'admin.users.memberships.remove.ownerBlocked':
      'Cannot remove ownership here. Transfer the business to another owner first.',
    'admin.users.memberships.removed': 'Membership removed.',
    'admin.users.oauth': 'OAuth identities',
    'admin.users.oauth.empty': 'No OAuth identities linked.',
    'admin.users.oauth.remove': 'Remove OAuth identity',
    'admin.users.oauth.remove.confirm': 'Unlink {provider} from this user?',
    'admin.users.oauth.removed': 'OAuth identity removed.',
    'admin.owner.of': 'Owner of',
    'admin.businesses.title': 'Businesses',
    'admin.businesses.detail.title': 'Business details',
    'admin.businesses.subtitle': 'All tenants, credits, and feature flags',
    'admin.businesses.search.placeholder': 'Name, IČO, owner email…',
    'admin.businesses.empty': 'No businesses found',
    'admin.businesses.saved': 'Business saved.',
    'admin.businesses.flags': 'Settings & feature flags',
    'admin.businesses.notes': 'Platform notes',
    'admin.businesses.suspend.section': 'Suspension',
    'admin.businesses.suspend': 'Suspend business',
    'admin.businesses.unsuspend': 'Unsuspend business',
    'admin.businesses.suspended': 'Business suspended.',
    'admin.businesses.unsuspended': 'Business unsuspended.',
    'admin.businesses.related.people': 'People',
    'admin.businesses.related.workplaces': 'Workplaces',
    'admin.businesses.related.positions': 'Positions',
    'admin.businesses.related.jobs': 'Job listings',
    'admin.businesses.related.contracts': 'Contracts',
    'admin.businesses.related.invites': 'Invites',
    'admin.businesses.related.shifts': 'Recent shifts',
    'admin.businesses.related.empty': 'Nothing here yet.',
    'admin.businesses.role.owner': 'Owner',
    'admin.businesses.role.manager': 'Manager',
    'admin.businesses.role.employee': 'Employee',
    'admin.stats.contracts': 'Contracts',
    'admin.stats.jobs': 'Job listings',
    'admin.stats.shifts': 'Shifts',
    'admin.stats.positions': 'Positions',
    'admin.stats.invites': 'Invites',
    'admin.invite.used': 'Used',
    'admin.invite.open': 'Open',
    'admin.invite.expired': 'Expired',
    'admin.credits.title': 'Credits',
    'admin.credits.subtitle': 'Platform-wide credit balances and adjustments',
    'admin.credits.label': 'credits',
    'admin.credits.days.left': 'days left',
    'admin.credits.adjust': 'Adjust credits',
    'admin.credits.reason': 'Reason (required)',
    'admin.credits.apply': 'Apply',
    'admin.credits.adjusted': 'Credits updated.',
    'admin.credits.recent': 'Recent transactions',
    'admin.credits.recent.empty': 'No transactions yet.',
    'admin.credits.seeMore': 'See more',
    'admin.credits.seeLess': 'See less',
    'admin.credits.chart.title': 'Bought vs spent',
    'admin.credits.chart.bought': 'Bought',
    'admin.credits.chart.spent': 'Spent',
    'admin.credits.range.day': 'Day',
    'admin.credits.range.week': 'Week',
    'admin.credits.range.month': 'Month',
    'admin.credits.range.year': 'Year',
    'admin.contacts.title': 'Contact inbox',
    'admin.contacts.subtitle': 'Leads and enterprise inquiries from the marketing site',
    'admin.contacts.empty': 'No contact submissions',
    'admin.contacts.count': 'submissions',
    'admin.contacts.notes': 'Admin notes',
    'admin.contacts.notes.placeholder': 'Internal notes…',
    'admin.contacts.detail.title': 'Contact submission',
    'admin.contacts.saved': 'Contact updated.',
    'admin.contacts.moved': 'Contact moved.',
    'admin.contacts.move.to': 'Move to stage',
    'admin.contacts.stage.add': 'Add stage',
    'admin.contacts.stage.new': 'New stage',
    'admin.contacts.stage.created': 'Stage created.',
    'admin.contacts.stage.renamed': 'Stage renamed.',
    'admin.contacts.stage.deleted': 'Stage deleted.',
    'admin.contacts.stage.delete': 'Delete stage',
    'admin.contacts.stage.delete.confirm': 'Delete this stage? Cards will move to another stage.',
    'admin.contacts.stage.rename': 'Rename stage',
    'admin.contacts.stage.rename.hint': 'Double-click to rename',
    'admin.contacts.stage.color': 'Stage color',
    'admin.contacts.stage.empty': 'Drop cards here',
    'admin.contacts.field.firstName': 'First name',
    'admin.contacts.field.lastName': 'Last name',
    'admin.contacts.field.email': 'Email',
    'admin.contacts.field.companyName': 'Company name',
    'admin.contacts.field.companySize': 'Company size',
    'admin.contacts.field.subject': 'Subject',
    'admin.contacts.field.message': 'Message',
    'admin.contacts.field.newsletter': 'Product updates',
    'admin.contacts.newsletter.yes': 'Wants product updates',
    'admin.contacts.newsletter.no': 'No product updates',
    'admin.activity.title': 'Activity feed',
    'admin.activity.subtitle': 'Cross-tenant and platform admin audit log',
    'admin.activity.platform.only': 'Platform admin only',
    'admin.activity.empty': 'No activity yet',
    'admin.contracts.title': 'Contracts',
    'admin.contracts.subtitle': 'All employment contracts across businesses',
    'admin.contracts.empty': 'No contracts found',
    'admin.jobs.title': 'Job listings',
    'admin.jobs.subtitle': 'Job board moderation overview',
    'admin.jobs.empty': 'No job listings found',
    'admin.jobs.applications': 'applications',
    'admin.impersonation.start': 'View as user',
    'admin.impersonation.started': 'Now viewing as this user.',
    'admin.impersonation.ended': 'Returned to platform admin.',
    'admin.impersonation.exit': 'Exit impersonation',
    'admin.impersonation.banner': 'Impersonating {name} ({email})',
    'admin.flag.managers.jobs': 'Managers can manage jobs',
    'admin.flag.managers.create.wp': 'Managers can create workplaces',
    'admin.flag.managers.edit.assigned': 'Managers can edit assigned workplaces',
    'admin.flag.managers.edit.all': 'Managers can edit all workplaces',
    'admin.field.id': 'User ID',
    'admin.field.firstName': 'First name',
    'admin.field.lastName': 'Last name',
    'admin.field.email': 'Email',
    'admin.field.username': 'Username',
    'admin.field.phone': 'Phone',
    'admin.field.address': 'Address',
    'admin.field.language': 'Language',
    'admin.field.shiftsView': 'Shifts default view',
    'admin.field.created': 'Created',
    'admin.search': 'Search',
    'admin.filter.all': 'All',
    'admin.filter.status': 'Status',
    'admin.filter.suspended': 'Suspended',
    'admin.filter.active': 'Active',
    'admin.badge.suspended': 'Suspended',
    'admin.suspend.reason': 'Reason',
    'admin.view.all': 'View all',
    'admin.prev': 'Previous',
    'admin.next': 'Next',
    'admin.cancel': 'Cancel',
    'admin.working': 'Working…',
    'admin.error.generic': 'Something went wrong.',

    // Product tour
    'tour.help': 'Help',
    'tour.next': 'Next',
    'tour.back': 'Back',
    'tour.skip': 'Skip',
    'tour.finish.cta': 'Go to my app',
    'tour.finish.title': 'You are ready',
    'tour.finish.body': 'You have seen how Ompli works with sample data. Continue into your real workspace - it may still be empty until you create your first items.',
    'tour.demo.banner.title': 'Demo environment',
    'tour.demo.banner.body': 'We are showing sample data so you can explore the app. Your real data is unchanged.',
    'login.demo.title': 'Firma Ompli – try the demo',
    'login.demo.subtitle': 'Pick a role. Changes stay in this browser session only — nothing is saved to the database.',
    'login.demo.role.owner': 'Owner',
    'login.demo.role.manager': 'Manager',
    'login.demo.role.employee': 'Employee',
    'login.demo.blurb.owner': 'Runs the show — surname sounds like “boss”.',
    'login.demo.blurb.manager': 'Management and a good schnitzel — everything covered.',
    'login.demo.blurb.empBar': 'Bartender — always knows where the beer flows from.',
    'login.demo.blurb.empServe': 'Waitress — trays in the name and in her hands.',
    'login.demo.blurb.empWh': 'Warehouse — every pallet under control.',
    'login.demo.blurb.empHost': 'Hostess / promo — smile in the name and on her face.',
    'login.demo.success': 'Demo session started.',
    'login.demo.failed': 'Could not start the demo.',
    'demo.session.banner.title': 'Demo – Firma Ompli',
    'demo.session.banner.body': 'Edits apply only to this session. Logout clears everything.',
    'demo.session.switch': 'Role',
    'demo.session.reset': 'Reset data',
    'demo.session.switched': 'Switched demo role.',
    'demo.session.switchFailed': 'Could not switch role.',
    'demo.session.resetOk': 'Demo data regenerated.',
    'demo.session.resetFailed': 'Could not reset demo data.',
    'tour.replay.title': 'Product tour',
    'tour.replay.description': 'Replay the guided tour with sample data. When you finish, you return to your real workspace.',
    'tour.replay.cta': 'Replay tour',
    'tour.replay.started': 'Starting the product tour…',
    'tour.gate.title': 'Continue to the next page?',
    'tour.gate.body': 'Next up: {page}. You can keep going page by page, or explore at your own pace.',
    'tour.gate.continue': 'Continue to next page',
    'tour.gate.discover': 'Explore as I go',
    'tour.gate.last.title': 'Tour complete',
    'tour.gate.last.body': 'You have visited every section. Switch to your real workspace whenever you are ready.',
    'tour.next.progress': 'Next ({current} of {total})',
    'tour.ch.dashboard.filters.title': 'Filters',
    'tour.ch.dashboard.filters.body': 'Narrow the dashboard by period, status, or person.',
    'tour.ch.dashboard.stats.title': 'Key stats',
    'tour.ch.dashboard.stats.body': 'A quick snapshot of shifts, people, and fill rate.',
    'tour.ch.dashboard.activity.title': 'Recent activity',
    'tour.ch.dashboard.activity.body': 'Applications, leave requests, and other team events appear here.',
    'tour.ch.dashboard.upcoming.title': 'Upcoming shifts',
    'tour.ch.dashboard.upcoming.body': 'See what is scheduled next for your team.',
    'tour.ch.dashboard.credits.title': 'Credits',
    'tour.ch.dashboard.credits.body': 'Credits pay for filled shifts. Check your balance here or buy more in Settings → Billing.',
    'tour.ch.reports.cats.title': 'Report categories',
    'tour.ch.reports.cats.body': 'Pick a category to browse available report types.',
    'tour.ch.reports.seg.title': 'Report type',
    'tour.ch.reports.seg.body': 'Switch between the report formats in this category.',
    'tour.ch.reports.filters.title': 'Date range',
    'tour.ch.reports.filters.body': 'Set the period you want to analyse.',
    'tour.ch.reports.export.title': 'Export CSV',
    'tour.ch.reports.export.body': 'Download the current report as a spreadsheet.',
    'tour.ch.shifts.view.title': 'List or calendar',
    'tour.ch.shifts.view.body': 'Toggle between list and calendar views of your shifts.',
    'tour.ch.shifts.filters.title': 'Filters',
    'tour.ch.shifts.filters.body': 'Filter by workplace, position, or status.',
    'tour.ch.shifts.fill.title': 'Fill rate',
    'tour.ch.shifts.fill.body': 'See how well your shifts are staffed at a glance.',
    'tour.ch.shifts.presets.title': 'Presets',
    'tour.ch.shifts.presets.body': 'Save recurring shift templates to create shifts faster.',
    'tour.ch.shifts.create.title': 'Create shift',
    'tour.ch.shifts.create.body': 'Use this button when you want to schedule a new shift.',
    'tour.ch.shifts.create.panel.title': 'Create shift form',
    'tour.ch.shifts.create.panel.body': 'Fill in the details here. Nothing is saved during the tour.',
    'tour.ch.ann.compose.panel.title': 'Compose announcement',
    'tour.ch.ann.compose.panel.body': 'Write and target your message here. Nothing is sent during the tour.',
    'tour.ch.employees.edit.panel.title': 'Edit employee',
    'tour.ch.employees.edit.panel.body': 'Update details in this panel. Changes are not saved during the tour.',
    'tour.ch.contracts.send.panel.title': 'Send contract',
    'tour.ch.contracts.send.panel.body': 'Choose the employee and contract details here. Nothing is sent during the tour.',
    'tour.ch.workplaces.add.panel.title': 'Add workplace',
    'tour.ch.workplaces.add.panel.body': 'Enter the location details here. Nothing is saved during the tour.',
    'tour.ch.positions.add.panel.title': 'Add position',
    'tour.ch.positions.add.panel.body': 'Define the position here. Nothing is saved during the tour.',
    'tour.ch.jobs.create.panel.title': 'Post a job',
    'tour.ch.jobs.create.panel.body': 'Fill in the listing here. Nothing is published during the tour.',
    'tour.ch.shifts.list.title': 'Shift cards',
    'tour.ch.shifts.list.body': 'Open a shift to see assignments, pay, and details.',
    'tour.ch.attendance.tabs.title': 'Request types',
    'tour.ch.attendance.tabs.body': 'Switch between attendance, leave, and swap requests.',
    'tour.ch.attendance.list.title': 'Pending requests',
    'tour.ch.attendance.list.body': 'Review who is waiting for your decision.',
    'tour.ch.attendance.actions.title': 'Approve or deny',
    'tour.ch.attendance.actions.body': 'Use these actions to confirm or reject a request.',
    'tour.ch.vacation.kpi.title': 'Leave entitlement',
    'tour.ch.vacation.kpi.body': 'Track entitlement, used hours, and remaining leave.',
    'tour.ch.vacation.form.title': 'New leave request',
    'tour.ch.vacation.form.body': 'Submit dates and hours for vacation or leave.',
    'tour.ch.vacation.list.title': 'Leave requests',
    'tour.ch.vacation.list.body': 'See open and past leave requests, and approve them when you manage a team.',
    'tour.ch.ann.compose.title': 'Compose',
    'tour.ch.ann.compose.body': 'Use this button to write an announcement for your team.',
    'tour.ch.ann.tabs.title': 'Inbox and sent',
    'tour.ch.ann.tabs.body': 'Switch between received announcements and ones you sent.',
    'tour.ch.ann.list.title': 'Announcement list',
    'tour.ch.ann.list.body': 'Open an item to read the full message.',
    'tour.ch.messages.filters.title': 'Contact filters',
    'tour.ch.messages.filters.body': 'Filter by source (colleagues, applicants) and role.',
    'tour.ch.messages.contacts.title': 'Contacts',
    'tour.ch.messages.contacts.body': 'Pick a person to open their conversation.',
    'tour.ch.messages.thread.title': 'Conversation',
    'tour.ch.messages.thread.body': 'Read the thread and reply from the composer at the bottom.',
    'tour.ch.employees.invite.title': 'Invite employees',
    'tour.ch.employees.invite.body': 'Generate an invite code so new team members can join.',
    'tour.ch.employees.card.title': 'Employee card',
    'tour.ch.employees.card.body': 'See status, pay rate, and contact details at a glance.',
    'tour.ch.employees.edit.title': 'Edit employee',
    'tour.ch.employees.edit.body': 'Use Edit to update pay, status, or positions for this person.',
    'tour.ch.managers.invite.title': 'Invite managers',
    'tour.ch.managers.invite.body': 'Generate an invite so a manager can join your business.',
    'tour.ch.managers.card.title': 'Manager card',
    'tour.ch.managers.card.body': 'See assigned workplaces and open edit to adjust permissions.',
    'tour.ch.contracts.filters.title': 'Contract filters',
    'tour.ch.contracts.filters.body': 'Filter by active or inactive contracts.',
    'tour.ch.contracts.send.title': 'Send contract',
    'tour.ch.contracts.send.body': 'Use this button to issue a new contract.',
    'tour.ch.contracts.list.title': 'Contracts',
    'tour.ch.contracts.list.body': 'Track status, download files, and manage signatures.',
    'tour.ch.workplaces.add.title': 'Add location',
    'tour.ch.workplaces.add.body': 'Use this button to create a new workplace location.',
    'tour.ch.workplaces.grid.title': 'Workplaces',
    'tour.ch.workplaces.grid.body': 'Each card is a location where people can work shifts.',
    'tour.ch.positions.add.title': 'Add position',
    'tour.ch.positions.add.body': 'Use this button to define a new job position.',
    'tour.ch.positions.grid.title': 'Positions',
    'tour.ch.positions.grid.body': 'Browse and edit the positions for your business.',
    'tour.ch.jobs.create.title': 'Post a job',
    'tour.ch.jobs.create.body': 'Use this button to publish a new job listing.',
    'tour.ch.jobs.apps.title': 'Applications',
    'tour.ch.jobs.apps.body': 'Jump to the applications inbox to review candidates.',
    'tour.ch.jobs.view.title': 'List or grid',
    'tour.ch.jobs.view.body': 'Toggle how job listings are displayed.',
    'tour.ch.jobapps.grid.title': 'Applications',
    'tour.ch.jobapps.grid.body': 'Each card is a candidate who applied to one of your jobs.',
    'tour.ch.jobapps.actions.title': 'Review actions',
    'tour.ch.jobapps.actions.body': 'Message the applicant, approve, or deny the application.',
    'tour.ch.activity.view.title': 'List or calendar',
    'tour.ch.activity.view.body': 'View activity as a feed or on a calendar.',
    'tour.ch.activity.filters.title': 'Activity filters',
    'tour.ch.activity.filters.body': 'Filter by type - shifts, leave, applications, and more.',
    'tour.ch.activity.feed.title': 'Activity feed',
    'tour.ch.activity.feed.body': 'A timeline of what is happening across your business.',
    'tour.ch.settings.tabs.title': 'Settings tabs',
    'tour.ch.settings.tabs.body': 'General, Preferences, Personal, and for owners also Features and Billing.',
    'tour.ch.settings.prefs.title': 'Preferences',
    'tour.ch.settings.prefs.body': 'Language, notifications, and replay of this product tour.',
    'tour.ch.settings.billing.title': 'Billing & credits',
    'tour.ch.settings.billing.body': 'Check credits and manage your billing here.',
    'tour.ch.help.title': 'Help center',
    'tour.ch.help.body': 'Open documentation anytime from the sidebar.',
    'tour.ch.help.replay.title': 'Replay tour',
    'tour.ch.help.replay.body': 'You can restart this guided tour from Settings → Preferences.',
    'tour.ch.myjobs.list.title': 'My jobs',
    'tour.ch.myjobs.list.body': 'Businesses and roles you belong to, plus join-with-code.',
    'tour.ch.emp.dashboard.stats.body': 'Hours and earnings for the current period.',
    'tour.ch.emp.earnings.title': 'Earnings',
    'tour.ch.emp.earnings.body': 'Estimated earnings based on completed work.',
    'tour.ch.emp.shifts.view.body': 'Browse open shifts in list or calendar view.',
    'tour.ch.emp.shifts.list.title': 'Open shifts',
    'tour.ch.emp.shifts.list.body': 'Sign up for shifts that still need people.',
    'tour.ch.emp.ann.list.body': 'Announcements from your employers appear here.',
    'tour.ch.emp.myshifts.list.title': 'My shifts',
    'tour.ch.emp.myshifts.list.body': 'Shifts you are already assigned to.',
    'tour.ch.emp.myshifts.leave.title': 'Leave & attendance',
    'tour.ch.emp.myshifts.leave.body': 'Request to leave a shift or manage attendance from here.',
    'tour.ch.emp.vacation.form.body': 'Request vacation with dates and hours.',
    'tour.ch.emp.vacation.list.body': 'Track the status of your leave requests.',
    'tour.ch.emp.contracts.list.title': 'My contracts',
    'tour.ch.emp.contracts.list.body': 'View and sign employment contracts sent to you.',
    'tour.ch.emp.cv.title': 'CV manager',
    'tour.ch.emp.cv.body': 'Upload and update the CV employers see on applications.',
    'tour.ch.emp.jobs.view.body': 'Browse open job listings and apply.',
    'tour.owner.intro.nav.title': 'Your navigation',
    'tour.owner.intro.nav.body': 'Here you will find Shifts, Employees, Job Board, Messages, and more - everything you need to run your team.',
    'tour.owner.intro.upcoming.title': 'Upcoming shifts',
    'tour.owner.intro.upcoming.body': 'This is where upcoming shifts appear. Sample shifts are shown so you can see how a busy dashboard looks.',
    'tour.owner.intro.activity.title': 'Recent activity',
    'tour.owner.intro.activity.body': 'Applications, leave requests, and other team events show up here so you stay on top of what is happening.',
    'tour.owner.intro.announcements.title': 'Announcements',
    'tour.owner.intro.announcements.body': 'Important updates for your team land under Announcements. Open this anytime to review unread items.',
    'tour.owner.intro.credits.title': 'Credits',
    'tour.owner.intro.credits.body': 'Credits pay for filled shifts. You can check your balance here and buy more in Settings → Billing.',
    'tour.owner.intro.create.title': 'Create a shift',
    'tour.owner.intro.create.body': 'Use this button to schedule a new shift and invite workers to fill it.',
    'tour.owner.intro.help.title': 'Help & docs',
    'tour.owner.intro.help.body': 'Open the help center anytime. You can also replay this tour later from Settings → Preferences.',
    'tour.employee.intro.nav.title': 'Your navigation',
    'tour.employee.intro.nav.body': 'Use the sidebar for My Shifts, Messages, Job Board, and your profile settings.',
    'tour.employee.intro.myshifts.title': 'My shifts',
    'tour.employee.intro.myshifts.body': 'Upcoming shifts you are signed up for appear here, including workplace location details.',
    'tour.employee.intro.attendance.title': 'Leave & attendance',
    'tour.employee.intro.attendance.body': 'From a shift card you can request to leave or manage attendance. Geo check-in is handled on mobile when available.',
    'tour.employee.intro.earnings.title': 'Hours & earnings',
    'tour.employee.intro.earnings.body': 'Your dashboard shows hours worked and earnings for the month once you complete shifts.',
    'tour.employee.intro.announcements.title': 'Announcements',
    'tour.employee.intro.announcements.body': 'Employer announcements and important alerts appear here.',
    'tour.mini.shifts.fill.title': 'Fill rate',
    'tour.mini.shifts.fill.body': 'See how well your shifts are staffed at a glance.',
    'tour.mini.shifts.create.title': 'New shift',
    'tour.mini.shifts.create.body': 'Create shifts for your workplaces and track who signs up.',
    'tour.mini.employees.invite.title': 'Invite employees',
    'tour.mini.employees.invite.body': 'Generate an invite code and share it so new team members can join your business.',
    'tour.mini.employees.card.title': 'Employee card',
    'tour.mini.employees.card.body': 'Each card shows status, pay rate, and quick actions to edit or remove a team member.',
    'tour.mini.jobs.create.title': 'Post a job',
    'tour.mini.jobs.create.body': 'Publish listings on the Job Board to attract new workers.',
    'tour.mini.jobs.apps.title': 'Applications',
    'tour.mini.jobs.apps.body': 'Review who applied and continue the conversation in Job Applications.',
    'tour.mini.credits.buy.title': 'Buy credits',
    'tour.mini.credits.buy.body': 'Purchase credits here to keep scheduling and filling shifts.',
    'tour.mini.credits.notify.title': 'Preferences',
    'tour.mini.credits.notify.body': 'Adjust language, default views, and other personal preferences. Replay the product tour from this tab anytime.',
    'notfound.code': '404',
    'notfound.title': 'Nothing here yet',
    'notfound.description': 'This page doesn’t exist, or it’s still under construction.',
    'notfound.cta': 'Back to home',
    'cookies.title': 'Cookies',
    'cookies.body':
      'We use cookies to keep you signed in, remember preferences, and improve your experience. By clicking Accept, you agree to our',
    'cookies.policy': 'Cookie Policy',
    'cookies.accept': 'Accept',
    'cookies.preferences': 'Preferences',
    'cookies.close': 'Close',
    'cookies.prefs.intro': 'Choose which cookies we may use. Necessary cookies are always on so the app works.',
    'cookies.prefs.necessary': 'Necessary',
    'cookies.prefs.necessary.desc': 'Session login, security, and basic preferences stored in your browser.',
    'cookies.prefs.optional': 'Optional',
    'cookies.prefs.optional.desc': 'Live chat and similar helpers that improve support on marketing pages.',
    'cookies.prefs.save': 'Save preferences',
    'cookies.prefs.back': 'Back',
  },
  cs: {
    'settings.currency.selection': 'Výběr měny',
    'settings.currency.description': 'Vyberte si preferovanou měnu pro nákup kreditů a fakturaci.',
    'settings.currency.czk': 'CZK (Česká koruna)',
    'settings.currency.eur': 'EUR (Euro)',
    'settings.company.logo': 'Logo firmy',
    'settings.company.logo.description': 'Nahrajte logo firmy. Zobrazí se na nabídce práce a v detailech inzerátů.',
    'settings.company.logo.upload': 'Nahrát logo',
    'settings.company.logo.hint': 'JPG, PNG nebo GIF · max 5 MB',
    'settings.company.logo.updated': 'Logo firmy bylo aktualizováno.',
    'settings.company.logo.failed': 'Nepodařilo se nahrát logo firmy.',
    'settings.company.logo.owner.only': 'Logo firmy může měnit pouze majitel.',
    'settings.business.info': 'Informace o firmě',
    'settings.business.info.description': 'Zobrazte a upravte všechny údaje o firmě uložené v systému.',
    'settings.business.name': 'Název firmy',
    'settings.business.name.placeholder': 'Zadejte název firmy',
    'settings.business.name.required': 'Název firmy je povinný.',
    'settings.business.updated': 'Informace o firmě byly uloženy.',
    'settings.business.update.failed': 'Nepodařilo se uložit informace o firmě.',
    'settings.business.id': 'ID firmy',
    'settings.business.created': 'Vytvořeno',
    'settings.business.owner': 'Vlastník',
    'settings.business.section.basic': 'Základní údaje',
    'settings.business.section.contact': 'Kontakt',
    'settings.business.section.legal': 'Identifikátory',
    'settings.business.section.address': 'Sídlo / adresa',
    'settings.business.description': 'Popis',
    'settings.business.description.placeholder': 'Krátký popis firmy',
    'settings.business.email': 'E-mail firmy',
    'settings.business.phone': 'Telefon firmy',
    'settings.business.website': 'Web',
    'settings.business.companyName': 'Název společnosti',
    'settings.business.companyName.placeholder': 'Začněte psát název společnosti…',
    'settings.business.ico': 'IČO',
    'settings.business.dic': 'DIČ',
    'settings.business.ares.hint': 'Nápověda z ARES. Při uložení s vyplněným IČO se všechna tři pole ověří a adresa se doplní z registru.',
    'settings.business.ares.company.required': 'Při vyplněném IČO je název společnosti povinný.',
    'settings.business.ares.mismatch': 'Identifikátory firmy nesouhlasí s ARES.',
    'profile.information': 'Informace o profilu',
    'first.name': 'Jméno',
    'last.name': 'Příjmení',
    'email.address': 'E-mailová adresa',
    'username': 'Uživatelské jméno',
    'role': 'Role',
    'update.profile': 'Aktualizovat profil',
    'change.password': 'Změnit heslo',
    'current.password': 'Současné heslo',
    'new.password': 'Nové heslo',
    'password.min.length': 'Musí mít alespoň 6 znaků',
    'confirm.password': 'Potvrdit nové heslo',
    'contracts': 'Smlouvy',
    'enable.contracts': 'Povolit smlouvy',
    'contract.cycle.day': 'Den cyklu smlouvy',
    'default.contract.duration': 'Výchozí doba trvání smlouvy',
    'monthly': 'Měsíčně',
    'yearly': 'Ročně',
    'unlimited': 'Neomezeně',
    'custom': 'Vlastní',
    'save': 'Uložit',
    'saving': 'Ukládání...',
    'settings.saved': 'Nastavení uloženo!',
    'failed.save.settings': 'Nepodařilo se uložit nastavení',
    'settings.title': 'Nastavení',
    'settings.subtitle': 'Spravujte nastavení účtu a preference',
    'settings.tab.general': 'Obecné',
    'settings.tab.preferences': 'Preference',
    'settings.tab.personal': 'Osobní',
    'settings.tab.features': 'Funkce',
    'settings.tab.billing': 'Fakturace',
    'settings.preferences.shifts.view': 'Výchozí zobrazení směn',
    'settings.preferences.shifts.view.description':
      'Vaše osobní volba - jestli se stránka Směny otevře jako seznam, nebo kalendář. Neovlivní ostatní uživatele ve firmě.',
    'settings.preferences.employee.rate': 'Použít hodinovou sazbu zaměstnance ve výchozím stavu',
    'settings.preferences.employee.rate.description':
      'Nastavení firmy pro majitele a manažery. Při vytváření směny bude předem zaškrtnuté „Použít hodinovou sazbu zaměstnance, pokud je vyplněná“. U konkrétní směny to pořád můžete změnit. Každá firma má vlastní hodnotu.',
    'settings.preferences.saved': 'Preference uložena.',
    'settings.preferences.save.failed': 'Preference se nepodařilo uložit.',
    'settings.calendar.title': 'Synchronizace kalendáře',
    'settings.calendar.description':
      'Napojte Google, Microsoft, Apple nebo ICS. Konflikty na Směnách berou všechny. Schválené směny se zapisují do Google a Microsoft (ty znovu připojte, pokud jste je napojili dřív). Apple/ICS jsou jen ke čtení.',
    'settings.calendar.status.connected': 'Kalendář připojen',
    'settings.calendar.status.not.connected': 'Nepřipojeno',
    'settings.calendar.multi.hint': '{count} kalendáře synchronizovány - události se na Směnách slučují.',
    'settings.calendar.disconnect': 'Odpojit',
    'settings.calendar.disconnected': 'Kalendář odpojen.',
    'settings.calendar.disconnect.failed': 'Kalendář se nepodařilo odpojit.',
    'settings.calendar.connected': 'Kalendář úspěšně připojen.',
    'settings.calendar.connect.failed': 'Kalendář se nepodařilo připojit.',
    'settings.calendar.connect': 'Připojit',
    'settings.calendar.provider.ics': 'ICS',
    'settings.calendar.oauth.not.configured':
      'Google/Microsoft OAuth není na serveru nastavené. Můžete se ale připojit přes Apple nebo ICS odkaz.',
    'settings.calendar.oauth.setup.hint':
      'Doplňte do .env GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET a MICROSOFT_CLIENT_ID / MICROSOFT_CLIENT_SECRET (viz docs/calendar-oauth-setup.md) a restartujte backend.',
    'settings.calendar.apple.title': 'Připojit Apple Kalendář',
    'settings.calendar.apple.help':
      'V Apple Kalendáři: Kalendář → Sdílet kalendář / Veřejný kalendář → zkopírujte odkaz pro odběr (webcal:// nebo https://) a vložte ho sem.',
    'settings.calendar.ics.title': 'Připojit ICS feed',
    'settings.calendar.ics.help':
      'Vložte libovolnou soukromou nebo veřejnou ICS adresu (tajná adresa Google, Outlook ICS, jiné kalendářové aplikace).',
    'settings.ai.tokens.title': 'Přístup pro AI asistenty',
    'settings.ai.tokens.description':
      'Vytvořte osobní read-only token pro Cursor, Claude, ChatGPT nebo jiného asistenta s podporou MCP.',
    'settings.ai.tokens.server.url': 'URL vzdáleného MCP serveru',
    'settings.ai.tokens.server.url.claude': 'URL MCP serveru (vložte do Claude / Cursor)',
    'settings.ai.tokens.claude.https':
      'Claude vyžaduje HTTPS a OAuth souhlas. Lokálně spusťte make mcp-tunnel, vložte https://…/mcp, OAuth Client ID nechte prázdné a token ompli_pat_… vložte na stránce Ompli.',
    'settings.ai.tokens.tunnel.missing.title': 'HTTPS tunnel neběží',
    'settings.ai.tokens.tunnel.missing.body':
      'Claude neumí http://localhost. V terminálu spusťte make mcp-tunnel, počkejte na adresu trycloudflare.com a tento formulář znovu otevřete — Ompli pak ukáže tunnelovou /mcp adresu automaticky.',
    'settings.ai.tokens.help.how.title': 'Jak to funguje',
    'settings.ai.tokens.help.how.body':
      'Token je osobní a jen pro čtení. Asistent vidí jen data, která už stejně vidíte v Ompli, se stejnými oprávněními role. Nic nevytvoří, neupraví, neschválí ani nesmaže.',
    'settings.ai.tokens.help.steps.title': 'Co dělat',
    'settings.ai.tokens.help.steps.body':
      '1) Spusťte make mcp-tunnel. 2) Vytvořte token — ve formuláři zkopírujte HTTPS MCP URL. 3) Přidejte ji v Claude (custom connector; Client ID prázdné) nebo Cursoru. 4) Token vložte při OAuth souhlasu / do configu.',
    'settings.ai.tokens.one.time.hint':
      'Každý token je osobní a jen pro čtení. Tajná hodnota se po vytvoření zobrazí pouze jednou.',
    'settings.ai.tokens.one.time.warning':
      'Token si uložte teď. Z bezpečnostních důvodů ho Ompli po zavření tohoto okna znovu neukáže.',
    'settings.ai.tokens.list.title': 'Osobní přístupové tokeny',
    'settings.ai.tokens.list.description':
      'Jakýkoliv token můžete kdykoliv zneplatnit, pokud už danému asistentovi nebo zařízení nevěříte.',
    'settings.ai.tokens.load.failed': 'Nepodařilo se načíst AI tokeny.',
    'settings.ai.tokens.empty': 'Zatím nemáte vytvořený žádný AI token.',
    'settings.ai.tokens.create': 'Vytvořit token',
    'settings.ai.tokens.created': 'AI token byl vytvořen.',
    'settings.ai.tokens.create.failed': 'Nepodařilo se vytvořit AI token.',
    'settings.ai.tokens.created.title': 'Zkopírujte svůj nový token',
    'settings.ai.tokens.created.description':
      'Vložte tento token do MCP klienta spolu s adresou serveru uvedenou níže.',
    'settings.ai.tokens.created.token.label': 'Váš osobní token',
    'settings.ai.tokens.modal.title': 'Vytvořit AI token',
    'settings.ai.tokens.modal.description':
      'Token bude používat stejná oprávnění jako váš účet v Ompli a umí pouze číst data.',
    'settings.ai.tokens.name': 'Název tokenu',
    'settings.ai.tokens.name.placeholder': 'Např. Cursor na MacBooku',
    'settings.ai.tokens.name.required': 'Název tokenu je povinný.',
    'settings.ai.tokens.business': 'Firma',
    'settings.ai.tokens.business.placeholder': 'Vyberte firmu',
    'settings.ai.tokens.business.required': 'Vyberte, pro kterou firmu má token fungovat.',
    'settings.ai.tokens.copy': 'Kopírovat token',
    'settings.ai.tokens.copy.url': 'Kopírovat URL',
    'settings.ai.tokens.copied': 'Token byl zkopírován.',
    'settings.ai.tokens.copy.failed': 'Token se nepodařilo zkopírovat.',
    'settings.ai.tokens.url.copied': 'MCP URL byla zkopírována.',
    'settings.ai.tokens.url.copy.failed': 'MCP URL se nepodařilo zkopírovat.',
    'settings.ai.tokens.expires': 'Platnost do',
    'settings.ai.tokens.last.used': 'Naposledy použit',
    'settings.ai.tokens.never.used': 'Ještě nikdy',
    'settings.ai.tokens.revoke': 'Zneplatnit',
    'settings.ai.tokens.revoked': 'AI token byl zneplatněn.',
    'settings.ai.tokens.revoke.failed': 'Nepodařilo se zneplatnit AI token.',
    'settings.ai.tokens.revoke.confirm': 'Opravdu chcete tento AI token okamžitě zneplatnit?',
    'shifts.calendar.conflict': 'Konflikt',
    'shifts.calendar.free': 'Volno',
    'shifts.calendar.busy': 'Zaneprázdněn/a',
    'shifts.calendar.conflict.with': 'Konflikt: {title}',
    'shifts.calendar.personal': 'Osobní kalendář',
    'shifts.calendar.personal.allday': 'Celý den',
    'shifts.calendar.synced': 'Osobní kalendář synchronizován · {count} událostí tento měsíc',
    'shifts.calendar.personal.hide': 'Skrýt události kalendáře',
    'shifts.calendar.personal.show': 'Zobrazit události kalendáře',
    'shifts.calendar.personal.hidden': 'Události osobního kalendáře jsou skryté',
    'shifts.leave.request.submitted': 'Žádost o odhlášení byla odeslána.',
    'shifts.leave.left': 'Opustili jste směnu.',
    'recent.activity.leave_request': 'Žádost o odhlášení',
    'settings.profile.description': 'Aktualizujte své osobní údaje',
    'settings.password.description': 'Změňte heslo k vašemu účtu',
    'settings.owner.only': 'Toto nastavení může spravovat pouze vlastník firmy.',
    'settings.managers.jobs.title': 'Přístup manažerů k nabídkám práce',
    'settings.managers.jobs.tooltip': 'Určuje, zda mohou manažeři vystavovat nabídky práce a přijímat přihlášky. Pokud je vypnuto, neuvidí v menu Nabídku práce ani Přihlášky na práci.',
    'settings.managers.jobs.enable': 'Povolit manažerům vystavovat a přijímat nabídky práce',
    'settings.managers.announcements.title': 'Oznámení od manažerů',
    'settings.managers.announcements.tooltip': 'Pokud je zapnuto, manažeři mohou psát a posílat oznámení lidem ve firmě.',
    'settings.managers.announcements.enable': 'Povolit manažerům posílat oznámení',
    'announcements': 'Oznámení',
    'announcements.subtitle': 'Firemní a platformní zprávy pro vás',
    'announcements.tab.inbox': 'Doručené',
    'announcements.tab.compose': 'Napsat',
    'announcements.tab.sent': 'Odeslané',
    'announcements.compose.title': 'Nové oznámení',
    'announcements.detail.title': 'Oznámení',
    'announcements.search.placeholder': 'Hledat oznámení…',
    'announcements.search.empty': 'Žádná oznámení neodpovídají hledání',
    'announcements.empty.title': 'Zatím žádná oznámení',
    'announcements.empty.description': 'Až vám někdo pošle oznámení, uvidíte ho tady.',
    'announcements.sent.empty.title': 'Žádná odeslaná oznámení',
    'announcements.sent.empty.description': 'Oznámení, která odešlete, se zobrazí zde.',
    'announcements.field.title': 'Název',
    'announcements.field.summary': 'Krátký popisek',
    'announcements.field.body': 'Celé sdělení',
    'announcements.field.body.placeholder': 'Volitelný rich text po rozkliknutí…',
    'announcements.field.channels': 'Kam doručit',
    'announcements.field.audience': 'Příjemci',
    'announcements.channel.both': 'Aplikace i web',
    'announcements.channel.app': 'Jen aplikace',
    'announcements.channel.web': 'Jen web',
    'announcements.audience.all': 'Všichni ve firmě',
    'announcements.audience.roles': 'Podle role',
    'announcements.audience.users': 'Konkrétní lidé',
    'announcements.audience.users.empty': 'Nikdo není k dispozici',
    'announcements.audience.users.search': 'Hledat uživatele…',
    'announcements.audience.platform.all': 'Všichni uživatelé Ompli',
    'announcements.audience.businesses': 'Vybrané firmy (+ role)',
    'announcements.audience.roles.hint': 'Vybrané role napříč všemi firmami (nebo nechte firmy prázdné).',
    'announcements.role.employee': 'Zaměstnanci',
    'announcements.role.manager': 'Manažeři',
    'announcements.role.owner': 'Majitelé',
    'announcements.send': 'Odeslat oznámení',
    'announcements.sending': 'Odesílám…',
    'announcements.send.success': 'Oznámení odesláno.',
    'announcements.send.error': 'Oznámení se nepodařilo odeslat.',
    'announcements.load.error': 'Nepodařilo se načíst oznámení.',
    'announcements.no.body': 'Bez dalšího sdělení.',
    'announcements.from': 'Od {name}',
    'announcements.from.ompli': 'Tým Ompli',
    'announcements.from.system': 'Systém',
    'announcements.from.unknown': 'Neznámý',
    'announcements.feed.sent': 'odeslal(a)',
    'announcements.feed.you.sent': 'Odeslali jste',
    'announcements.feed.system.sent': 'Systém odeslal',
    'announcements.feed.auto': 'Automaticky',
    'announcements.feed.to.recipients': '{count} příjemcům',
    'announcements.recipients': '{count} příjemců',
    'announcements.delete.confirm': 'Smazat toto oznámení pro všechny?',
    'announcements.delete.success': 'Oznámení smazáno.',
    'announcements.delete.error': 'Oznámení se nepodařilo smazat.',
    'admin.announcements.title': 'Oznámení',
    'admin.announcements.subtitle':
      'Posílejte platformní zprávy všem, vybraným firmám, rolím nebo konkrétním lidem',
    'admin.announcements.sent.empty.description':
      'Platformní oznámení, která odešlete, i automatické systémové notifikace se zobrazí zde.',
    'admin.announcements.compose': 'Nové oznámení',
    'admin.announcements.auto.detail.title': 'Automatická notifikace',
    'admin.announcements.auto.changes': 'Co se změnilo',
    'admin.announcements.auto.shift': 'Související směna',
    'admin.announcements.auto.shift.missing': 'Směna #{id} už není dostupná',
    'admin.announcements.auto.action.assigned': 'Přiřazení ke směně',
    'admin.announcements.auto.action.updated': 'Změna směny',
    'admin.announcements.auto.action.removed': 'Odstranění ze směny',
    'admin.announcements.auto.action.denied': 'Zamítnutí žádosti o směnu',
    'admin.announcements.auto.action.cancelled': 'Zrušení směny',
    'admin.announcements.auto.action.unknown': 'Systémová notifikace',
    'admin.announcements.auto.change.title': 'Změna názvu',
    'admin.announcements.auto.change.description': 'Změna popisu',
    'admin.announcements.auto.change.location': 'Změna pracoviště',
    'admin.announcements.auto.change.position': 'Změna pozice',
    'admin.announcements.auto.change.time': 'Změna času',
    'admin.announcements.auto.change.pay.increased': 'Odměna navýšena',
    'admin.announcements.auto.change.pay.decreased': 'Odměna snížena',
    'admin.announcements.auto.change.pay': 'Změna odměny',
    'admin.announcements.auto.change.employee.rate.on': 'Zapnuta hodinová sazba zaměstnance',
    'admin.announcements.auto.change.employee.rate.off': 'Vypnuta hodinová sazba zaměstnance',
    'admin.announcements.auto.change.capacity.increased': 'Kapacita navýšena',
    'admin.announcements.auto.change.capacity.decreased': 'Kapacita snížena',
    'admin.announcements.auto.change.approval': 'Změna schvalování',
    'admin.announcements.auto.change.leave': 'Změna odhlašování',
    'admin.nav.announcements': 'Oznámení',
    'admin.flag.managers.announcements': 'Manažeři mohou posílat oznámení',
    'settings.managers.workplaces.title': 'Přístup manažerů k pracovištím',
    'settings.managers.workplaces.tooltip': 'Vyberte, co mohou manažeři u pracovišť dělat. Mazání zůstává jen majiteli.',
    'settings.managers.workplaces.edit.assigned': 'Upravovat pracoviště, ke kterým jsou přiřazeni',
    'settings.managers.workplaces.edit.all': 'Upravovat všechna pracoviště',
    'settings.managers.workplaces.create': 'Vytvářet nová pracoviště',
    'settings.managers.workplaces.scope': 'Komu tato oprávnění platí',
    'settings.managers.workplaces.scope.all': 'Všem manažerům',
    'settings.managers.workplaces.scope.selected': 'Jen vybraným manažerům',
    'settings.managers.workplaces.scope.pick': 'Vyberte manažery',
    'settings.managers.workplaces.scope.empty': 'V této firmě zatím nejsou žádní manažeři.',
    'settings.credits.description': 'Spravujte zůstatek kreditů a zobrazte využití',
    'profile.upload.photo': 'Nahrát fotku',
    'profile.photo.hint': 'JPG, PNG nebo GIF · max 5 MB',
    'profile.photo.updated': 'Profilová fotka aktualizována.',
    'profile.photo.failed': 'Nahrání fotky se nezdařilo.',
    'profile.address.placeholder': 'Ulice, město, PSČ',
    'address.country': 'Stát',
    'address.region': 'Kraj',
    'address.city': 'Město',
    'address.street': 'Ulice',
    'address.zip': 'PSČ',
    'join.invited': 'Byli jste pozváni!',
    'join.invited.to': 'Byli jste pozváni do',
    'join.login.and.join': 'Přihlásit se a připojit',
    'join.create.account': 'Vytvořit účet a připojit se',
    'join.invalid.code': 'Neplatný odkaz pozvánky',
    'join.go.to.login': 'Přejít na přihlášení',
    'join.joining': 'Připojování...',
    'join.loading': 'Kontrola pozvánky...',
    'join.success': 'Úspěšně přidáno!',
    'join.success.sub': 'Úspěšně jste se připojili do',
    'join.redirecting': 'Přesměrování na dashboard...',
    'join.registering.via.invite': 'Registrace přes odkaz pozvánky',
    'language.selection': 'Výběr jazyka',
    'language.description': 'Vaše osobní volba - jazyk se změní jen pro váš účet, ne pro ostatní uživatele.',
    'language.czech': 'Čeština',
    'language.english': 'Angličtina',
    'language.saved': 'Preferovaný jazyk uložen.',
    'language.save.failed': 'Preferovaný jazyk se nepodařilo uložit.',
    'settings.credits.management': 'Správa kreditů',
    'credits.balance': 'Zůstatek kreditů',
    'credits.available': 'Dostupné',
    'credits.employees': 'Zaměstnanci',
    'credits.managers': 'Manažeři',
    'credits.total.users': 'Celkem uživatelů',
    'credits.daily.cost': 'Denní náklady',
    'credits.days.remaining': 'Zbývající dny',
    'credits.consider.purchasing': 'Zvažte nákup více kreditů',
    'credits.days.remaining.message': 'dní zbývá. Zvažte nákup více kreditů pro pokračování v používání služby.',
    'credits.purchase.credits': 'Nakoupit kredity',
    'credits.no.discount': 'Bez slevy',
    'credits.savings': 'úspora',
    'credits.custom.amount': 'Vlastní částka',
    'credits.purchase.button': 'Nakoupit',
    'credits.credits': 'kreditů',
    'credits.for': 'za',
    'credits.deducted.daily': 'Kredity se odečítají denně podle aktivních uživatelů',
    'credits.free.tier.info': 'Bezplatná úroveň dostupná pro firmy s 5 nebo méně uživateli',
    'credits.recent.transactions': 'Poslední transakce',
    'credits.purchase.type': 'Nákup kreditů',
    'credits.daily.deduction.type': 'Denní odpočet',
    'credits.refund.type': 'Vrácení kreditů',
    'credits.purchase.description': 'Nákup kreditů: {amount} kreditů za {price}',
    'credits.daily.deduction.description': 'Denní odpočet: {amount} kreditů',
    'credits.refund.description': 'Vrácení kreditů: {amount} kreditů',
    'updating': 'Aktualizace...',
    'profile.updated': 'Profil byl úspěšně aktualizován!',
    'failed.update.profile': 'Nepodařilo se aktualizovat profil',
    'network.error': 'Došlo k síťové chybě',
    'passwords.not.match': 'Hesla se neshodují',
    'password.too.short': 'Heslo musí mít alespoň 6 znaků',
    'password.updated': 'Heslo bylo úspěšně změněno!',
    'failed.update.password': 'Nepodařilo se změnit heslo',
    'contracts.tooltip': 'Povolit funkce správy smluv pro vaši firmu',
    'dashboard': 'Dashboard',
    'reports': 'Reporty',
    'shifts': 'Směny',
    'attendance.requests': 'Žádosti o docházku',
    'employees': 'Zaměstnanci',
    'messages': 'Zprávy',
    'reports.title': 'Reporty',
    'reports.subtitle': 'Přehledy firmy, hodiny, obsazenost a mzdové exporty',
    'reports.cat.hours': 'Odpracovaný čas',
    'reports.cat.payroll': 'Mzdové podklady',
    'reports.cat.shifts': 'Směny a obsazenost',
    'reports.cat.leave': 'Nepřítomnosti',
    'reports.cat.contracts': 'Smlouvy a compliance',
    'reports.hours.earnings': 'Hodiny a výdělky',
    'reports.occupancy': 'Obsazenost směn',
    'reports.leave': 'Žádosti o odhlášení',
    'reports.contracts': 'Smlouvy a limity',
    'reports.attendance.sheets': 'Docházkové listy',
    'reports.export.csv': 'Export CSV',
    'reports.export.done': 'CSV exportováno.',
    'reports.export.empty': 'Není co exportovat.',
    'reports.load.error': 'Nepodařilo se načíst report.',
    'reports.empty.title': 'Žádná data za období',
    'reports.empty.desc': 'Zkuste jiný rozsah dat nebo filtry.',
    'reports.kpi.people': 'Lidé',
    'reports.kpi.shifts': 'Směny',
    'reports.kpi.hours': 'Hodiny',
    'reports.kpi.earnings': 'Výdělky',
    'reports.kpi.rows': 'Řádky',
    'reports.kpi.fill.rate': 'Obsazenost',
    'reports.kpi.open.slots': 'Volná místa',
    'reports.kpi.understaffed': 'Neobsazené',
    'reports.kpi.total': 'Celkem',
    'reports.kpi.pending': 'Čekající',
    'reports.kpi.approved': 'Schválené',
    'reports.kpi.rejected': 'Zamítnuté',
    'reports.kpi.signed': 'Podepsané',
    'reports.kpi.near.limit': 'Blízko limitu',
    'reports.kpi.cssz.missing': 'Chybí ČSSZ',
    'reports.col.employee': 'Zaměstnanec',
    'reports.col.avg.rate': 'Prům. sazba',
    'reports.col.locations': 'Pracoviště',
    'reports.col.date': 'Datum',
    'reports.col.shift': 'Směna',
    'reports.col.when': 'Kdy',
    'reports.col.filled': 'Obsazeno',
    'reports.col.status': 'Stav',
    'reports.col.requested': 'Požádáno',
    'reports.col.type': 'Typ',
    'reports.col.compliance': 'Compliance',
    'reports.unit.people': 'osoby',
    'reports.cat.workplaces': 'Pracoviště',
    'reports.cat.people': 'Lidé',
    'reports.cat.positions': 'Pozice',
    'reports.cat.jobs': 'Nabídka práce',
    'reports.workplaces': 'Přehled pracovišť',
    'reports.employees': 'Zaměstnanci',
    'reports.managers': 'Manažeři',
    'reports.shifts': 'Detail směn',
    'reports.positions': 'Pozice',
    'reports.jobs': 'Nabídky práce',
    'reports.kpi.to.pay': 'K výplatě',
    'reports.kpi.workplaces': 'Pracoviště',
    'reports.kpi.visits': 'Návštěvy',
    'reports.kpi.active': 'Aktivní',
    'reports.kpi.inactive': 'Neaktivní',
    'reports.kpi.managers': 'Manažeři',
    'reports.kpi.positions': 'Pozice',
    'reports.kpi.listings': 'Inzeráty',
    'reports.kpi.applications': 'Přihlášky',
    'reports.kpi.accepted': 'Přijaté',
    'reports.kpi.workers': 'Pracovníci',
    'reports.col.manager': 'Manažer',
    'reports.col.workers': 'Pracovníci',
    'reports.col.job': 'Nabídka',
    'reports.col.created': 'Vytvořeno',
    'reports.workplaces.people.detail': 'Kdo kde pracoval',
    'reports.workplaces.people.detail.desc': 'Návštěvy, hodiny a částka k výplatě u každého zaměstnance na pracovišti',
    'workplaces': 'Pracoviště',
    'job.board': 'Nabídka práce',
    'job.applications': 'Přihlášky na práci',
    'positions': 'Pozice',
    'recent.activity': 'Poslední aktivita',
    'managers': 'Manažeři',
    'settings': 'Nastavení',
    'logout': 'Odhlásit se',
    'my.shifts': 'Moje směny',
    'create.shift.compliance.blocked': 'Směna porušuje pravidla zákoníku práce. Opravte zvýrazněné problémy před uložením.',
    'create.shift.compliance.structure.title': 'Směnu nelze uložit (pravidla pracoviště)',
    'create.shift.compliance.assignees.title': 'Tyto osoby nelze přiřadit',
    'create.shift.compliance.checking': 'kontroluji…',
    'compliance.blocker.SHIFT_MAX_12H': 'Pracovní doba směny překračuje maximum 12 hodin.',
    'compliance.blocker.BREAK_REQUIRED': 'U směny delší než 6 h musí být přestávka min. 30 min nejpozději po 6. hodině (nastavení pauzy na pobočce).',
    'compliance.blocker.DAILY_REST_11H': 'Mezi směnami této osoby je méně než 11 hodin odpočinku.',
    'compliance.blocker.WEEKLY_REST_24H': 'V 7denním období kolem této směny chybí souvislých 24 hodin volna.',
    'compliance.blocker.DPP_YEARLY_CAP': 'Byl by překročen roční limit DPP (300 h).',
    'compliance.blocker.DPC_WEEKLY_AVG': 'Průměrná týdenní doba DPČ by překročila 20 h.',
    'compliance.blocker.BOZP_REQUIRED': 'Před účastí na směně je potřeba potvrdit BOZP.',
    'vacation': 'Dovolená',
    'vacation.subtitle': 'Zákonný nárok na dovolenou (zákoník práce). Oddělené od odhlášení ze směny.',
    'vacation.entitlement': 'Nárok',
    'vacation.used': 'Vyčerpáno / čeká',
    'vacation.remaining': 'Zůstatek',
    'vacation.worked': 'Odpracováno letos',
    'vacation.not.eligible': 'Zatím nemáte nárok na dovolenou v tomto roce.',
    'vacation.new.request': 'Nová žádost o dovolenou',
    'vacation.from': 'Od',
    'vacation.to': 'Do',
    'vacation.hours': 'Hodiny',
    'vacation.reason': 'Důvod (volitelné)',
    'vacation.submit': 'Odeslat žádost',
    'vacation.requests': 'Žádosti',
    'vacation.empty': 'Žádné žádosti o dovolenou',
    'vacation.empty.desc': 'Schválené a čekající žádosti se zobrazí zde.',
    'vacation.load.failed': 'Nepodařilo se načíst dovolenou.',
    'vacation.request.failed': 'Nepodařilo se vytvořit žádost o dovolenou.',
    'vacation.request.created': 'Žádost o dovolenou odeslána.',
    'vacation.update.failed': 'Nepodařilo se aktualizovat žádost.',
    'vacation.approved': 'Dovolená schválena.',
    'vacation.rejected': 'Dovolená zamítnuta.',
    'vacation.cancelled': 'Dovolená zrušena.',
    'vacation.status.approved': 'Schváleno',
    'vacation.status.rejected': 'Zamítnuto',
    'vacation.status.cancelled': 'Zrušeno',
    'vacation.status.pending': 'Čeká',
    'vacation.no.business': 'Žádná firma',
    'vacation.no.business.desc': 'Pro správu dovolené se připojte k firmě nebo ji vytvořte.',
    'vacation.cz.only': 'Evidence dovolené je pro pracoviště v ČR',
    'vacation.cz.only.desc': 'Zákonný nárok na dovolenou platí, když je země pracoviště Česká republika.',
    'vacation.annual.weeks': 'Výměra dovolené (týdny)',
    'vacation.annual.weeks.hint': 'Zákonné minimum jsou 4 týdny. Používá se pro výpočet u HPP i DPP/DPČ.',
    'my.contracts': 'Moje smlouvy',
    'my.jobs': 'Moje práce',
    'my.jobs.subtitle': 'Firmy, ke kterým patříte, a kódy pro připojení dalších.',
    'my.jobs.join.title': 'Připojit se kódem',
    'my.jobs.join.hint': 'Požádejte zaměstnavatele o kód a vložte ho sem.',
    'my.jobs.join.placeholder': 'Zadejte pozvánkový kód',
    'my.jobs.join': 'Připojit',
    'my.jobs.your': 'Vaše práce',
    'my.jobs.count': 'prací',
    'my.jobs.empty': 'Zatím žádná práce',
    'my.jobs.empty.desc': 'Zadejte kód výše a připojte se k první firmě.',
    'my.jobs.empty.desc.owner': 'Zatím nemáte žádné firmy.',
    'my.jobs.leave': 'Odejít',
    'my.jobs.leaving': 'Odcházím…',
    'my.jobs.leave.confirm': 'Odejít z {name}? Ztratíte přístup ke směnám a datům.',
    'my.jobs.joined': 'Úspěšně připojeno.',
    'my.jobs.left': 'Opustili jste práci.',
    'my.jobs.error.load': 'Nepodařilo se načíst práce.',
    'my.jobs.error.join': 'Nepodařilo se připojit.',
    'my.jobs.error.leave': 'Nepodařilo se odejít.',
    'my.jobs.role.owner': 'Vlastník',
    'my.jobs.role.manager': 'Manažer',
    'my.jobs.role.employee': 'Zaměstnanec',
    'cv.manager': 'Správce CV',
    'dashboard.welcome.back': 'Vítejte zpět',
    'dashboard.whats.happening': 'Co se děje',
    'dashboard.business': 'Firma',
    'dashboard.today': 'Dnes',
    'dashboard.total.shifts': 'Celkem směn',
    'dashboard.active.employees': 'Aktivní zaměstnanci',
    'dashboard.workplaces': 'Pracoviště',
    'dashboard.fill.rate': 'Míra obsazenosti',
    'dashboard.total.users': 'Celkem uživatelů',
    'dashboard.recent.activity': 'Poslední aktivita',
    'dashboard.new.shift.scheduled': 'Nová směna naplánována',
    'dashboard.hours.ago': 'hodin zpět',
    'dashboard.upcoming.shifts': 'Nadcházející směny',
    'dashboard.morning.shift': 'Ranní směna',
    'dashboard.tomorrow': 'Zítra',
    'dashboard.view.details': 'Zobrazit detaily',
    'dashboard.view.all': 'Zobrazit vše',
    'dashboard.you': 'Vy',
    'dashboard.activity.assigned_to': 'byl přiřazen ke směně',
    'dashboard.activity.denied_from': 'byl odmítnut ze směny',
    'dashboard.activity.requested': 'požádal o směnu',
    'dashboard.activity.leave_requested': 'požádal o odhlášení ze směny',
    'dashboard.activity.leave_approved': 'odhlášení schváleno ze směny',
    'dashboard.activity.leave_rejected': 'odhlášení zamítnuto ze směny',
    'dashboard.no.activity.title': 'Žádná nedávná aktivita',
    'dashboard.no.activity.desc': 'Aktivita se zde zobrazí, jakmile váš tým začne aplikaci používat.',
    'dashboard.no.shifts.title': 'Žádné nadcházející směny',
    'dashboard.no.shifts.desc': 'Naplánované nadcházející směny se zde zobrazí.',
    'dashboard.filled': 'obsazeno',
    'dashboard.filter.date.range': 'Vyberte období',
    'dashboard.filter.date.pick.end': 'Teď vyberte koncové datum',
    'dashboard.filter.shift.status': 'Stav směny',
    'dashboard.filter.workplace': 'Pracoviště',
    'dashboard.filter.person': 'Zaměstnanec / manažer',
    'dashboard.filter.all.people': 'Všichni lidé',
    'dashboard.filter.role.employee': 'Zaměstnanec',
    'dashboard.filter.role.manager': 'Manažer',
    'add': 'Přidat',
    'edit': 'Upravit',
    'delete': 'Smazat',
    'cancel': 'Zrušit',
    'close': 'Zavřít',
    'submit': 'Odeslat',
    'loading': 'Načítání...',
    'no.data': 'Žádná data k dispozici',
    'error': 'Chyba',
    'success': 'Úspěch',
    'warning': 'Upozornění',
    'info': 'Informace',
    'confirm': 'Potvrdit',
    'back': 'Zpět',
    'next': 'Další',
    'previous': 'Předchozí',
    'search': 'Hledat',
    'filter': 'Filtr',
    'sort': 'Seřadit',
    'refresh': 'Obnovit',
    'view': 'Zobrazit',
    'create': 'Vytvořit',
    'update': 'Aktualizovat',
    'remove': 'Odebrat',
    'select': 'Vybrat',
    'all': 'Vše',
    'none': 'Žádné',
    'yes': 'Ano',
    'no': 'Ne',
    'ok': 'OK',
    'apply': 'Použít',
    'reset': 'Resetovat',
    'clear': 'Vymazat',
    'show': 'Zobrazit',
    'hide': 'Skrýt',
    'more': 'Více',
    'less': 'Méně',
    'details': 'Detaily',
    'summary': 'Shrnutí',
    'overview': 'Přehled',
    'status': 'Stav',
    'date': 'Datum',
    'time': 'Čas',
    'name': 'Název',
    'description': 'Popis',
    'type': 'Typ',
    'category': 'Kategorie',
    'priority': 'Priorita',
    'assigned': 'Přiřazeno',
    'created': 'Vytvořeno',
    'updated': 'Aktualizováno',
    'actions': 'Akce',
    'options': 'Možnosti',
    'preferences': 'Předvolby',
    'profile': 'Profil',
    'account': 'Účet',
    'security': 'Zabezpečení',
    'notifications': 'Oznámení',
    'help': 'Nápověda',
    'support': 'Podpora',
    'about': 'O aplikaci',
    'contact': 'Kontakt',
    'privacy': 'Soukromí',
    'terms': 'Podmínky',
    'conditions': 'Podmínky',
    'policy': 'Zásady',
    'legal': 'Právní',
    'copyright': 'Autorská práva',
    'version': 'Verze',
    'language': 'Jazyk',
    'currency': 'Měna',
    'timezone': 'Časové pásmo',
    'format': 'Formát',
    'display': 'Zobrazení',
    'theme': 'Motiv',
    'color': 'Barva',
    'size': 'Velikost',
    'position': 'Pozice',
    'location': 'Umístění',
    'address': 'Adresa',
    'phone': 'Telefon',
    'email': 'E-mail',
    'website': 'Webové stránky',
    'social': 'Sociální',
    'media': 'Média',
    'file': 'Soubor',
    'image': 'Obrázek',
    'document': 'Dokument',
    'attachment': 'Příloha',
    'link': 'Odkaz',
    'url': 'URL',
    'code': 'Kód',
    'id': 'ID',
    'reference': 'Odkaz',
    'number': 'Číslo',
    'amount': 'Částka',
    'quantity': 'Množství',
    'price': 'Cena',
    'cost': 'Náklady',
    'total': 'Celkem',
    'subtotal': 'Mezisoučet',
    'tax': 'Daň',
    'discount': 'Sleva',
    'percentage': 'Procento',
    'rate': 'Sazba',
    'frequency': 'Frekvence',
    'duration': 'Doba trvání',
    'period': 'Období',
    'interval': 'Interval',
    'schedule': 'Rozvrh',
    'calendar': 'Kalendář',
    'event': 'Událost',
    'meeting': 'Schůzka',
    'appointment': 'Termín',
    'task': 'Úkol',
    'project': 'Projekt',
    'work': 'Práce',
    'job': 'Práce',
    'career': 'Kariéra',
    'employment': 'Zaměstnání',
    'contract': 'Smlouva',
    'agreement': 'Dohoda',
    'deal': 'Obchod',
    'offer': 'Nabídka',
    'proposal': 'Návrh',
    'request': 'Žádost',
    'application': 'Přihláška',
    'form': 'Formulář',
    'survey': 'Průzkum',
    'feedback': 'Zpětná vazba',
    'review': 'Recenze',
    'rating': 'Hodnocení',
    'comment': 'Komentář',
    'message': 'Zpráva',
    'notification': 'Oznámení',
    'alert': 'Upozornění',
    'reminder': 'Připomenutí',
    'invitation': 'Pozvánka',
    'invite': 'Pozvat',
    'share': 'Sdílet',
    'send': 'Odeslat',
    'receive': 'Přijmout',
    'read': 'Číst',
    'write': 'Psát',
    'reply': 'Odpovědět',
    'forward': 'Přeposlat',
    'archive': 'Archivovat',
    'trash': 'Koš',
    'restore': 'Obnovit',
    'duplicate': 'Duplikovat',
    'copy': 'Kopírovat',
    'paste': 'Vložit',
    'cut': 'Vyjmout',
    'undo': 'Zpět',
    'redo': 'Znovu',
    'print': 'Tisk',
    'scan': 'Skenovat',
    'fax': 'Fax',
    'sms': 'SMS',
    'call': 'Volat',
    'video': 'Video',
    'audio': 'Zvuk',
    'photo': 'Fotografie',
    'picture': 'Obrázek',
    'gallery': 'Galerie',
    'album': 'Album',
    'folder': 'Složka',
    'directory': 'Adresář',
    'path': 'Cesta',
    'route': 'Trasa',
    'page': 'Stránka',
    'section': 'Sekce',
    'chapter': 'Kapitola',
    'part': 'Část',
    'piece': 'Kus',
    'item': 'Položka',
    'element': 'Prvek',
    'component': 'Komponenta',
    'module': 'Modul',
    'system': 'Systém',
    'service': 'Služba',
    'function': 'Funkce',
    'feature': 'Funkce',
    'tool': 'Nástroj',
    'utility': 'Utility',
    'resource': 'Zdroj',
    'asset': 'Aktivum',
    'material': 'Materiál',
    'product': 'Produkt',
    'goods': 'Zboží',
    'merchandise': 'Zboží',
    'inventory': 'Inventář',
    'stock': 'Zásoby',
    'supply': 'Dodávka',
    'demand': 'Poptávka',
    'market': 'Trh',
    'industry': 'Průmysl',
    'sector': 'Sektor',
    'field': 'Obor',
    'city': 'Město',
    'town': 'Městečko',
    'village': 'Vesnice',
    'country': 'Země',
    'state': 'Stát',
    'province': 'Provincie',
    'county': 'Okres',
    'postal': 'PSČ',
    'zip': 'PSČ',
    'shifts.title': 'Směny',
    'shifts.subtitle': 'Spravujte své pracovní směny a rozvrhy',
    'shifts.presets': 'Předvolby směn',
    'shifts.create.shift': 'Vytvořit směnu',
    'shifts.list.view': 'Seznam',
    'shifts.calendar.view': 'Kalendář',
    'shifts.month.view': 'Měsíc',
    'shifts.week.view': 'Týden',
    'shifts.calendar.more': '+{count} další',
    'shifts.calendar.show.less': 'Zobrazit méně',
    'shifts.calendar.prev': 'Předchozí měsíc',
    'shifts.calendar.next': 'Další měsíc',
    'shifts.calendar.prev.week': 'Předchozí týden',
    'shifts.calendar.next.week': 'Další týden',
    'shifts.calendar.select.day': 'Vyberte den',
    'shifts.calendar.select.day.desc': 'Klikněte na den v kalendáři pro zobrazení směn.',
    'shifts.no.shifts.for.date': 'Pro toto datum nejsou žádné směny',
    'shifts.create.for.this.day': 'Vytvořte směnu pro tento den.',
    'shifts.reschedule.success': 'Směna přesunuta.',
    'shifts.reschedule.failed': 'Směnu se nepodařilo přesunout.',
    'shifts.bulk.selected': 'Vybráno: {count}',
    'shifts.bulk.duplicate.week': 'Duplikovat +1 týden',
    'shifts.bulk.copy.dates': 'Kopírovat na datum…',
    'shifts.bulk.copy.apply': 'Kopírovat',
    'shifts.bulk.copy.pick.date': 'Vyberte datum',
    'shifts.bulk.delete': 'Smazat',
    'shifts.bulk.clear': 'Zrušit výběr',
    'shifts.bulk.delete.confirm': 'Smazat {count} vybraných směn?',
    'shifts.bulk.delete.owner.only': 'Směny může mazat jen majitel.',
    'shifts.bulk.failed': 'Hromadná akce selhala.',
    'shifts.bulk.deleted': 'Smazáno {count} směn.',
    'shifts.bulk.created': 'Vytvořeno {count} směn.',
    'create.shift.notify.eligible': 'Oznámit vhodným zaměstnancům (push + e-mail)',
    'create.shift.recurrence': 'Opakování',
    'create.shift.recurrence.none': 'Neopakovat',
    'create.shift.recurrence.daily': 'Denně',
    'create.shift.recurrence.weekly': 'Týdně',
    'create.shift.recurrence.weekdays': 'Pracovní dny (Po–Pá)',
    'create.shift.recurrence.interval': 'Vlastní interval',
    'create.shift.recurrence.until': 'Opakovat do',
    'workplaces.color': 'Barva',
    'workplaces.color.custom': 'Vlastní barva',
    'workplaces.parent': 'Nadřazená pobočka',
    'workplaces.parent.none': 'Žádná (nejvyšší úroveň)',
    'workplaces.parent.hint': 'Max. 3 úrovně. Nelze vybrat tuto pobočku ani její podpobočky.',
    'workplaces.add.child': 'Přidat podpobočku',
    'workplaces.search.placeholder': 'Hledat pobočky…',
    'workplaces.tree.expand': 'Rozbalit',
    'workplaces.tree.collapse': 'Sbalit',
    'workplaces.tree.expand.all': 'Rozbalit vše',
    'workplaces.tree.collapse.all': 'Sbalit vše',
    'workplaces.employees.count': '{count} zaměstnanců',
    'workplaces.delete.has.children': 'Nejdřív přesuňte nebo smažte podpobočky, teprve potom lze smazat tuto pobočku.',
    'workplaces.filter.include.children': 'Včetně podpoboček',
    'vacation.leave.kind': 'Typ volna',
    'vacation.kind.vacation': 'Dovolená',
    'vacation.kind.timeoff': 'Volno',
    'groups.title': 'Uživatelské skupiny',
    'groups.subtitle': 'Nálepky pro hromadné úpravy a cílení - není to totéž jako pracovní pozice.',
    'groups.create': 'Vytvořit skupinu',
    'groups.edit': 'Upravit skupinu',
    'groups.name': 'Název skupiny',
    'groups.empty': 'Zatím žádné skupiny',
    'groups.empty.desc': 'Vytvořte skupinu pro cílení oznámení, notifikací směn nebo hromadnou změnu nastavení více lidem najednou.',
    'groups.created': 'Skupina vytvořena.',
    'groups.updated': 'Skupina uložena.',
    'groups.create.failed': 'Skupinu se nepodařilo vytvořit.',
    'groups.save.failed': 'Skupinu se nepodařilo uložit.',
    'groups.delete.confirm': 'Smazat tuto skupinu?',
    'groups.deleted': 'Skupina smazána.',
    'groups.delete.failed': 'Skupinu se nepodařilo smazat.',
    'groups.load.failed': 'Skupiny se nepodařilo načíst.',
    'groups.members.count': '{count} členů',
    'groups.members.none': 'Zatím bez členů',
    'groups.members.add': 'Přidat lidi',
    'groups.members.hint': 'Vyberte lidi, kteří do skupiny patří.',
    'groups.members.search': 'Začněte psát pro vyhledání',
    'groups.members.empty': 'Nejsou k dispozici žádní lidé k přidání.',
    'groups.members.no.match': 'Nikdo neodpovídá hledání.',
    'groups.bulk.open': 'Hromadně upravit členy',
    'groups.bulk.title': 'Hromadná úprava: {name}',
    'groups.bulk.hint': 'Změny se projeví u všech aktivních členů této skupiny.',
    'groups.bulk.workplaces': 'Pobočky',
    'groups.bulk.mode': 'Režim úpravy poboček',
    'groups.bulk.mode.replace': 'Nahradit současné pobočky',
    'groups.bulk.mode.add': 'Přidat k současným pobočkám',
    'groups.bulk.pay': 'Nastavit osobní hodinovou sazbu',
    'groups.bulk.pay.rate': 'Hodinová sazba',
    'groups.bulk.pay.placeholder': 'Prázdné = vymazat sazbu',
    'groups.bulk.pay.hint': 'Prázdné pole sazbu vymaže.',
    'groups.bulk.apply': 'Použít na členy',
    'groups.bulk.nothing': 'Nejdřív vyberte pobočky a/nebo hodinovou sazbu.',
    'groups.bulk.done': 'Upraveno {count} členů.',
    'groups.bulk.failed': 'Hromadná úprava selhala.',
    'announcements.audience.groups': 'Uživatelské skupiny',
    'announcements.audience.groups.empty': 'Zatím žádné skupiny - založte je v Uživatelských skupinách.',
    'announcements.audience.groups.required': 'Vyberte alespoň jednu skupinu.',
    'create.shift.notify.groups': 'Omezit na skupiny (volitelné)',
    'create.shift.notify.groups.hint': 'Bez výběru se oznámí všem vhodným na pobočce směny.',
    'reports.export.server': 'Stáhnout ze serveru',
    'reports.export.server.done': 'Serverový export stažen.',
    'reports.export.failed': 'Export selhal.',
    'shift.series.edit.scope.prompt': 'Rozsah úpravy série: THIS | THIS_AND_FUTURE | ALL',
    'shifts.create.for.upcoming': 'Vytvořte směnu pro nadcházející dny.',
    'shifts.status.all': 'Všechny stavy',
    'shifts.status.all.shifts': 'Všechny směny',
    'shifts.status.active': 'Aktivní',
    'shifts.status.drafts': 'Návrhy',
    'shifts.status.draft': 'Návrh',
    'shifts.status.upcoming': 'Nadcházející',
    'auto.plan.cta': 'Navrhnout směny',
    'auto.plan.title': 'Navrhnout směny',
    'auto.plan.step.of': 'Krok {step} z {total}',
    'auto.plan.step.source': 'Zdroj',
    'auto.plan.step.period': 'Období a šablony',
    'auto.plan.step.fill': 'Obsazení',
    'auto.plan.source.hint': 'Vyberte, jak sestavit návrh rozvrhu.',
    'auto.plan.source.presets': 'Ze šablon směn',
    'auto.plan.source.presets.desc': 'Rozvine vybrané šablony do cílového období.',
    'auto.plan.source.copy': 'Kopírovat minulé období',
    'auto.plan.source.copy.desc': 'Zkopíruje existující týden/měsíc na nová data.',
    'auto.plan.period.start': 'Začátek období',
    'auto.plan.period.end': 'Konec období',
    'auto.plan.locations': 'Filtr poboček',
    'auto.plan.locations.all': 'Všechny pobočky',
    'auto.plan.weekdays': 'Dny v týdnu',
    'auto.plan.weekday.mon': 'Po',
    'auto.plan.weekday.tue': 'Út',
    'auto.plan.weekday.wed': 'St',
    'auto.plan.weekday.thu': 'Čt',
    'auto.plan.weekday.fri': 'Pá',
    'auto.plan.weekday.sat': 'So',
    'auto.plan.weekday.sun': 'Ne',
    'auto.plan.presets.select': 'Vyberte šablony',
    'auto.plan.presets.empty': 'Pro tento filtr nejsou žádné šablony.',
    'auto.plan.source.start': 'Začátek zdroje',
    'auto.plan.source.end': 'Konec zdroje',
    'auto.plan.copy.hint': 'Směny ze zdrojového období se zkopírují do cíle (jen struktura; lidé se při auto-obsazení navrhují znovu).',
    'auto.plan.fill.hint': 'Návrh zůstane skrytý před zaměstnanci, dokud jej nezveřejníte.',
    'auto.plan.fill.open': 'Bez lidí (otevřené sloty)',
    'auto.plan.fill.open.desc': 'Vytvoří jen kapacitu — lidi přiřadíte nebo se přihlásí později.',
    'auto.plan.fill.auto': 'Automaticky obsadit',
    'auto.plan.fill.auto.desc': 'Obsazení podle rosteru pobočky a compliance pravidel.',
    'auto.plan.slots.hint': 'Pro každý den v týdnu v cílovém období přidejte jednu nebo více šablon.',
    'auto.plan.slots.empty': 'Pro tento den zatím žádné směny.',
    'auto.plan.slot.add': 'Přidat směnu ze šablony',
    'auto.plan.slot.remove': 'Odebrat slot',
    'auto.plan.capacity': 'Kapacita {max}',
    'auto.plan.eligible': '{count} dostupných',
    'auto.plan.eligible.min': '≥ {count} dostupných',
    'auto.plan.capacity.shortage': 'Nepůjde naplnit: potřeba {max}, volní jen {eligible}',
    'auto.plan.conflict.summary': '{count} slot(ů) nepůjde plně obsadit',
    'auto.plan.conflict.ok': 'Všech {count} plánovaných slot(ů) vypadá obsaditelně',
    'auto.plan.preflight.checking': 'Kontroluji dostupnost…',
    'auto.plan.propose.partial': 'Vytvořeno {count} návrhů. {unfillable} slot(ů) může zůstat neúplných.',
    'auto.plan.copy.cards.hint': 'Vyberte, které směny zkopírovat do nového období.',
    'auto.plan.copy.empty': 'Ve zdrojovém období nebyly nalezeny žádné směny.',
    'auto.plan.prefer.position': 'Preferovat pozici (volitelné)',
    'auto.plan.prefer.position.any': 'Libovolná pozice',
    'auto.plan.draft.notice': 'Výsledek je návrh (DRAFT). Zkontrolujte, upravte a pak publikujte nebo zahoďte.',
    'auto.plan.back': 'Zpět',
    'auto.plan.next': 'Další',
    'auto.plan.submit': 'Vytvořit návrh',
    'auto.plan.proposing': 'Vytvářím…',
    'auto.plan.load.error': 'Nepodařilo se načíst data plánovače.',
    'auto.plan.propose.success': 'Vytvořeno {count} návrhů směn.',
    'auto.plan.propose.error': 'Nepodařilo se vytvořit návrh.',
    'auto.plan.publish.success': 'Publikováno {count} směn.',
    'auto.plan.publish.error': 'Publikace se nezdařila.',
    'auto.plan.discard.confirm': 'Zahodit všechny návrhy směn (včetně ručně uložených)?',
    'auto.plan.discard.success': 'Návrh byl zahozen.',
    'auto.plan.discard.error': 'Zahození se nezdařilo.',
    'auto.plan.banner.title': 'Návrhy směn · {count}',
    'auto.plan.banner.hint': 'Zaměstnanci je neuvidí, dokud nezveřejníte. Související návrhy mají stejnou barvu okraje.',
    'auto.plan.banner.review': 'Zobrazit návrhy',
    'auto.plan.banner.hide': 'Skrýt návrhy',
    'auto.plan.banner.publish': 'Publikovat vše',
    'auto.plan.banner.discard': 'Zahodit vše',
    'auto.plan.min.hours.title': 'Minimální hodiny s návrhy',
    'auto.plan.min.hours.hint':
      'Započítává přiřazení z návrhů v týdnu/měsíci zaměstnance. Oficiálně až po publikaci.',
    'auto.plan.min.hours.empty': 'Žádný zaměstnanec nemá zapnuté minimální hodiny.',
    'auto.plan.min.hours.draft.delta': '+{hours} h návrhy',
    'auto.plan.min.hours.draft.none': 'Bez hodin z návrhů',
    'auto.plan.min.hours.published': 'Zveřejněné {hours} h',
    'auto.plan.min.hours.prev': 'Předchozí',
    'auto.plan.min.hours.next': 'Další',
    'shifts.status.attending': 'Účastním se',
    'shifts.status.attended': 'Absolvováno',
    'shifts.status.request.denied': 'Žádost zamítnuta',
    'shifts.status.request.pending': 'Žádost čeká',
    'shifts.status.leave.requested': 'Žádost o odhlášení',
    'shifts.status.attendance.requested': 'Žádost o účast',
    'shifts.all.locations': 'Všechna pracoviště',
    'shifts.search.placeholder': 'Hledat směny...',
    'attendance.requests.go.back': 'Zpět',
    'attendance.requests.title': 'Žádosti o docházku',
    'attendance.requests.subtitle': 'Spravujte žádosti o docházku a odhlášení',
    'attendance.requests.attendance.requests': 'Žádosti o docházku',
    'attendance.requests.leave.requests': 'Žádosti o odhlášení',
    'attendance.requests.no.pending.attendance': 'Žádné čekající žádosti o docházku',
    'employees.title': 'Zaměstnanci',
    'employees.subtitle': 'Spravujte členy týmu a jejich přístup',
    'employees.subtitle.manager': 'Spravujte zaměstnance přiřazené na vaše pobočky',
    'employees.generate.invite': 'Vygenerovat pozvánku',
    'employees.users': 'uživatelů',
    'employees.active.invite.codes': 'Aktivní pozvánky',
    'employees.no.active.codes': 'Žádné aktivní pozvánky',
    'employees.total.employees': 'Celkem zaměstnanců',
    'employees.active.employees': 'Aktivní zaměstnanci',
    'employees.current.employees': 'Současní zaměstnanci',
    'employees.no.employees': 'Zatím žádní zaměstnanci',
    'employees.inactive': 'Neaktivní',
    'employees.contracts.active.hint': 'Pro aktivaci zaměstnance musí mít podepsanou aktivní smlouvu.',
    'employees.edit': 'Upravit',
    'employees.remove': 'Odebrat',
    'messages.title': 'Zprávy',
    'messages.subtitle': 'Pište týmu i majitelům/manažerům firem, kam se hlásíte',
    'messages.search.contacts': 'Hledat kontakty...',
    'messages.filter.all': 'Všichni',
    'messages.filter.owners': 'Majitelé',
    'messages.filter.managers': 'Manažeři',
    'messages.filter.employees': 'Zaměstnanci',
    'messages.filter.team': 'Tým',
    'messages.filter.applications': 'Přihlášky',
    'messages.empty.title': 'Zatím žádné kontakty',
    'messages.empty.desc': 'Tady uvidíte kolegy z pracovišť. Po přihlášce na nabídku práce můžete psát i majiteli a manažerům dané firmy.',
    'messages.empty.search': 'Žádné kontakty neodpovídají hledání.',
    'messages.empty.cta.jobs': 'Prohlížet nabídky',
    'messages.badge.application': 'Přihláška',
    'messages.badge.pending': 'Čekající přihláška',
    'messages.badge.approved': 'Přijatý / schválený',
    'messages.badge.rejected': 'Přihláška uzavřena',
    'messages.for.job': 'K pozici: {title}',
    'messages.message.employer': 'Napsat firmě',
    'messages.message.applicant': 'Napsat uchazeči',
    'messages.job.context': 'Reaguje na nabídku práce',
    'messages.job.context.open': 'Zobrazit nabídku',
    'messages.view.cv': 'Zobrazit CV',
    'messages.no.cv': 'CV není nahrané',
    'job.details.message.employer': 'Napsat firmě',
    'job.details.message.employer.hint': 'Po přihlášce se můžete firmy zeptat na pozici.',
    'job.applications.message': 'Napsat',
    'messages.role.owner': 'Majitel',
    'messages.role.manager': 'Manažer',
    'messages.role.employee': 'Zaměstnanec',
    'messages.at': 'v',
    'messages.select.contact': 'Vyberte kontakt',
    'messages.select.contact.subtitle': 'Vyberte kontakt pro zahájení konverzace',
    'messages.swap.offer': 'Nabídnout směnu',
    'messages.swap.modal.title': 'Nabídnout suplování směny',
    'messages.swap.modal.select.shift': 'Vyberte směnu',
    'messages.swap.modal.select.colleague': 'Vyberte kolegu',
    'messages.swap.modal.note': 'Poznámka (volitelně)',
    'messages.swap.modal.note.placeholder': 'Přidejte krátkou zprávu…',
    'messages.swap.modal.send': 'Odeslat nabídku',
    'messages.swap.modal.cancel': 'Zrušit',
    'messages.swap.modal.no.shifts': 'Nemáte žádné schválené budoucí směny k nabídnutí.',
    'messages.swap.modal.no.colleagues': 'Pro tuto směnu nejsou vhodní žádní kolegové.',
    'messages.swap.modal.loading': 'Načítání…',
    'messages.swap.card.title': 'Nabídka suplování',
    'messages.swap.accept': 'Přijmout',
    'messages.swap.decline': 'Odmítnout',
    'messages.swap.cancel': 'Zrušit nabídku',
    'messages.swap.status.PENDING': 'Čeká na odpověď',
    'messages.swap.status.DECLINED': 'Odmítnuto',
    'messages.swap.status.CANCELLED': 'Zrušeno',
    'messages.swap.status.PENDING_MANAGER': 'Čeká na manažera',
    'messages.swap.status.COMPLETED': 'Dokončeno',
    'messages.swap.status.REJECTED_MANAGER': 'Zamítnuto manažerem',
    'messages.swap.toast.sent': 'Nabídka směny odeslána.',
    'messages.swap.toast.accepted': 'Nabídka přijata.',
    'messages.swap.toast.accepted.manager': 'Přijato - čeká na schválení manažerem.',
    'messages.swap.toast.declined': 'Nabídka odmítnuta.',
    'messages.swap.toast.cancelled': 'Nabídka zrušena.',
    'messages.swap.toast.error': 'Něco se pokazilo.',
    'messages.swap.toast.link.created': 'Sdílitelný odkaz na výměnu vytvořen.',
    'messages.swap.modal.mode.colleague': 'Kolega',
    'messages.swap.modal.mode.link': 'Sdílet odkaz',
    'messages.swap.modal.create.link': 'Vytvořit a zkopírovat odkaz',
    'messages.swap.modal.link.hint':
      'Kdokoli, kdo splní podmínky směny, ji přes odkaz může převzít. Platí první, kdo přijme.',
    'messages.swap.modal.link.ready': 'Odkaz je připravený — pošlete ho kolegům (funguje na webu i v appce).',
    'messages.swap.modal.done': 'Hotovo',
    'swap.link.title': 'Nabídka výměny směny',
    'swap.link.from': 'Nabízí',
    'swap.link.claim': 'Převzít tuto směnu',
    'swap.link.claiming': 'Přebírám…',
    'swap.link.claimed': 'Směna převzata.',
    'swap.link.cancel': 'Zrušit odkaz',
    'swap.link.cancelled': 'Odkaz na výměnu zrušen.',
    'swap.link.copy': 'Zkopírovat odkaz',
    'swap.link.copied': 'Odkaz zkopírován.',
    'swap.link.copy.failed': 'Odkaz se nepodařilo zkopírovat.',
    'swap.link.login.required': 'Pro zobrazení a přijetí výměny se přihlaste.',
    'swap.link.login.cta': 'Přihlásit se',
    'swap.link.back': 'Zpět',
    'swap.link.open.app': 'Otevřít v aplikaci Ompli',
    'swap.link.owner.hint': 'Toto je váš sdílitelný odkaz. Zkopírujte ho a pošlete kolegům.',
    'swap.link.not.eligible': 'Na tuto směnu nemáte nárok.',
    'swap.link.unavailable': 'Tento odkaz na výměnu už není dostupný.',
    'swap.link.error': 'Odkaz na výměnu se nepodařilo načíst.',
    'swap.link.status.OPEN': 'Otevřený',
    'swap.link.status.CLAIMED': 'Převzatý',
    'swap.link.status.CANCELLED': 'Zrušený',
    'attendance.requests.swap.requests': 'Suplování',
    'attendance.requests.no.pending.swap': 'Žádné čekající suplování',
    'attendance.requests.swap.from': 'Od',
    'attendance.requests.swap.to': 'Pro',
    'attendance.requests.swap.approved': 'Suplování schváleno.',
    'attendance.requests.swap.rejected': 'Suplování zamítnuto.',
    'attendance.requests.swap.empty.desc': 'Žádosti o suplování od kolegů čekající na schválení se zobrazí zde.',
    'workplaces.title': 'Pracoviště',
    'workplaces.subtitle.owner': 'Spravujte lokace a pracoviště vaší firmy',
    'workplaces.add.location': 'Přidat pracoviště',
    'contracts.title': 'Smlouvy',
    'contracts.send.contract': 'Odeslat smlouvu',
    'contracts.contract': 'Smlouva',
    'contracts.duration': 'Doba trvání',
    'contracts.download': 'Stáhnout',
    'contracts.waiting.sign': 'Čeká na podpis',
    'contracts.terminate.contract': 'Ukončit smlouvu',
    'contracts.contract.history': 'Historie smluv',
    'contracts.terminated': 'Ukončeno',
    'contracts.download.signed': 'Stáhnout podepsanou',
    'contracts.status.draft': 'Koncept',
    'contracts.status.sent': 'Odesláno',
    'contracts.status.signed': 'Podepsáno',
    'contracts.status.awaiting': 'Čeká na podpis',
    'contracts.status.rejected': 'Zamítnuto',
    'contracts.status.inactive': 'Neaktivní',
    'contracts.duration.monthly': 'Měsíční',
    'contracts.duration.yearly': 'Roční',
    'contracts.duration.unlimited': 'Neomezená',
    'contracts.duration.custom': 'Vlastní',
    'job.board.title': 'Nabídka práce',
    'job.board.subtitle': 'Zveřejňujte a spravujte pracovní příležitosti',
    'job.board.post.job': 'Zveřejnit práci',
    'job.board.list.view': 'Seznam',
    'job.board.grid.view': 'Mřížka',
    'job.board.my.jobs': 'Moje práce',
    'job.board.all.jobs': 'Všechny práce',
    'job.board.search.jobs': 'Hledat práce...',
    'positions.title': 'Pozice',
    'positions.subtitle': 'Spravujte pracovní pozice a role',
    'positions.add.position': 'Přidat pozici',
    'positions.edit.position': 'Upravit pozici',
    'positions.add.new.position': 'Přidat novou pozici',
    'positions.form.subtitle': 'Vyplňte níže uvedené údaje pro nastavení pozice',
    'positions.section.basic': 'Základní informace',
    'positions.section.workplaces': 'Pracoviště',
    'positions.section.workplaces.hint': 'Pokud nic nevyberete, pozice platí pro všechna pracoviště. Jinak zvolte, kde se má používat.',
    'positions.all.workplaces': 'Všechna pracoviště',
    'positions.no.workplaces.available': 'Zatím nejsou k dispozici žádná pracoviště.',
    'positions.add.workplace.search': 'Hledat pracoviště…',
    'positions.add.workplace.no.match': 'Žádné odpovídající pracoviště',
    'positions.remove.workplace': 'Odebrat pracoviště',
    'positions.empty.desc': 'Vytvořte první pozici a začněte přiřazovat zaměstnance k rolím.',
    'positions.save.changes': 'Uložit změny',
    'create.shift.position': 'Pozice',
    'create.shift.select.position': 'Vyberte pozici (volitelné)',
    'create.shift.position.none': 'Bez pozice',
    'create.shift.position.select.location.first': 'Nejdřív vyberte pracoviště, aby se zobrazily dostupné pozice',
    'positions.created': 'Pozice vytvořena.',
    'positions.updated': 'Pozice aktualizována.',
    'positions.deleted': 'Pozice smazána.',
    'positions.no.limit': 'Bez limitu',
    'positions.any.gender': 'Jakékoliv pohlaví',
    'positions.max.people': 'Max lidí',
    'positions.gender': 'Pohlaví',
    'recent.activity.title': 'Poslední aktivita',
    'recent.activity.subtitle': 'Sledujte všechny aktivity a změny ve firmě',
    'recent.activity.filters': 'Filtry',
    'recent.activity.activities': 'aktivit',
    'recent.activity.shift.assignment': 'přiřazení směny',
    'recent.activity.shift.created': 'Směna vytvořena',
    'recent.activity.requested': 'Požadováno',
    'recent.activity.business.event': 'událost firmy',
    'recent.activity.employee.rejoined': 'Zaměstnanec se vrátil',
    'recent.activity.rejoined.as': 'vrátil se jako',
    'recent.activity.left': 'Odešel',
    'recent.activity.inactive': 'Neaktivní',
    'recent.activity.changes': 'Změny',
    'recent.activity.change.created': 'Vytvořeno',
    'recent.activity.change.updated': 'Upraveno',
    'recent.activity.change.deleted': 'Smazáno',
    'recent.activity.date': 'Datum',
    'recent.activity.business.events': 'Události firmy',
    'recent.activity.no.found': 'Žádné aktivity',
    'recent.activity.no.found.for': 'Žádné aktivity pro {type}',
    'recent.activity.count': '{shown} z {total} aktivit',
    'recent.activity.on.date': 'Aktivity dne {date}',
    'recent.activity.type.all': 'Vše',
    'recent.activity.type.change': 'Změna',
    'recent.activity.type.shift_assignment': 'Přiřazení směny',
    'recent.activity.type.leave_request': 'Žádost o odhlášení',
    'recent.activity.type.job_application': 'Přihláška na práci',
    'recent.activity.type.cv_update': 'Aktualizace CV',
    'recent.activity.type.business_event': 'Událost firmy',
    'recent.activity.type.contract': 'Smlouva',
    'activity.summary.created': 'Vytvořeno: {entity}',
    'activity.summary.created.named': 'Vytvořeno: {entity} - {name}',
    'activity.summary.updated': 'Upraveno: {entity}: {changes}',
    'activity.summary.updated.named': 'Upraveno: {entity} ({name}): {changes}',
    'activity.summary.updated.plain': 'Upraveno: {entity}',
    'activity.summary.updated.plain.named': 'Upraveno: {entity} ({name})',
    'activity.summary.deleted': 'Smazáno: {entity}',
    'activity.summary.deleted.named': 'Smazáno: {entity} - {name}',
    'activity.summary.more': '+{count} dalších',
    'activity.val.empty': 'prázdné',
    'activity.val.on': 'ano',
    'activity.val.off': 'ne',
    'activity.entity.unknown': 'položka',
    'activity.entity.User': 'uživatel',
    'activity.entity.Business': 'firma',
    'activity.entity.Location': 'pracoviště',
    'activity.entity.Position': 'pozice',
    'activity.entity.Shift': 'směna',
    'activity.entity.ShiftPreset': 'šablona směny',
    'activity.entity.ShiftAssignment': 'přiřazení směny',
    'activity.entity.LeaveRequest': 'žádost o odhlášení',
    'activity.entity.JobListing': 'nabídka práce',
    'activity.entity.JobApplication': 'přihláška na práci',
    'activity.entity.UserBusiness': 'zaměstnanec',
    'activity.entity.ManagerBusiness': 'manažer',
    'activity.entity.ManagerWorkplaceAssignment': 'pracoviště manažera',
    'activity.entity.Contract': 'smlouva',
    'activity.entity.CV': 'CV',
    'activity.entity.Invite': 'pozvánka',
    'activity.entity.Worker': 'pracovník',
    'activity.entity.UserBusinessPosition': 'pozice zaměstnance',
    'activity.entity.CalendarExternalEvent': 'kalendářová událost',
    'activity.entity.WorkedHoursEntry': 'záznam odpracovaných hodin',
    'activity.field.active': 'aktivní',
    'activity.field.payRate': 'sazba',
    'activity.field.payAmount': 'částka',
    'activity.field.payType': 'typ odměny',
    'activity.field.notes': 'poznámky',
    'activity.field.phone': 'telefon',
    'activity.field.address': 'adresa',
    'activity.field.startDate': 'datum začátku',
    'activity.field.endDate': 'datum konce',
    'activity.field.startTime': 'čas začátku',
    'activity.field.endTime': 'čas konce',
    'activity.field.position': 'pozice',
    'activity.field.name': 'název',
    'activity.field.title': 'titulek',
    'activity.field.description': 'popis',
    'activity.field.email': 'e-mail',
    'activity.field.maxAttendees': 'max. počet lidí',
    'activity.field.approvalType': 'typ schválení',
    'activity.field.allowLeave': 'povolení odhlášení',
    'activity.field.locationId': 'pracoviště',
    'activity.field.positionId': 'pozice',
    'activity.field.status': 'stav',
    'activity.field.firstName': 'jméno',
    'activity.field.lastName': 'příjmení',
    'activity.field.username': 'uživatelské jméno',
    'activity.field.hourlyPayRate': 'hodinová sazba',
    'activity.field.contractsEnabled': 'smlouvy zapnuty',
    'managers.title': 'Manažeři',
    'managers.subtitle': 'Spravujte manažery vaší firmy a jejich přístup',
    'managers.invite.manager': 'Pozvat manažera',
    'managers.generate.invite': 'Vygenerovat pozvánku',
    'managers.generating': 'Generuji...',
    'managers.active.invites': 'Aktivní pozvánky',
    'managers.active.invite.codes': 'Aktivní pozvánky',
    'managers.no.active.invites': 'Žádné aktivní pozvánky',
    'managers.current.managers': 'Současní manažeři',
    'managers.no.managers': 'Zatím žádní manažeři',
    'managers.total.managers': 'Celkem manažerů',
    'managers.active.managers': 'Aktivní manažeři',
    'managers.remove.manager': 'Odebrat manažera',
    'managers.remove': 'Odebrat',
    'managers.edit': 'Upravit',
    'managers.users': 'Uživatelé',
    'managers.no.workplaces': 'Žádná přiřazená pracoviště',
    'sent': 'Odesláno',
    'signed': 'Podepsáno',
    'daily': 'Denně',
    'hourly': 'Hodinově',
    'attendance': 'Docházka',
    'terminate': 'Ukončit',
    'history': 'Historie',
    'post': 'Zveřejnit',
    'no.limit': 'Bez limitu',
    'any.gender': 'Jakékoliv pohlaví',
    'max.people': 'Max lidí',
    'shift.assignment': 'přiřazení směny',
    'shift.created': 'Směna vytvořena',
    'requested': 'Požadováno',
    'business.event': 'událost firmy',
    'employee.rejoined': 'Zaměstnanec se vrátil',
    'rejoined.as': 'vrátil se jako',
    'left': 'Odešel',
    'invite.manager': 'Pozvat manažera',
    'active.invites': 'Aktivní pozvánky',
    'no.active.invites': 'Žádné aktivní pozvánky',
    'total.managers': 'Celkem manažerů',
    'active.managers': 'Aktivní manažeři',
    'remove.manager': 'Odebrat manažera',
    'login.welcome.back': 'Vítejte zpět',
    'login.sign.in.account': 'Přihlaste se ke svému účtu',
    'login.employer': 'Zaměstnavatel',
    'login.employee': 'Zaměstnanec',
    'login.manager': 'Manažer',
    'login.email.address': 'E-mailová adresa',
    'login.password': 'Heslo',
    'login.remember.me': 'Zapamatovat si mě',
    'login.forgot.password': 'Zapomněli jste heslo?',
    'login.sign.in': 'Přihlásit se',
    'login.quick.login.owner': 'Rychlé přihlášení - Vlastník',
    'login.quick.login.employee': 'Rychlé přihlášení - Zaměstnanec',
    'login.quick.login.manager': 'Rychlé přihlášení - Manažer',
    'login.or.continue.with': 'Nebo pokračujte s',
    'login.oauth.error': 'Přihlášení přes Google/Microsoft/Apple selhalo',
    'login.oauth.success': 'Úspěšně přihlášeno.',
    'login.oauth.apple.unavailable': 'Přihlášení přes Apple není na serveru nastavené.',
    'login.dont.have.account': 'Nemáte účet?',
    'login.register.here': 'Zaregistrujte se zde',
    'register.join.ompli': 'Připojte se k Ompli',
    'register.choose.role': 'Vyberte svou roli',
    'register.im.employer': 'Jsem zaměstnavatel',
    'register.im.employee': 'Jsem zaměstnanec',
    'register.im.manager': 'Jsem manažer',
    'register.next': 'Další',
    'register.already.have.account': 'Už máte účet?',
    'register.sign.in': 'Přihlásit se',
    'active': 'Aktivní',
    'all.activities': 'Všechny aktivity',
    'attendance.requests.approve': 'Schválit',
    'attendance.requests.cancel': 'Zrušit',
    'attendance.requests.confirm.denial': 'Potvrdit zamítnutí',
    'attendance.requests.deny': 'Zamítnout',
    'attendance.requests.deny.leave.request': 'Zamítnout žádost o odhlášení',
    'attendance.requests.deny.reason': 'Důvod zamítnutí',
    'attendance.requests.enter.denial.reason': 'Zadejte důvod zamítnutí',
    'attendance.requests.no.pending.leave': 'Žádné čekající žádosti o odhlášení',
    'attendance.requests.leave.approved': 'Žádost o odhlášení schválena.',
    'attendance.requests.leave.denied': 'Žádost o odhlášení zamítnuta.',
    'attendance.requests.leave.empty.desc': 'Žádosti o odhlášení od týmu se zobrazí zde.',
    'request_leave': 'Požádat o odhlášení',
    'request_leave_shift_message': 'Žádáte o odhlášení ze směny',
    'reason_for_leaving': 'Důvod odhlášení',
    'please_provide_reason': 'Zadejte důvod (volitelné)',
    'attendance.requests.pending': 'Čekající',
    'contracts.all': 'Vše',
    'contracts.approve': 'Schválit',
    'contracts.choose.pdf': 'Vybrat PDF',
    'contracts.contract.sent': 'Smlouva odeslána',
    'contracts.custom.end.date': 'Vlastní datum ukončení',
    'contracts.enter.rejection.reason': 'Zadejte důvod zamítnutí',
    'contracts.enter.termination.reason': 'Zadejte důvod ukončení',
    'contracts.expiry': 'Vypršení',
    'contracts.failed.download': 'Nepodařilo se stáhnout',
    'contracts.failed.send': 'Nepodařilo se odeslat',
    'contracts.failed.terminate': 'Nepodařilo se ukončit',
    'contracts.failed.upload': 'Nepodařilo se nahrát',
    'contracts.no.history': 'Žádná historie',
    'contracts.please.download.sign': 'Prosím stáhněte a podepište',
    'contracts.reason.optional': 'Důvod (volitelné)',
    'contracts.reject': 'Zamítnout',
    'contracts.reject.contract': 'Zamítnout smlouvu',
    'contracts.rejecting': 'Zamítám…',
    'contracts.rejected': 'Smlouva zamítnuta.',
    'contracts.failed.reject': 'Nepodařilo se zamítnout smlouvu.',
    'contracts.select.employees': 'Vybrat zaměstnance',
    'contracts.send': 'Odeslat',
    'contracts.send.signed': 'Odeslat podepsanou',
    'contracts.sending.signed': 'Odesílání podepsané',
    'contracts.signed.uploaded': 'Podepsaná smlouva nahrána',
    'contracts.terminate': 'Ukončit',
    'contracts.terminate.contract.for': 'Ukončit smlouvu pro',
    'contracts.until': 'Do',
    'contracts.upload.pdf': 'Nahrát PDF',
    'employment.type': 'Typ úvazku',
    'employment.type.DPP': 'DPP (dohoda o provedení práce)',
    'employment.type.DPC': 'DPČ (dohoda o pracovní činnosti)',
    'employment.type.HPP': 'HPP (pracovní smlouva)',
    'employment.workplace': 'Pracoviště',
    'employment.select.workplace': 'Vyberte pracoviště',
    'employment.job.description': 'Popis práce',
    'employment.job.description.hint': 'Konkrétní popis vykonávané práce',
    'employment.start.date': 'Datum nástupu',
    'employment.end.date': 'Datum ukončení (volitelné)',
    'employment.pay.type': 'Typ odměny',
    'employment.pay.hourly': 'Hodinová',
    'employment.pay.monthly': 'Měsíční',
    'employment.pay.amount': 'Výše odměny',
    'employment.weekly.hours': 'Sjednaný týdenní fond hodin',
    'employment.send.missing.basic': 'Vyberte alespoň jednoho zaměstnance.',
    'employment.send.missing.fields': 'Vyplňte pracoviště, datum nástupu, popis práce a odměnu.',
    'employment.send.missing.weekly': 'U DPČ/HPP je povinný týdenní fond hodin.',
    'employment.cssz.mark': 'Označit nahlášení ČSSZ',
    'employment.cssz.marked': 'Nahlášení ČSSZ označeno jako hotové.',
    'employment.cssz.failed': 'Nepodařilo se označit nahlášení ČSSZ.',
    'employment.cssz.done': 'ČSSZ nahlášeno',
    'employment.bozp.mark': 'Označit BOZP',
    'employment.bozp.marked': 'BOZP potvrzeno.',
    'employment.bozp.failed': 'Nepodařilo se označit BOZP.',
    'employment.bozp.done': 'BOZP hotovo',
    'employment.checklist.title': 'Měsíční přehled ČSSZ',
    'employment.checklist.desc': 'Checklist v aplikaci - potvrďte odeslání měsíčního přehledu',
    'employment.checklist.due': 'do',
    'employment.checklist.mark': 'Označit jako odesláno',
    'employment.checklist.marked': 'Tento měsíc odesláno',
    'employment.checklist.done': 'Měsíční přehled označen jako hotový.',
    'employment.checklist.undone': 'Označení měsíčního přehledu zrušeno.',
    'employment.checklist.failed': 'Nepodařilo se aktualizovat checklist.',
    'employment.compliance.title': 'Hodiny a compliance',
    'employment.compliance.no.contract': 'Zaměstnanec nemá aktivní podepsanou smlouvu.',
    'employment.hours.ytd': 'Hodiny letos',
    'employment.earnings.month': 'Výdělek tento měsíc',
    'employment.dpp.remaining': 'Zbývající hodiny DPP',
    'employment.suggest.switch': 'Zvažte přechod na',
    'employment.term': 'Doba trvání smlouvy',
    'employment.term.fixed': 'Na dobu určitou',
    'employment.term.until.task': 'Do splnění úkolu',
    'employment.term.indefinite': 'Na dobu neurčitou',
    'employment.term.from': 'od',
    'employment.term.to': 'do',
    'employment.term.quick': 'Rychlá volba délky',
    'employment.term.quick.2w': '2 týdny',
    'employment.term.quick.1m': '1 měsíc',
    'employment.term.quick.3m': '3 měsíce',
    'employment.term.quick.6m': '6 měsíců',
    'employment.term.quick.1y': '1 rok',
    'employment.term.dpp.long.warn': 'Tato doba je 6 a více měsíců. Při práci na plný úvazek hrozí překročení limitu DPP 300 h/rok - jen informativní upozornění; upravte rozsah, pokud bude práce intenzivnější.',
    'employment.term.task.desc': 'Popis konkrétní práce',
    'employment.term.task.placeholder': 'např. výpomoc při stěhování skladu 5.–8. srpna',
    'employment.term.task.hint': 'Nepoužívejte obecné fráze typu „pomocné práce“ - při kontrole hrozí posouzení jako skrytý pracovní poměr. Popište konkrétní úkol.',
    'employment.term.indefinite.info': 'Smlouva nemá konec a běží, dokud ji jedna ze stran neukončí.',
    'employment.term.termination': 'Způsob ukončení',
    'employment.term.termination.required': 'Vyberte způsob ukončení smlouvy na dobu neurčitou.',
    'employment.term.termination.notice': 'Výpovědí (15denní lhůta)',
    'employment.term.termination.notice.hint': 'Kterákoli strana může smlouvu ukončit s výpovědní lhůtou 15 dnů.',
    'employment.term.termination.agreement': 'Dohodou obou stran',
    'employment.term.termination.agreement.hint': 'Smlouva končí písemnou dohodou obou stran.',
    'employment.term.end.required': 'U smlouvy na dobu určitou je povinné datum do.',
    'employment.term.end.after.start': 'Datum do musí být později než datum od.',
    'employment.term.until.task.dpp.only': 'Možnost „Do splnění úkolu“ je dostupná jen u DPP.',
    'employment.term.summary': 'Shrnutí',
    'employment.weekly.hours.dpc.hint': 'U DPČ nesmí průměrný rozsah přesáhnout 20 hodin týdně za sjednané období.',
    'signing.title': 'Smlouva',
    'signing.subtitle': 'Přečtěte si celé znění a podepište elektronicky v aplikaci. PDF vznikne až po podpisu obou stran.',
    'signing.hash': 'Otisk dokumentu',
    'signing.open': 'Otevřít a podepsat',
    'signing.send.for.signature': 'Podepsat a odeslat brigádníkovi',
    'signing.sent.toast': 'Smlouva odeslána k podpisu v aplikaci.',
    'bozp.title': 'Dokument BOZP',
    'bozp.hint': 'Nahrajte PDF nebo napište školení v editoru. Brigádník musí PDF stáhnout a potvrdit, než může podepsat smlouvu.',
    'bozp.mode.write': 'Napsat v editoru',
    'bozp.mode.upload': 'Nahrát PDF',
    'bozp.write.placeholder': 'Napište obsah školení BOZP…',
    'bozp.write.required': 'Napište alespoň krátký text BOZP (min. 20 znaků), nebo nahrajte PDF.',
    'bozp.upload.required': 'Nahrajte PDF BOZP, nebo přepněte na editor a text napište.',
    'bozp.employee.title': 'Školení BOZP (povinné)',
    'bozp.employee.hint': 'Nejdřív stáhněte PDF BOZP, pak potvrďte, že jste ho přečetli. Teprve potom můžete podepsat smlouvu.',
    'bozp.download': 'Stáhnout PDF BOZP',
    'bozp.download.again': 'Stáhnout znovu',
    'bozp.downloading': 'Stahuji…',
    'bozp.download.done': 'PDF BOZP staženo.',
    'bozp.download.failed': 'Stažení PDF BOZP se nezdařilo.',
    'bozp.download.first': 'Nejdřív stáhněte PDF BOZP, pak můžete potvrdit.',
    'bozp.ack.text': 'Stáhl/a jsem a přečetl/a dokument školení BOZP a souhlasím s jeho obsahem.',
    'bozp.ack.done': 'Souhlas s BOZP uložen.',
    'bozp.ack.failed': 'Nepodařilo se potvrdit BOZP.',
    'bozp.ack.required': 'Před podpisem potvrďte souhlas s BOZP.',
    'bozp.sign.locked': 'Nejdřív dokončete kroky BOZP výše, teprve pak můžete podepsat smlouvu.',
    'term.title': 'Ukončení smlouvy',
    'term.back': 'Zpět',
    'term.failed': 'Akce se nezdařila.',
    'term.ended': 'Ukončeno',
    'term.fixed.hint': 'Když nic neuděláte, smlouva se po datu konce ukončí automaticky.',
    'term.reminder.expiry': 'Tato smlouva končí',
    'term.reminder.task': 'Tato dohoda do splnění úkolu běží přes 60 dní - je úkol ještě aktivní?',
    'term.action.extend': 'Prodloužit koncové datum',
    'term.action.task.done': 'Označit úkol jako splněný',
    'term.action.notice': 'Podat výpověď (15 dní)',
    'term.action.agreement': 'Navrhnout ukončení dohodou',
    'term.action.immediate': 'Okamžité zrušení',
    'term.extend.hint': 'Současné datum konce:',
    'term.extend.required': 'Zvolte nové datum konce.',
    'term.extend.submit': 'Navrhnout dodatek',
    'term.extend.done': 'Smlouva prodloužena.',
    'term.extend.proposed': 'Dodatek o prodloužení odeslán - čeká se na podpis brigádníka.',
    'term.extend.signed': 'Dodatek podepsán.',
    'term.extend.waiting': 'Dodatek o prodloužení čeká na podpis brigádníka. Nové datum konce:',
    'term.extend.amendment.hint': 'Prodloužení se řeší písemným dodatkem. Obě strany ho musí podepsat, teprve pak se změní datum konce.',
    'term.extend.amendment.title': 'Dodatek o prodloužení',
    'term.extend.sign': 'Podepsat dodatek',
    'term.attention.amendment': 'Zaměstnavatel navrhl prodloužení smlouvy. Přečtěte si dodatek a podepište.',
    'term.task.date': 'Datum dokončení',
    'term.task.hours': 'Odpracované hodiny (volitelné)',
    'term.task.note': 'Poznámka (volitelné)',
    'term.task.submit': 'Uzavřít úkol',
    'term.task.done': 'Úkol označen jako splněný - smlouva ukončena.',
    'term.notice.hint': 'Podáním výpovědi začne sledování doručení. 15denní lhůta běží od potvrzení doručení druhou stranou (nebo zadejte datum, pokud už bylo doručeno mimo appku).',
    'term.notice.delivered.optional': 'Datum doručení (volitelné, pokud už doručeno)',
    'term.notice.submit': 'Podat výpověď',
    'term.notice.filed': 'Výpověď podána.',
    'term.notice.ack.hint': 'Potvrďte převzetí výpovědi. Od dneška běží 15denní lhůta.',
    'term.notice.ack': 'Potvrdit doručení',
    'term.notice.acked': 'Doručení potvrzeno - běží výpovědní lhůta.',
    'term.notice.running': 'Výpovědní lhůta končí',
    'term.agreement.hint': 'Navrhněte datum konce. Druhá strana musí potvrdit, než se smlouva uzavře.',
    'term.agreement.note': 'Poznámka (volitelné)',
    'term.agreement.submit': 'Navrhnout dohodu',
    'term.agreement.proposed': 'Návrh dohody odeslán.',
    'term.agreement.end': 'Dohodnuté datum konce',
    'term.agreement.confirm': 'Souhlasím s ukončením smlouvy k navrhovanému datu.',
    'term.agreement.sign': 'Potvrdit dohodu',
    'term.agreement.signed': 'Souhlas s dohodou uložen.',
    'term.agreement.waiting': 'Potvrdili jste. Čeká se na druhou stranu.',
    'term.immediate.warning': 'Okamžité zrušení je výjimečné a jen za zákonem vymezených podmínek. Doručení druhé straně bude zaznamenáno.',
    'term.immediate.reason': 'Zákonný důvod / popis',
    'term.immediate.submit': 'Podat okamžité zrušení',
    'term.immediate.filed': 'Okamžité zrušení podáno - čeká se na potvrzení doručení.',
    'term.immediate.ack.hint': 'Potvrďte, že jste převzali oznámení o okamžitém zrušení.',
    'term.immediate.ack': 'Potvrdit doručení a uzavřít',
    'term.immediate.acked': 'Smlouva ukončena okamžitým zrušením.',
    'term.certificate': 'Vygenerovat potvrzení o ukončení',
    'term.certificate.ready': 'Potvrzení o ukončení je připraveno.',
    'term.cssz.offboard': 'Označit odhlášení z ČSSZ',
    'term.cssz.offboard.done': 'Odhlášení z ČSSZ označeno.',
    'term.reason.EXPIRED': 'Uplynutí doby',
    'term.reason.TASK_DONE': 'Splnění úkolu',
    'term.reason.NOTICE': 'Výpověď',
    'term.reason.AGREEMENT': 'Dohoda',
    'term.reason.IMMEDIATE': 'Okamžité zrušení',
    'term.manage': 'Ukončení smlouvy',
    'term.attention.title': 'Vyžaduje vaši akci',
    'term.attention.hint': 'Zaměstnavatel zahájil ukončení smlouvy. Otevřete ji a potvrďte doručení nebo dohodu.',
    'term.attention.badge': 'Čeká na vás',
    'term.attention.cta': 'Otevřít a reagovat',
    'term.attention.notice': 'Zaměstnavatel podal výpověď. Potvrďte převzetí - od potvrzení běží 15denní lhůta.',
    'term.attention.immediate': 'Zaměstnavatel podal okamžité zrušení. Potvrďte, že jste oznámení převzali.',
    'term.attention.agreement': 'Zaměstnavatel navrhl ukončení dohodou. Přečtěte si návrh a potvrďte.',
    'term.sign.title': 'Váš elektronický podpis',
    'term.sign.clickwrap': 'Toto ukončení potvrzuji a elektronicky podepisuji.',
    'signing.editor.title': 'Text smlouvy',
    'signing.editor.placeholder': 'Návrh smlouvy se zobrazí zde…',
    'signing.editor.hint': 'Upravte před odesláním. PDF se nepoužívá k podpisu - vygeneruje se až po podpisu obou stran.',
    'signing.editor.regenerate': 'Znovu vygenerovat z dat',
    'signing.editor.generating': 'Generuji…',
    'signing.body.required': 'Text smlouvy je povinný.',
    'signing.employer.sign': 'Váš elektronický podpis (zaměstnavatel)',
    'signing.method.clickwrap': 'Souhlasím a podepisuji',
    'signing.method.drawn': 'Podpis na displeji',
    'signing.clickwrap.text': 'Smlouvu jsem přečetl/a a elektronicky ji podepisuji jako zaměstnavatel.',
    'signing.confirm.required': 'Potvrďte souhlas s podpisem.',
    'signing.draw.required': 'Nakreslete prosím podpis.',
    'signing.draw.hint': 'Podepište myší nebo prstem',
    'signing.clear': 'Vymazat',
    'signing.submit': 'Podepsat smlouvu',
    'signing.submitting': 'Podepisuji…',
    'signing.signed': 'Podpis uložen.',
    'signing.fully.signed': 'Obě strany podepsaly - archivní PDF je připraveno.',
    'signing.failed': 'Podpis se nezdařil.',
    'signing.load.failed': 'Nepodařilo se načíst smlouvu.',
    'signing.waiting.other': 'Podepsali jste. Čeká se na druhou stranu.',
    'signing.waiting.employee': 'Čeká se na podpis brigádníka',
    'signing.waiting.employer': 'Čeká se na váš podpis',
    'signing.party.employer': 'Zaměstnavatel',
    'signing.party.employee': 'Brigádník',
    'signing.download.archive': 'Stáhnout archivní PDF',
    'signing.identity.optional': 'Vyžadovat ověření identity před podpisem brigádníka',
    'signing.identity.optional.hint': 'Volitelný e-mail/SMS kód. Vhodné u delších smluv; u krátkých brigád klidně vypněte.',
    'signing.identity.hint': 'Před podpisem zadejte ověřovací kód.',
    'signing.identity.request': 'Poslat kód',
    'signing.identity.code': 'Ověřovací kód',
    'signing.identity.sent': 'Ověřovací kód odeslán.',
    'signing.identity.failed': 'Odeslání kódu se nezdařilo.',
    'signing.identity.dev': 'Dev kód',
    'create.shift.allow.leave': 'Povolit odhlášení',
    'create.shift.approval.type': 'Typ schválení',
    'create.shift.automatic': 'Automatické',
    'create.shift.create.from.preset': 'Vytvořit z předvolby',
    'create.shift.create.shift': 'Vytvořit směnu',
    'create.shift.description': 'Popis',
    'create.shift.disabled': 'Zakázáno',
    'create.shift.end.date': 'Datum ukončení',
    'create.shift.enter.preset.name': 'Zadejte název předvolby',
    'create.shift.fixed': 'Pevné',
    'create.shift.location': 'Pracoviště',
    'create.shift.manual': 'Ruční',
    'create.shift.max.attendees': 'Max účastníků',
    'create.shift.payment.amount': 'Výše platby',
    'create.shift.per.hour': 'Za hodinu',
    'create.shift.preset.name': 'Název předvolby',
    'create.shift.save.as.preset': 'Uložit jako předvolbu',
    'create.shift.save.as.preset.title': 'Uložit jako předvolbu',
    'create.shift.save.preset': 'Uložit předvolbu',
    'create.shift.select.location': 'Vybrat pracoviště',
    'create.shift.select.preset': 'Vybrat předvolbu',
    'create.shift.start.date': 'Datum začátku',
    'create.shift.title': 'Vytvořit novou směnu',
    'create.shift.back.to.presets': 'Zpět na předvolby',
    'create.shift.title.label': 'Napište název sem',
    'create.shift.description.placeholder': 'Napište sem…',
    'create.shift.created': 'Směna vytvořena.',
    'create.shift.created.with.assignees': 'Směna vytvořena a lidé přiřazeni.',
    'create.shift.created.draft': 'Směna uložena jako návrh. Publikujte, až budete připraveni.',
    'create.shift.save.as.draft': 'Uložit jako návrh',
    'create.shift.save.as.draft.hint': 'Zaměstnanci ji neuvidí, dokud nezveřejníte. Notifikace zatím neodejdou.',
    'create.shift.save.draft': 'Uložit návrh',
    'create.shift.saving.draft': 'Ukládám návrh…',
    'create.shift.draft.no.recurrence': 'Návrh nemůže být opakující se série. Vypněte opakování, nebo směnu rovnou publikujte.',
    'create.shift.preset.saved': 'Předvolba uložena.',
    'create.shift.add.people': 'Přidat lidi',
    'create.shift.add.people.search': 'Začněte psát pro vyhledání',
    'create.shift.add.people.hint': 'Vyberte pracoviště pro filtrování lidí přiřazených tam.',
    'create.shift.add.people.empty': 'Nejsou k dispozici žádní lidé k přiřazení.',
    'create.shift.add.people.no.match': 'Nikdo neodpovídá hledání.',
    'create.shift.add.group': 'Přidat ze skupiny',
    'create.shift.add.group.search': 'Začněte psát pro vyhledání',
    'create.shift.add.group.hint': 'Vyberte pracoviště pro filtrování členů skupiny, které lze přiřadit.',
    'create.shift.add.group.hint.filtered': 'Přidají se jen členové, kteří odpovídají vybrané pobočce (a pozici).',
    'create.shift.add.group.empty': 'Zatím nemáte žádné uživatelské skupiny.',
    'create.shift.add.group.no.match': 'Žádná skupina neodpovídá hledání.',
    'create.shift.add.group.no.members': 'V této skupině nejsou pro aktuální filtry žádní přiřaditelní členové.',
    'create.shift.add.group.members': 'Lze přiřadit {count} z {total}',
    'create.shift.remove.group': 'Odebrat skupinu',
    'create.shift.remove.person': 'Odebrat',
    'create.shift.pay.min.hourly': 'Minimální hodinová sazba je 134,40 Kč / hodinu.',
    'create.shift.pay.min.hourly.hint': 'Minimum 134,40 Kč / hodinu',
    'create.shift.pay.fixed.effective': '≈ {rate} Kč / hodinu',
    'create.shift.pay.fixed.too.low': 'Pevná platba musí odpovídat alespoň 134,40 Kč / hodinu za délku této směny.',
    'create.shift.use.employee.rate': 'Použít hodinovou sazbu zaměstnance, pokud je vyplněná',
    'create.shift.use.employee.rate.hint': 'Pokud má zaměstnanec vlastní sazbu, použije se ta. Jinak platí sazba směny výše. Sazba se zamkne v momentě přihlášení na směnu.',
    'create.shift.capacity.unreachable':
      'Tuto směnu nikdy nepůjde naplnit - kritériím (pracoviště/pozice) odpovídá jen {available} zaměstnanců, ale max. počet účastníků je {max}.',
    'create.shift.capacity.assignees.exceed':
      'Přiřadili jste {assigned} lidi, ale max. počet účastníků je jen {max}.',
    'create.shift.time.invalid': 'Konec musí být po začátku.',
    'create.shift.with.approval': 'Se schválením',
    'create.shift.without.approval': 'Bez schválení',
    'cv.manager.cancel': 'Zrušit',
    'cv.manager.click.to.upload': 'Klikněte pro nahrání',
    'cv.manager.delete': 'Smazat',
    'cv.manager.delete.confirm.message': 'Opravdu chcete smazat své CV?',
    'cv.manager.delete.confirm.title': 'Smazat CV',
    'cv.manager.delete.cv': 'Smazat CV',
    'cv.manager.drag.and.drop': 'Přetáhněte své CV sem',
    'cv.manager.havent.uploaded': 'Zatím jste nenahráli CV',
    'cv.manager.last.updated': 'Naposledy aktualizováno',
    'cv.manager.open.pdf.new.tab': 'Otevřít PDF v nové kartě',
    'cv.manager.pdf.only': 'Pouze PDF soubory',
    'cv.manager.selected.file': 'Vybraný soubor',
    'cv.manager.subtitle': 'Spravujte své CV a přihlášky na práci',
    'cv.manager.title': 'Správce CV',
    'cv.manager.upload': 'Nahrát',
    'cv.manager.upload.cv': 'Nahrát CV',
    'cv.manager.upload.description': 'Nahrajte své CV pro přihlášky na práci',
    'cv.manager.upload.new.cv': 'Nahrát nové CV',
    'cv.manager.upload.title': 'Nahrát CV',
    'cv.manager.your.cv': 'Vaše CV',
    'cv.updates': 'Aktualizace CV',
    'dashboard.hours.this.month': 'Hodiny tento měsíc',
    'dashboard.monthly.earnings': 'Měsíční výdělky',
    'dashboard.shifts.this.month': 'Směny tento měsíc',
    'edit.shift.title': 'Upravit směnu',
    'edit.shift.update.shift': 'Aktualizovat směnu',
    'employees.active': 'Aktivní',
    'employees.address': 'Adresa',
    'employees.cancel': 'Zrušit',
    'employees.copied': 'Zkopírováno!',
    'employees.copy': 'Kopírovat',
    'employees.delete.code': 'Smazat kód',
    'employees.edit.employee': 'Upravit zaměstnance',
    'employees.email': 'E-mail',
    'employees.expires': 'Vyprší',
    'employees.hourly.pay.rate': 'Hodinová sazba',
    'employees.not.eligible': 'Není způsobilý',
    'employees.notes': 'Poznámky',
    'employees.personal.info': 'Osobní údaje',
    'employees.phone': 'Telefon',
    'employees.positions': 'Pozice',
    'employees.workplaces': 'Pracoviště',
    'employees.no.workplaces': 'Žádná pracoviště nejsou k dispozici.',
    'employees.save': 'Uložit',
    'employees.sex': 'Pohlaví',
    'employees.start.date': 'Datum začátku',
    'employees.no.position': 'Bez pozice',
    'employees.pay.rate': 'Hodinová sazba',
    'pay.per.hour': '/h',
    'employees.status': 'Status',
    'employees.hired.date': 'Datum nástupu',
    'employees.min.hours.title': 'Minimální hodiny',
    'employees.min.hours.enable': 'Sledovat minimální naplánované hodiny',
    'employees.min.hours.amount': 'Minimální počet hodin',
    'employees.min.hours.period.week': 'Za týden',
    'employees.min.hours.period.month': 'Za měsíc',
    'employees.min.hours.week.short': 'týden',
    'employees.min.hours.save': 'Uložit minimální hodiny',
    'employees.min.hours.saved': 'Minimální hodiny uloženy.',
    'employees.min.hours.save.failed': 'Minimální hodiny se nepodařilo uložit.',
    'employees.min.hours.current': 'Naplánováno v tomto období',
    'employees.min.hours.history': 'Historie období',
    'employees.min.hours.open': 'otevřené',
    'employees.min.hours.card': 'Min. hodiny',
    'employees.min.hours.disclaimer':
      'Ompli vidí jen hodiny u této firmy. Hodiny u jiných zaměstnavatelů nevidí — odpovědnost za soulad se zákonem zůstává na firmě a zaměstnanci.',
    'employees.min.hours.need.contract':
      'Nejdřív nastavte aktivní podepsanou smlouvu (typ a případně sjednaný rozsah hodin), teprve potom jde nastavit minimum.',
    'employees.min.hours.go.contracts': 'Přejít na Smlouvy',
    'employees.min.hours.contract.type': 'Typ smlouvy',
    'employees.min.hours.agreed': 'Sjednáno',
    'employees.min.hours.confirm.title': 'Projekce překračuje roční strop DPP',
    'employees.min.hours.confirm.body':
      'Při plném plnění minima do konce roku by došlo k překročení limitu 300 h u DPP. Přesto uložit?',
    'employees.min.hours.confirm.save': 'Přesto uložit',
    'dashboard.min.hours.under.title': 'Zaměstnanci pod minimem',
    'dashboard.min.hours.under.empty.title': 'Všichni jsou v pořádku',
    'dashboard.min.hours.under.empty.desc':
      'Žádný zaměstnanec s nastaveným minimem teď nemá naplánováno méně hodin, než má mít.',
    'settings.min.hours.title': 'Minimální hodiny',
    'settings.min.hours.tooltip':
      'Po zapnutí mohou majitel a manažer nastavovat u zaměstnanců minimální naplánované hodiny a na Přehledu vidět, kdo je pod minimem.',
    'settings.min.hours.enable': 'Zapnout sledování minimálních hodin pro celou firmu',
    'employees.min.hours.feature.off':
      'Sledování minimálních hodin je pro firmu vypnuté. Zapněte ho v Nastavení → Funkce.',
    'employees.min.hours.go.settings': 'Otevřít Nastavení → Funkce',
    'filters': 'Filtry',
    'inactive': 'Neaktivní',
    'job.board.current.cv': 'Aktuální CV',
    'job.board.cv.description': 'Popis CV',
    'job.board.daily.attendance': 'Denní docházka',
    'job.board.last.updated': 'Naposledy aktualizováno',
    'job.board.manage.cv': 'Spravovat CV',
    'job.board.no.cv.uploaded': 'Žádné CV nahráno',
    'job.board.remove.job.listing': 'Odebrat nabídku práce',
    'job.board.view.cv': 'Zobrazit CV',
    'job.board.view.details': 'Zobrazit detaily',
    'job.board.your.cv': 'Vaše CV',
    'job.board.flexible.hours': 'Flexibilní hodiny',
    'job.board.hiring.role': 'Hledáme',
    'job.board.edit.job': 'Upravit nabídku',
    'job.board.save.changes': 'Uložit změny',
    'job.board.saving': 'Ukládám...',
    'job.board.posting': 'Zveřejňuji...',
    'job.board.cancel': 'Zrušit',
    'job.board.create.success': 'Nabídka vytvořena.',
    'job.board.edit.success': 'Nabídka aktualizována.',
    'job.board.edit.failed': 'Nepodařilo se aktualizovat nabídku.',
    'job.board.delete.success': 'Nabídka odstraněna.',
    'job.board.form.title': 'Název pozice',
    'job.board.form.image': 'Obrázek',
    'job.board.form.image.placeholder': 'https://example.com/image.jpg',
    'job.board.form.image.upload': 'Nahrát obrázek',
    'job.board.form.image.or.url': 'Nebo vložte URL obrázku',
    'job.board.form.image.hint': 'JPG, PNG, GIF nebo WebP · max 5 MB',
    'job.board.form.image.uploading': 'Nahrávám...',
    'job.board.form.image.uploaded': 'Obrázek nahrán.',
    'job.board.form.image.upload.failed': 'Nepodařilo se nahrát obrázek.',
    'job.board.form.image.remove': 'Odstranit obrázek',
    'job.board.form.location': 'Pracoviště',
    'job.board.form.start.date': 'Možný nástup',
    'job.board.form.hourly.pay': 'Hodinová sazba',
    'job.board.form.min.age': 'Minimální věk',
    'job.board.form.required': 'Počet lidí',
    'job.board.form.required.hint': 'Kolik lidí chcete na tuto pozici přijmout.',
    'job.board.form.employment.type': 'Typ úvazku',
    'job.board.form.no.business': 'Firma nebyla nalezena.',
    'job.board.form.create.failed': 'Nepodařilo se vytvořit nabídku.',
    'job.board.details.min.age': 'Minimální věk: {age}',
    'job.details.edit.subtitle': 'Upravte název, plat, lokaci a další údaje nabídky.',
    'job.details.edit.title': 'Název pozice',
    'job.details.edit.image': 'URL obrázku',
    'job.details.edit.start.date': 'Možný nástup',
    'job.details.edit.required': 'Počet lidí',
    'job.details.edit.location.required': 'Vyberte pracoviště.',
    'job.details.description.required': 'Zadejte popis práce.',
    'job.details.back': 'Zpět na nabídku práce',
    'job.details.not.found': 'Nabídka nenalezena',
    'job.details.meta.location': 'Lokace',
    'job.details.meta.type': 'Typ úvazku',
    'job.details.meta.min.age': 'Minimální věk',
    'job.details.meta.pay': 'Hodinová sazba',
    'job.details.meta.hiring': 'Hledáme',
    'job.details.hiring.employee': 'Zaměstnance',
    'job.details.hiring.manager': 'Manažera',
    'job.details.description': 'Popis práce',
    'job.details.posted.on': 'Zveřejněno',
    'job.details.apply.title': 'Přihlásit se',
    'job.details.apply.subtitle': 'Pošlete přihlášku a brzy se vám ozveme.',
    'job.details.apply.now': 'Přihlásit se',
    'job.details.apply.again': 'Druhá šance: Přihlásit znovu',
    'job.details.applying': 'Odesílám...',
    'job.details.apply.blocked': 'Na tuto pozici se už nemůžete přihlásit.',
    'job.details.apply.success': 'Přihláška odeslána.',
    'job.details.apply.failed': 'Nepodařilo se odeslat přihlášku.',
    'job.details.status.pending': 'Přihláška čeká',
    'job.details.status.approved': 'Přihláška schválena',
    'job.details.status.rejected': 'Přihláška zamítnuta',
    'job.details.about.company': 'O firmě {name}',
    'job.details.about.fallback': 'Zjistěte více o této firmě a o tom, jaké je v ní pracovat.',
    'job.details.view.company': 'Zobrazit firmu',
    'job.details.hiring.employee.option': 'Zaměstnanec',
    'job.details.hiring.manager.option': 'Manažer',
    'job.details.publisher':
      'Nabídku zveřejňuje firma s názvem {companyName}, IČO: {ico}, se sídlem na adrese: {street}, {city}, {zip}, {region}, {country}.',
    'job.details.publisher.contact':
      'V případě zájmu nás můžete kontaktovat na telefonu: {phone}, e-mailu: {email}, případně navštivte náš web: {website}.',
    'job.details.publisher.na': '-',
    'login.login.failed': 'Přihlášení selhalo',
    'login.network.error': 'Síťová chyba',
    'login.show.password': 'Zobrazit heslo',
    'managers.cancel': 'Zrušit',
    'managers.copied': 'Zkopírováno!',
    'managers.delete.invite': 'Smazat pozvánku',
    'managers.edit.manager': 'Upravit manažera',
    'managers.expires': 'Vyprší',
    'managers.hourly.pay.rate': 'Hodinová sazba',
    'managers.save': 'Uložit',
    'messages.failed.fetch.contacts': 'Nepodařilo se načíst kontakty',
    'messages.failed.send': 'Nepodařilo se odeslat',
    'messages.media': 'Média',
    'messages.media.selected': 'Média vybrána',
    'messages.replying.to': 'Odpovídáte na',
    'messages.send': 'Odeslat',
    'messages.type.message': 'Napište svou zprávu...',
    'messages.your.browser.no.video': 'Váš prohlížeč nepodporuje video',
    'my.contracts.active': 'Aktivní',
    'my.contracts.all': 'Vše',
    'my.contracts.awaiting.approval': 'Čeká na schválení',
    'my.contracts.choose.pdf': 'Vybrat PDF',
    'my.contracts.contract': 'Smlouva',
    'my.contracts.contract.history': 'Historie smluv',
    'my.contracts.download': 'Stáhnout',
    'my.contracts.download.signed': 'Stáhnout podepsanou',
    'my.contracts.duration': 'Doba trvání',
    'my.contracts.expiry': 'Vypršení',
    'my.contracts.failed.download': 'Nepodařilo se stáhnout',
    'my.contracts.failed.upload': 'Nepodařilo se nahrát',
    'my.contracts.from': 'Od',
    'my.contracts.inactive': 'Neaktivní',
    'my.contracts.no.history': 'Žádná historie smluv',
    'my.contracts.no.history.desc': 'Podepsané a ukončené smlouvy se zobrazí zde.',
    'my.contracts.no.unsigned': 'Žádné nepodepsané smlouvy',
    'my.contracts.no.unsigned.desc': 'Až vám zaměstnavatel pošle smlouvu, objeví se tady k podpisu.',
    'my.contracts.failed.load': 'Nepodařilo se načíst smlouvy.',
    'my.contracts.please.download.sign': 'Prosím stáhněte a podepište',
    'my.contracts.reason': 'Důvod',
    'my.contracts.sending.signed': 'Odesílání podepsané',
    'my.contracts.signed.uploaded': 'Podepsaná smlouva nahrána',
    'my.contracts.terminated.by.owner': 'Ukončeno vlastníkem',
    'my.contracts.title': 'Moje smlouvy',
    'my.contracts.until': 'Do',
    'my.shifts.subtitle': 'Spravujte své pracovní směny',
    'my.shifts.title': 'Moje směny',
    'positions.any': 'Jakékoliv',
    'positions.confirm.delete': 'Potvrdit smazání',
    'positions.female': 'Žena',
    'positions.gender.optional': 'Pohlaví (volitelné)',
    'positions.male': 'Muž',
    'positions.max.people.optional': 'Max lidí (volitelné)',
    'positions.no.positions': 'Žádné pozice',
    'positions.other': 'Jiné',
    'positions.position.name': 'Název pozice',
    'positions.save': 'Uložit',
    'presets.cancel': 'Zrušit',
    'presets.create.preset': 'Vytvořit předvolbu',
    'presets.create.shift.preset': 'Vytvořit předvolbu směny',
    'presets.end.hour': 'Koncová hodina',
    'presets.example.cashier': 'Příklad: Pokladní',
    'presets.example.morning.shift': 'Příklad: Ranní směna',
    'presets.failed.create.preset': 'Nepodařilo se vytvořit předvolbu',
    'presets.created': 'Předvolba vytvořena.',
    'presets.updated': 'Předvolba uložena.',
    'presets.deleted': 'Předvolba smazána.',
    'presets.delete.confirm': 'Opravdu chcete smazat tuto předvolbu?',
    'presets.empty.title': 'Zatím žádné předvolby směn',
    'presets.empty.description':
      'Vytvořte předvolbu pro rychlé vyplnění detailů při tvorbě nových směn.',
    'presets.edit.shift.preset': 'Upravit předvolbu směny',
    'presets.saving': 'Ukládání…',
    'presets.failed.update.preset': 'Nepodařilo se uložit předvolbu',
    'presets.failed.fetch.preset': 'Nepodařilo se načíst předvolbu',
    'presets.failed.delete.preset': 'Nepodařilo se smazat předvolbu',
    'presets.failed.fetch.locations': 'Nepodařilo se načíst pracoviště',
    'presets.failed.fetch.presets': 'Nepodařilo se načíst předvolby',
    'presets.fixed': 'Pevné',
    'presets.go.back': 'Zpět',
    'presets.hourly': 'Hodinové',
    'presets.location': 'Pracoviště',
    'presets.max.attendees': 'Max účastníků',
    'presets.no.business': 'Žádná firma',
    'presets.pay.amount': 'Výše platby',
    'presets.pay.type': 'Typ platby',
    'presets.preset.name': 'Název předvolby',
    'presets.select.location': 'Vybrat pracoviště',
    'presets.shift.name': 'Název směny',
    'presets.start.hour': 'Počáteční hodina',
    'presets.subtitle': 'Vytvářejte a spravujte předvolby směn',
    'presets.title': 'Předvolby směn',
    'presets.use.preset': 'Použít předvolbu',
    'register.basic.info': 'Základní informace',
    'register.confirm.password': 'Potvrdit heslo',
    'register.details': 'Detaily',
    'register.email': 'E-mail',
    'register.first.name': 'Jméno',
    'register.last.name': 'Příjmení',
    'register.network.error': 'Síťová chyba',
    'register.password': 'Heslo',
    'register.passwords.dont.match': 'Hesla se neshodují',
    'register.please.enter.company': 'Prosím zadejte název firmy',
    'register.please.fill.all.fields': 'Prosím vyplňte všechna pole',
    'register.please.select.role': 'Prosím vyberte roli',
    'register.registration.failed': 'Registrace selhala',
    'register.registration.success': 'Účet vytvořen.',
    'register.oauth.linked': 'Pokračujte s účtem Google/Microsoft/Apple',
    'register.oauth.calendar.hint': 'Google/Microsoft kalendář se po registraci propojí. Apple Kalendář lze připojit přes ICS v Nastavení.',
    'register.oauth.calendar.ready': 'Účet vytvořen a kalendář propojen.',
    'register.oauth.expired': 'OAuth relace vypršela. Přihlaste se znovu přes Google, Microsoft nebo Apple.',
    'register.review': 'Kontrola',
    'register.select.role': 'Vybrat roli',
    'register.username': 'Uživatelské jméno',
    'register.back': 'Zpět',
    'register.registering': 'Registruji…',
    'register.create.account': 'Vytvořit účet',
    'register.details.employer': 'Osobní a firemní údaje',
    'register.details.employee': 'Osobní údaje',
    'register.details.manager': 'Osobní údaje',
    'register.section.personal': 'Osobní',
    'register.section.company': 'Firma',
    'register.sex': 'Pohlaví',
    'register.sex.select': 'Vyberte…',
    'register.sex.male': 'Muž',
    'register.sex.female': 'Žena',
    'register.sex.other': 'Jiné',
    'register.phone': 'Telefon',
    'register.phone.search': 'Hledat zemi…',
    'register.phone.search.none': 'Země nenalezena',
    'register.personal.address': 'Adresa bydliště',
    'register.invite.section': 'Pozvánka do firmy',
    'register.invite.question': 'Máte už pozvánkový kód do nějaké společnosti?',
    'register.invite.yes': 'Ano',
    'register.invite.no': 'Ne',
    'register.invite.code': 'Pozvánkový kód',
    'register.invite.placeholder': 'Zadejte pozvánkový kód',
    'register.invite.hint': 'Požádejte zaměstnavatele o kód - připojit se můžete i později v Moje práce.',
    'register.invite.from.link': 'Předvyplněno z odkazu pozvánky',
    'register.invite.checking': 'Kontrola pozvánkového kódu…',
    'register.invite.invalid': 'Neplatný nebo vypršelý pozvánkový kód',
    'register.invite.required': 'Zadejte pozvánkový kód',
    'register.invite.joining': 'Připojíte se k {name}',
    'register.invite.joined': 'Připojeno k {name}.',
    'register.invite.join.failed': 'Účet byl vytvořen, ale připojení k firmě selhalo. Kód můžete použít později.',
    'register.avatar.upload': 'Nahrát profilovou fotku',
    'register.avatar.hint': 'Volitelné · JPG nebo PNG',
    'register.logo.upload': 'Nahrát logo firmy',
    'register.logo.hint': 'Volitelné · JPG nebo PNG',
    'register.please.fill.personal': 'Vyplňte pohlaví, telefon a adresu bydliště.',
    'register.please.fill.legal': 'Vyplňte název společnosti a IČO (ARES).',
    'register.please.fill.business.address': 'Vyplňte sídlo / adresu firmy.',
    'register.review.role': 'Role',
    'register.review.name': 'Jméno',
    'register.review.selected': 'Vybráno',
    'register.panel.title.0': 'Vítejte v Ompli',
    'register.panel.desc.0': 'Vyberte roli a nastavte si pracovní prostor v několika krocích.',
    'register.panel.title.1': 'Váš účet v bezpečí',
    'register.panel.desc.1': 'Vytvořte přihlašovací údaje pro směny, nabídky práce i zprávy týmu.',
    'register.panel.title.2': 'Řekněte nám o sobě',
    'register.panel.desc.2': 'Profil a firemní údaje zajistí přesné rozvrhy, smlouvy i mzdy.',
    'register.panel.title.3': 'Připraveno ke startu',
    'register.panel.desc.3': 'Jednou zkontrolujte údaje - a rovnou přejděte do dashboardu Ompli.',
    'register.panel.footer': 'Jednoduché plánování směn pro moderní týmy',
    'shift.details.not.found': 'Detaily směny nenalezeny',
    'shift.details.title': 'Detail směny',
    'shift.details.attendees': 'Účastníci',
    'shift.details.no.attendees': 'Zatím žádní účastníci',
    'shift.details.start': 'Začátek',
    'shift.details.end': 'Konec',
    'shift.details.remove': 'Odebrat',
    'shift.details.remove.title': 'Odebrat zaměstnance',
    'shift.details.remove.confirm': 'Opravdu chcete odebrat tohoto zaměstnance ze směny?',
    'shift.details.remove.success': 'Zaměstnanec byl odebrán ze směny.',
    'shift.details.remove.failed': 'Nepodařilo se odebrat zaměstnance.',
    'shift.details.delete.title': 'Smazat směnu',
    'shift.details.delete.confirm': 'Opravdu chcete smazat tuto směnu? Tuto akci nelze vrátit.',
    'shift.details.delete.success': 'Směna byla smazána.',
    'shift.details.delete.failed': 'Nepodařilo se smazat směnu.',
    'shift.details.deleting': 'Mazání…',
    'shift.details.load.failed': 'Nepodařilo se načíst směnu.',
    'shift.details.attend': 'Zúčastnit se',
    'shift.details.request.attendance': 'Požádat o účast',
    'shift.details.attend.success': 'Žádost o účast odeslána.',
    'shift.details.attend.failed': 'Nepodařilo se požádat o účast.',
    'shift.details.request.leave': 'Požádat o odhlášení',
    'shift.details.leave': 'Odhlásit se',
    'shift.details.leave.failed': 'Nepodařilo se odhlásit ze směny.',
    'shift.details.swap.title': 'Nabídnout výměnu',
    'shift.details.swap.hint': 'Pošlete směnu kolegovi do chatu, nebo zkopírujte sdílitelný odkaz.',
    'shift.details.swap.to.colleague': 'Poslat do chatu',
    'shift.details.swap.copy.link': 'Kopírovat odkaz',
    'shift.details.leave.for': 'Žádáte o odhlášení ze směny: {title}',
    'shift.details.leave.reason.placeholder': 'Důvod (volitelné)',
    'shift.details.submit.leave': 'Odeslat žádost',
    'shift.details.processing': 'Zpracování…',
    'shift.details.save.success': 'Směna uložena.',
    'shift.details.save.failed': 'Nepodařilo se uložit směnu.',
    'shifts.attend': 'Zúčastnit se',
    'shifts.cancelled': 'Zrušeno',
    'shifts.checking.contracts': 'Kontrola smluv',
    'shifts.contact.boss': 'Kontaktovat šéfa',
    'shifts.contact.owner': 'Kontaktovat vlastníka',
    'shifts.contracts.enabled.message': 'Smlouvy jsou povoleny pro tuto firmu',
    'shifts.go.to.contracts': 'Přejít na smlouvy',
    'shifts.no.active.contract': 'Žádná aktivní smlouva',
    'shifts.payment.fixed': 'Pevná platba',
    'shifts.shifts.on': 'Směny dne',
    'shifts.side.month.toggle': 'Tento měsíc',
    'shifts.side.month.title': 'Směny tento měsíc',
    'shifts.no.shifts.for.month': 'Tento měsíc nejsou žádné směny',
    'shifts.side.week.toggle': 'Tento týden',
    'shifts.side.week.title': 'Směny tento týden',
    'shifts.no.shifts.for.week': 'Tento týden nejsou žádné směny',
    'shifts.status.approved': 'Schváleno',
    'shifts.status.full': 'Plné',
    'shifts.status.ongoing': 'Probíhající',
    'shifts.status.open': 'Otevřené',
    'shifts.status.past': 'Minulé',
    'shifts.status.pending': 'Čekající',
    'shifts.status.rejected': 'Zamítnuté',
    'shifts.view.details': 'Zobrazit detaily',
    'shifts.you.are.inactive': 'Jste neaktivní',
    'workplaces.add.location.button': 'Přidat pracoviště',
    'workplaces.add.new.location': 'Přidat nové pracoviště',
    'workplaces.address': 'Adresa',
    'workplaces.address.placeholder': 'Zadejte adresu',
    'workplaces.edit.location': 'Upravit pracoviště',
    'workplaces.location.name': 'Název pracoviště',
    'workplaces.location.name.placeholder': 'Zadejte název pracoviště',
    'workplaces.no.locations': 'Žádná pracoviště',
    'workplaces.save.changes': 'Uložit změny',
    'workplaces.subtitle.employee': 'Zobrazit vaše přiřazená pracoviště',
    'workplaces.subtitle.manager': 'Spravovat pracoviště pro váš tým',
    'workplaces.created': 'Pracoviště vytvořeno.',
    'workplaces.updated': 'Pracoviště aktualizováno.',
    'workplaces.deleted': 'Pracoviště smazáno.',
    'workplaces.confirm.delete': 'Smazat toto pracoviště?',
    'workplaces.form.subtitle': 'Vyplňte níže uvedené údaje pro nastavení pracoviště',
    'workplaces.section.basic': 'Základní informace',
    'workplaces.section.attendance': 'Docházka',
    'workplaces.section.address': 'Adresa',
    'workplaces.section.employees': 'Zaměstnanci',
    'workplaces.active': 'Aktivní',
    'workplaces.active.sub': 'Zaměstnanci mohou být přiřazeni k tomuto pracovišti',
    'workplaces.clock.in.before': 'Kolik minut před začátkem směny se může zaměstnanec přihlásit',
    'workplaces.break.entitlement': 'Nárok na přestávku',
    'workplaces.break.after.minutes': 'Po (odpracované minuty)',
    'workplaces.break.duration.minutes': 'Přestávka (celkem minuty)',
    'workplaces.early.arrival': 'Předčasný příchod na směnu',
    'workplaces.late.arrival': 'Pozdní příchod na směnu',
    'workplaces.early.departure': 'Předčasný odchod ze směny',
    'workplaces.late.departure': 'Pozdní odchod ze směny',
    'workplaces.record.exact.time': 'Zaznamenat přesný čas příchodu',
    'workplaces.record.exact.departure': 'Zaznamenat přesný čas odchodu',
    'workplaces.round.breaks': 'Zaokrouhlování přestávek',
    'workplaces.no.employees.available': 'V tomto businessu nebyli nalezeni žádní zaměstnanci.',
    'workplaces.add.people.search': 'Hledat lidi…',
    'workplaces.add.people.no.match': 'Nikdo neodpovídá',
    'workplaces.remove.person': 'Odebrat osobu',
    'workplaces.form.clock.in': 'Přihlášení před',
    'workplaces.form.break.after': 'Přestávka po',
    'workplaces.form.break.duration': 'Délka přestávky',
    'workplaces.empty.desc': 'Přidejte první pracoviště, aby zaměstnanci našli své směny.',
    'home.nav.features': 'Funkce',
    'home.nav.pricing': 'Ceník',
    'home.nav.about': 'O nás',
    'home.nav.contact': 'Kontakt',
    'home.nav.help': 'Nápověda',
    'home.auth.sign.in': 'Přihlásit se',
    'home.auth.get.started': 'Začít',
    'home.hero.title.line1': 'Moderní správa směn',
    'home.hero.title.line2': 'Pro rostoucí týmy',
    'home.hero.subtitle': 'Zjednodušte plánování směn, smlouvy a týmovou komunikaci - vše v jedné inteligentní platformě pro zaměstnavatele i zaměstnance.',
    'home.hero.try.free': 'Vyzkoušet zdarma',
    'home.hero.watch.demo': 'Sledovat demo',
    'home.features.easy.shift': 'Snadné plánování směn',
    'home.features.ai.powered': 'Podpora AI',
    'home.features.user.friendly': 'Přehledné rozhraní',
    'home.features.multiple.languages': 'Více jazyků',
    'home.section.experience.title': 'Vše, co potřebujete',
    'home.section.experience.subtitle': 'Výkonné funkce pro jednoduchou správu pracovní síly',
    'home.features.smart.scheduling.title': 'Chytré plánování',
    'home.features.smart.scheduling.description': 'Vytvářejte a spravujte směny pomocí šablon, opakujících se rozvrhů a aktualizací v reálném čase.',
    'home.features.team.communication.title': 'Týmová komunikace',
    'home.features.team.communication.description': 'Vestavěné zprávy udrží váš tým v kontaktu a v obraze.',
    'home.features.analytics.dashboard.title': 'Analytický dashboard',
    'home.features.analytics.dashboard.description': 'Sledujte docházku, hodiny a výkon pomocí přehledných dashboardů.',
    'home.features.contract.management.title': 'Správa smluv',
    'home.features.contract.management.description': 'Vytvářejte, odesílejte a sledujte pracovní smlouvy digitálně.',
    'home.features.leave.management.title': 'Správa odhlášení',
    'home.features.leave.management.description': 'Řešte žádosti o odhlášení a schvalování docházky bez námahy.',
    'home.features.job.board.title': 'Nabídka práce',
    'home.features.job.board.description': 'Inzerujte pozice a spravujte přihlášky přímo v platformě.',
    'home.features.customisable.title': 'Přizpůsobitelné',
    'home.features.customisable.description': 'Přizpůsobte platformu potřebám a procesům vaší firmy.',
    'home.features.easy.to.use.title': 'Snadné použití',
    'home.features.easy.to.use.description': 'Intuitivní rozhraní, které si váš tým oblíbí od prvního dne.',
    'home.features.multiple.workplaces.title': 'Více pracovišť',
    'home.features.multiple.workplaces.description': 'Spravujte více poboček z jednoho dashboardu.',
    'home.features.employee.friendly.title': 'Přívětivé pro zaměstnance',
    'home.features.employee.friendly.description': 'Zaměstnanci mohou procházet směny, sledovat výdělky a spravovat svůj rozvrh.',
    'home.section.testimonials.title': 'Důvěřují nám týmy',
    'home.section.testimonials.subtitle': 'Co říkají majitelé firem a manažeři o Ompli',
    'home.testimonials.track.hours.quote': 'Ompli nám výrazně usnadnilo sledování hodin a správu týmu na více pobočkách.',
    'home.testimonials.shift.presets.quote': 'Šablony směn nám ušetří hodiny každý týden. Plánování nikdy nebylo jednodušší.',
    'home.testimonials.costs.less.quote': 'Výrazně jsme snížili náklady na plánování a zároveň zlepšili spokojenost týmu.',
    'home.section.all.work.title': 'Veškerá práce na jednom místě',
    'home.section.all.work.subtitle': 'Od dashboardů po smlouvy - vše, co váš tým potřebuje',
    'home.features.dashboard.title': 'Dashboard',
    'home.features.dashboard.description': 'Kompletní přehled o vašem podnikání na první pohled.',
    'home.features.team.management.title': 'Správa týmu',
    'home.features.team.management.description': 'Snadno spravujte zaměstnance, manažery a role.',
    'home.features.schedule.view.title': 'Přehled rozvrhu',
    'home.features.schedule.view.description': 'Vizuální kalendář všech směn a dostupnosti týmu.',
    'home.features.analytics.title': 'Analytika',
    'home.features.analytics.description': 'Datové přehledy pro optimalizaci pracovní síly.',
    'home.features.messages.title': 'Zprávy',
    'home.features.messages.description': 'Komunikace v reálném čase mezi členy týmu.',
    'home.features.contracts.title': 'Smlouvy',
    'home.features.contracts.description': 'Digitální správa smluv od vytvoření po podpis.',
    'home.image.placeholder': 'Snímek obrazovky brzy',
    'home.section.how.it.works.title': 'Jak to funguje',
    'home.section.how.it.works.subtitle': 'Začněte během minut, ne dnů',
    'home.steps.sign.up.title': 'Registrace',
    'home.steps.sign.up.description': 'Vytvořte firemní účet za méně než 2 minuty.',
    'home.steps.invite.team.title': 'Pozvěte tým',
    'home.steps.invite.team.description': 'Pošlete pozvánky zaměstnancům a manažerům e-mailem.',
    'home.steps.configure.settings.title': 'Nastavte platformu',
    'home.steps.configure.settings.description': 'Nastavte pracoviště, pozice a šablony směn.',
    'home.steps.start.managing.title': 'Začněte spravovat',
    'home.steps.start.managing.description': 'Vytvářejte směny, spravujte smlouvy a rozvíjejte podnikání.',
    'home.section.pricing.title': 'Jednoduchý a transparentní ceník',
    'home.section.pricing.subtitle': 'Začněte zdarma, rostěte s námi',
    'home.pricing.free.name': 'Zdarma',
    'home.pricing.free.description': 'Ideální pro malé týmy na začátku',
    'home.pricing.features.up.to.5.employees': 'Až 5 zaměstnanců',
    'home.pricing.features.basic.scheduling': 'Základní plánování',
    'home.pricing.features.team.messaging': 'Týmové zprávy',
    'home.pricing.features.email.support': 'E-mailová podpora',
    'home.pricing.features.job.board': 'Přístup k nabídce práce',
    'home.pricing.button.get.started': 'Začít',
    'home.pricing.professional.name': 'Professional',
    'home.pricing.period.credits.user.month': 'kreditů/uživatel/měsíc',
    'home.pricing.professional.description': 'Pro rostoucí firmy s větším týmem',
    'home.pricing.features.everything.from.free': 'Vše ze Zdarma',
    'home.pricing.features.unlimited.employees': 'Neomezený počet zaměstnanců',
    'home.pricing.features.contract.management': 'Správa smluv',
    'home.pricing.features.priority.support': 'Prioritní podpora',
    'home.pricing.features.credit.based.billing': 'Fakturace na kredity',
    'home.pricing.features.referral.system': 'Referral systém',
    'home.pricing.button.upgrade.to.pro': 'Přejít na Pro',
    'home.pricing.enterprise.name': 'Enterprise',
    'home.pricing.period.contact.us': 'kontaktujte nás',
    'home.pricing.enterprise.description': 'Řešení na míru pro velké organizace',
    'home.pricing.features.everything.from.professional': 'Vše z Professional',
    'home.pricing.features.dedicated.support': 'Vyhrazená podpora',
    'home.pricing.features.bulk.discounts': 'Hromadné slevy',
    'home.pricing.features.best.for.big.companies': 'Nejlepší pro velké firmy',
    'home.pricing.features.your.logo.on.website': 'Vaše logo na našem webu',
    'home.pricing.button.contact.sales': 'Kontaktovat obchod',
    'home.pricing.most.popular': 'Nejoblíbenější',
    'home.pricing.ready.to.start': 'Připraveni začít?',
    'home.pricing.try.ompli.free': 'Vyzkoušejte Ompli pro váš tým zdarma.',
    'home.section.contact.title': 'Kontaktujte nás',
    'home.section.contact.subtitle': 'Máte dotazy? Rádi vás uslyšíme.',
    'home.contact.form.first.name': 'Jméno',
    'home.contact.form.placeholder.first.name': 'Jan',
    'home.contact.form.last.name': 'Příjmení',
    'home.contact.form.placeholder.last.name': 'Novák',
    'home.contact.form.email': 'E-mail',
    'home.contact.form.placeholder.email': 'jan@firma.cz',
    'home.contact.form.company.name.label': 'Název firmy',
    'home.contact.form.company.name.placeholder': 'Vaše firma',
    'home.contact.form.company.size.label': 'Velikost firmy',
    'home.contact.form.company.size.placeholder': 'Vyberte velikost',
    'home.contact.form.company.size.1.10': '1–10 zaměstnanců',
    'home.contact.form.company.size.11.50': '11–50 zaměstnanců',
    'home.contact.form.company.size.51.200': '51–200 zaměstnanců',
    'home.contact.form.company.size.201.1000': '201–1000 zaměstnanců',
    'home.contact.form.company.size.1000.plus': '1000+ zaměstnanců',
    'home.contact.form.subject.label': 'Předmět',
    'home.contact.form.subject.placeholder': 'Vyberte předmět',
    'home.contact.form.subject.general': 'Obecný dotaz',
    'home.contact.form.subject.sales': 'Obchodní dotaz',
    'home.contact.form.subject.support': 'Technická podpora',
    'home.contact.form.subject.partnership': 'Partnerství',
    'home.contact.form.subject.other': 'Jiné',
    'home.contact.form.message.label': 'Zpráva',
    'home.contact.form.message.placeholder': 'Napište nám, jak vám můžeme pomoci...',
    'home.contact.form.updates.checkbox': 'Posílejte mi novinky a aktualizace produktu',
    'home.contact.form.success': 'Zpráva byla úspěšně odeslána!',
    'home.contact.form.send': 'Odeslat zprávu',
    'home.footer.description': 'Ompli je moderní platforma pro správu směn, která firmám pomáhá plánovat, komunikovat a růst.',
    'home.footer.company.title': 'Společnost',
    'home.footer.about.us': 'O nás',
    'home.footer.careers': 'Kariéra',
    'home.footer.blog': 'Blog',
    'home.footer.pricing': 'Ceník',
    'home.footer.product.title': 'Produkt',
    'home.footer.scheduling': 'Plánování',
    'home.footer.contract': 'Smlouvy',
    'home.footer.analytics': 'Analytika',
    'home.footer.messaging': 'Zprávy',
    'home.footer.resources.title': 'Zdroje',
    'home.footer.help.center': 'Centrum nápovědy',
    'home.footer.api.docs': 'API dokumentace',
    'home.footer.tutorials': 'Návody',
    'home.footer.templates': 'Šablony',
    'home.footer.contact.title': 'Kontakt',
    'home.footer.copyright': '© 2025 Ompli. Všechna práva vyhrazena.',
    'login.quick.login.admin': 'Rychlé přihlášení jako Admin',
    'admin.badge': 'Platform Admin',
    'admin.nav.dashboard': 'Přehled',
    'admin.nav.users': 'Uživatelé',
    'admin.nav.businesses': 'Firmy',
    'admin.nav.credits': 'Kredity',
    'admin.nav.contacts': 'Kontakty',
    'admin.nav.activity': 'Aktivita',
    'admin.nav.contracts': 'Smlouvy',
    'admin.nav.jobs': 'Inzeráty',
    'admin.nav.blog': 'Blog',
    'admin.blog.title': 'Blog',
    'admin.blog.subtitle': 'Pište a publikujte články Magazínu (markdown, stejný render jako nápověda)',
    'admin.blog.new': 'Nový článek',
    'admin.blog.edit': 'Upravit',
    'admin.blog.delete': 'Smazat',
    'admin.blog.view': 'Zobrazit',
    'admin.blog.empty.title': 'Zatím žádné články',
    'admin.blog.empty.description': 'Vytvořte první článek Magazínu pro veřejný blog.',
    'admin.blog.search.empty': 'Žádný článek neodpovídá hledání',
    'admin.blog.load.error': 'Nepodařilo se načíst články',
    'admin.blog.save.success': 'Článek uložen jako koncept',
    'admin.blog.save.published': 'Článek publikován',
    'admin.blog.save.error': 'Nepodařilo se uložit článek',
    'admin.blog.delete.confirm': 'Opravdu smazat tento článek?',
    'admin.blog.delete.success': 'Článek smazán',
    'admin.blog.delete.error': 'Nepodařilo se smazat článek',
    'admin.blog.validation.title': 'Nadpis je povinný',
    'admin.blog.validation.body': 'Text článku je povinný',
    'admin.blog.status.draft': 'Koncept',
    'admin.blog.status.published': 'Publikováno',
    'admin.blog.editor.new': 'Nový článek',
    'admin.blog.editor.edit': 'Upravit článek',
    'admin.blog.editor.preview': 'Náhled',
    'admin.blog.editor.editMode': 'Upravit',
    'admin.blog.field.title': 'Nadpis',
    'admin.blog.field.slug': 'URL slug',
    'admin.blog.field.excerpt': 'Perex (karta)',
    'admin.blog.field.lead': 'Úvod',
    'admin.blog.field.coverUrl': 'URL cover obrázku',
    'admin.blog.field.coverCaption': 'Popisek coveru',
    'admin.blog.field.body': 'Text (markdown)',
    'admin.blog.action.draft': 'Uložit koncept',
    'admin.blog.action.publish': 'Publikovat',
    'admin.dashboard.title': 'Přehled platformy',
    'admin.dashboard.subtitle': 'Metriky napříč tenanty a rychlé ops akce',
    'admin.dashboard.low.balance': 'Nízký zůstatek kreditů',
    'admin.dashboard.low.balance.empty': 'Žádné firmy s nízkým zůstatkem.',
    'admin.stats.users': 'Uživatelé',
    'admin.stats.businesses': 'Firmy',
    'admin.stats.credits.purchased': 'Nakoupené kredity',
    'admin.stats.credits.deducted': 'Stržené kredity',
    'admin.stats.credits.balance': 'Celkový zůstatek',
    'admin.stats.credits.admin': 'Admin úpravy',
    'admin.stats.contacts.new': 'Nové kontakty',
    'admin.stats.suspended.users': 'Pozastavení uživatelé',
    'admin.stats.suspended.businesses': 'Pozastavené firmy',
    'admin.stats.deduction.today': 'Stržení dnes',
    'admin.stats.deduction.none': 'Nespuštěno',
    'admin.stats.employees': 'Zaměstnanci',
    'admin.stats.managers': 'Manažeři',
    'admin.stats.locations': 'Pracoviště',
    'admin.ops.deduction.run': 'Spustit denní stržení',
    'admin.ops.deduction.success': 'Denní stržení kreditů dokončeno.',
    'admin.users.title': 'Uživatelé',
    'admin.users.detail.title': 'Detail uživatele',
    'admin.users.subtitle': 'Vyhledávání a správa všech účtů na platformě',
    'admin.users.search.placeholder': 'Email, jméno, username…',
    'admin.users.empty': 'Žádní uživatelé',
    'admin.users.empty.desc': 'Zkuste jiný filtr nebo hledání.',
    'admin.users.label': 'uživatelů',
    'admin.users.joined': 'Registrace',
    'admin.users.businesses': 'Firmy',
    'admin.users.no.business': 'Žádné členství ve firmě',
    'admin.users.more.businesses': '+{n} dalších',
    'admin.users.edit': 'Upravit profil',
    'admin.filter.business': 'Firma',
    'admin.users.stats.title': 'Pracovní statistiky',
    'admin.users.stats.earnings': 'Výdělky',
    'admin.users.stats.earnings.info':
      'Součet výdělků ze schválených směn ve zvoleném období (z evidence odpracovaných hodin).',
    'admin.users.stats.shifts': 'Směny',
    'admin.users.stats.shifts.info':
      'Počet schválených směn odpracovaných ve zvoleném období.',
    'admin.users.stats.hours': 'Hodiny',
    'admin.users.stats.hours.info':
      'Součet odpracovaných hodin ze schválených směn (přestávky se odečítají, pokud jsou nastavené).',
    'admin.users.stats.vsPrevious': 'oproti předchozímu období',
    'admin.users.stats.avgRate': 'Prům. {rate}/h',
    'admin.users.stats.trendTitle': 'Výdělky a hodiny',
    'admin.users.stats.byBusiness': 'Výdělky podle firmy',
    'admin.users.stats.empty': 'V tomto období nejsou schválené směny.',
    'admin.users.activity.title': 'Aktivita v aplikaci',
    'admin.users.activity.logins': 'Přihlášení',
    'admin.users.activity.pageViews': 'Navštívené stránky',
    'admin.users.activity.actions': 'Akce',
    'admin.users.activity.uniquePages': 'Unikátní stránky',
    'admin.users.activity.events': 'Události',
    'admin.users.activity.trend': 'Přihlášení, stránky a akce',
    'admin.users.activity.byHour': 'Aktivita podle hodiny',
    'admin.users.activity.byWeekday': 'Aktivita podle dne',
    'admin.users.activity.devices': 'Zařízení',
    'admin.users.activity.browsers': 'Prohlížeče',
    'admin.users.activity.topPages': 'Nejnavštěvovanější stránky',
    'admin.users.activity.recentLogins': 'Poslední přihlášení',
    'admin.users.activity.recentActions': 'Poslední akce v aplikaci',
    'admin.users.activity.empty': 'V tomto období zatím nejsou data o přihlášení/zařízení.',
    'admin.users.activity.emptyPages':
      'Zatím žádné page views. Data se začnou plnit, jakmile uživatel prochází aplikaci.',
    'admin.users.activity.emptyActions': 'V tomto období žádné auditované akce.',
    'admin.users.activity.weekday.0': 'Ne',
    'admin.users.activity.weekday.1': 'Po',
    'admin.users.activity.weekday.2': 'Út',
    'admin.users.activity.weekday.3': 'St',
    'admin.users.activity.weekday.4': 'Čt',
    'admin.users.activity.weekday.5': 'Pá',
    'admin.users.activity.weekday.6': 'So',
    'admin.users.actions': 'Akce s účtem',
    'admin.users.saved': 'Uživatel uložen.',
    'admin.users.suspended': 'Uživatel pozastaven.',
    'admin.users.unsuspended': 'Pozastavení zrušeno.',
    'admin.users.suspend': 'Pozastavit uživatele',
    'admin.users.unsuspend': 'Zrušit pozastavení',
    'admin.users.reset.password': 'Reset hesla',
    'admin.users.password.reset': 'Dočasné heslo vygenerováno.',
    'admin.users.temp.password': 'Dočasné heslo',
    'admin.users.confirm.promote': 'Povýšit tohoto uživatele na SUPER_ADMIN?',
    'admin.users.memberships': 'Členství',
    'admin.users.memberships.empty': 'Žádná členství ve firmách.',
    'admin.users.memberships.remove': 'Odebrat členství',
    'admin.users.memberships.remove.confirm':
      'Odebrat tohoto uživatele z {name}? Ztratí přístup k této firmě.',
    'admin.users.memberships.remove.ownerBlocked':
      'Vlastnictví tady nejde odebrat. Nejdřív převeďte firmu na jiného vlastníka.',
    'admin.users.memberships.removed': 'Členství odebráno.',
    'admin.users.oauth': 'OAuth identity',
    'admin.users.oauth.empty': 'Žádné propojené OAuth účty.',
    'admin.users.oauth.remove': 'Odebrat OAuth identitu',
    'admin.users.oauth.remove.confirm': 'Odpojit {provider} od tohoto uživatele?',
    'admin.users.oauth.removed': 'OAuth identita odebrána.',
    'admin.owner.of': 'Majitel',
    'admin.businesses.title': 'Firmy',
    'admin.businesses.detail.title': 'Detail firmy',
    'admin.businesses.subtitle': 'Všichni tenanti, kredity a feature flagy',
    'admin.businesses.search.placeholder': 'Název, IČO, email majitele…',
    'admin.businesses.empty': 'Žádné firmy',
    'admin.businesses.saved': 'Firma uložena.',
    'admin.businesses.flags': 'Nastavení a feature flagy',
    'admin.businesses.notes': 'Platformové poznámky',
    'admin.businesses.suspend.section': 'Pozastavení',
    'admin.businesses.suspend': 'Pozastavit firmu',
    'admin.businesses.unsuspend': 'Zrušit pozastavení',
    'admin.businesses.suspended': 'Firma pozastavena.',
    'admin.businesses.unsuspended': 'Pozastavení firmy zrušeno.',
    'admin.businesses.related.people': 'Lidé',
    'admin.businesses.related.workplaces': 'Pracoviště',
    'admin.businesses.related.positions': 'Pozice',
    'admin.businesses.related.jobs': 'Inzeráty',
    'admin.businesses.related.contracts': 'Smlouvy',
    'admin.businesses.related.invites': 'Pozvánky',
    'admin.businesses.related.shifts': 'Nedávné směny',
    'admin.businesses.related.empty': 'Zatím nic.',
    'admin.businesses.role.owner': 'Vlastník',
    'admin.businesses.role.manager': 'Manažer',
    'admin.businesses.role.employee': 'Zaměstnanec',
    'admin.stats.contracts': 'Smlouvy',
    'admin.stats.jobs': 'Inzeráty',
    'admin.stats.shifts': 'Směny',
    'admin.stats.positions': 'Pozice',
    'admin.stats.invites': 'Pozvánky',
    'admin.invite.used': 'Použito',
    'admin.invite.open': 'Otevřená',
    'admin.invite.expired': 'Vypršela',
    'admin.credits.title': 'Kredity',
    'admin.credits.subtitle': 'Zůstatky a úpravy kreditů napříč platformou',
    'admin.credits.label': 'kreditů',
    'admin.credits.days.left': 'dní zbývá',
    'admin.credits.adjust': 'Upravit kredity',
    'admin.credits.reason': 'Důvod (povinný)',
    'admin.credits.apply': 'Provést',
    'admin.credits.adjusted': 'Kredity upraveny.',
    'admin.credits.recent': 'Nedávné transakce',
    'admin.credits.recent.empty': 'Zatím žádné transakce.',
    'admin.credits.seeMore': 'Zobrazit více',
    'admin.credits.seeLess': 'Zobrazit méně',
    'admin.credits.chart.title': 'Nákupy vs útraty',
    'admin.credits.chart.bought': 'Nákupy',
    'admin.credits.chart.spent': 'Útraty',
    'admin.credits.range.day': 'Den',
    'admin.credits.range.week': 'Týden',
    'admin.credits.range.month': 'Měsíc',
    'admin.credits.range.year': 'Rok',
    'admin.contacts.title': 'Kontaktní inbox',
    'admin.contacts.subtitle': 'Poptávky z marketingového webu',
    'admin.contacts.empty': 'Žádné kontakty',
    'admin.contacts.count': 'odeslání',
    'admin.contacts.notes': 'Poznámky admina',
    'admin.contacts.notes.placeholder': 'Interní poznámky…',
    'admin.contacts.detail.title': 'Kontaktní zpráva',
    'admin.contacts.saved': 'Kontakt uložen.',
    'admin.contacts.moved': 'Kontakt přesunut.',
    'admin.contacts.move.to': 'Přesunout do stage',
    'admin.contacts.stage.add': 'Přidat stage',
    'admin.contacts.stage.new': 'Nový stage',
    'admin.contacts.stage.created': 'Stage vytvořen.',
    'admin.contacts.stage.renamed': 'Stage přejmenován.',
    'admin.contacts.stage.deleted': 'Stage smazán.',
    'admin.contacts.stage.delete': 'Smazat stage',
    'admin.contacts.stage.delete.confirm': 'Smazat tento stage? Karty se přesunou do jiného stage.',
    'admin.contacts.stage.rename': 'Přejmenovat stage',
    'admin.contacts.stage.rename.hint': 'Dvojklik pro přejmenování',
    'admin.contacts.stage.color': 'Barva stage',
    'admin.contacts.stage.empty': 'Sem přetáhněte karty',
    'admin.contacts.field.firstName': 'Jméno',
    'admin.contacts.field.lastName': 'Příjmení',
    'admin.contacts.field.email': 'Email',
    'admin.contacts.field.companyName': 'Název firmy',
    'admin.contacts.field.companySize': 'Velikost firmy',
    'admin.contacts.field.subject': 'Předmět',
    'admin.contacts.field.message': 'Zpráva',
    'admin.contacts.field.newsletter': 'Produktové novinky',
    'admin.contacts.newsletter.yes': 'Chce novinky',
    'admin.contacts.newsletter.no': 'Nechce novinky',
    'admin.activity.title': 'Aktivita',
    'admin.activity.subtitle': 'Audit log napříč tenanty a platformovými akcemi',
    'admin.activity.platform.only': 'Jen platform admin',
    'admin.activity.empty': 'Zatím žádná aktivita',
    'admin.contracts.title': 'Smlouvy',
    'admin.contracts.subtitle': 'Všechny pracovní smlouvy napříč firmami',
    'admin.contracts.empty': 'Žádné smlouvy',
    'admin.jobs.title': 'Inzeráty',
    'admin.jobs.subtitle': 'Přehled job boardu pro moderaci',
    'admin.jobs.empty': 'Žádné inzeráty',
    'admin.jobs.applications': 'přihlášek',
    'admin.impersonation.start': 'Přihlásit jako',
    'admin.impersonation.started': 'Prohlížíte aplikaci jako tento uživatel.',
    'admin.impersonation.ended': 'Zpět v platform adminu.',
    'admin.impersonation.exit': 'Ukončit impersonaci',
    'admin.impersonation.banner': 'Impersonace: {name} ({email})',
    'admin.flag.managers.jobs': 'Manažeři mohou spravovat joby',
    'admin.flag.managers.create.wp': 'Manažeři mohou vytvářet pracoviště',
    'admin.flag.managers.edit.assigned': 'Manažeři mohou upravovat přiřazená pracoviště',
    'admin.flag.managers.edit.all': 'Manažeři mohou upravovat všechna pracoviště',
    'admin.field.id': 'ID uživatele',
    'admin.field.firstName': 'Jméno',
    'admin.field.lastName': 'Příjmení',
    'admin.field.email': 'Email',
    'admin.field.username': 'Uživatelské jméno',
    'admin.field.phone': 'Telefon',
    'admin.field.address': 'Adresa',
    'admin.field.language': 'Jazyk',
    'admin.field.shiftsView': 'Výchozí pohled směn',
    'admin.field.created': 'Vytvořeno',
    'admin.search': 'Hledat',
    'admin.filter.all': 'Vše',
    'admin.filter.status': 'Stav',
    'admin.filter.suspended': 'Pozastaveno',
    'admin.filter.active': 'Aktivní',
    'admin.badge.suspended': 'Pozastaveno',
    'admin.suspend.reason': 'Důvod',
    'admin.view.all': 'Zobrazit vše',
    'admin.prev': 'Předchozí',
    'admin.next': 'Další',
    'admin.cancel': 'Zrušit',
    'admin.working': 'Pracuji…',
    'admin.error.generic': 'Něco se pokazilo.',

    // Product tour
    'tour.help': 'Nápověda',
    'tour.next': 'Další',
    'tour.back': 'Zpět',
    'tour.skip': 'Přeskočit',
    'tour.finish.cta': 'Přejít do mé appky',
    'tour.finish.title': 'Jste připraveni',
    'tour.finish.body': 'Viděli jste, jak Ompli funguje na ukázkových datech. Pokračujte do svého skutečného prostředí - může být zatím prázdné, dokud sami něco nevytvoříte.',
    'tour.demo.banner.title': 'Ukázkové prostředí',
    'tour.demo.banner.body': 'Prohlížíte ukázková data. Vaše skutečná data zůstávají beze změny.',
    'login.demo.title': 'Firma Ompli – vyzkoušejte demo',
    'login.demo.subtitle': 'Vyberte roli. Změny platí jen v této session prohlížeče — do databáze se nic neukládá.',
    'login.demo.role.owner': 'Majitel',
    'login.demo.role.manager': 'Manažer',
    'login.demo.role.employee': 'Zaměstnanec',
    'login.demo.blurb.owner': 'Příjmení zní jako „šéf“ — pozici si zaslouží.',
    'login.demo.blurb.manager': 'Management i oblíbený řízek — vždycky má všechno obalené.',
    'login.demo.blurb.empBar': 'Barman — vždycky ví, odkud teče pivo.',
    'login.demo.blurb.empServe': 'Servírka — podnosy v příjmení i v ruce.',
    'login.demo.blurb.empWh': 'Skladník — přehled o každé paletě.',
    'login.demo.blurb.empHost': 'Hostesska / promo — úsměv v příjmení i na tváři.',
    'login.demo.success': 'Demo session spuštěna.',
    'login.demo.failed': 'Demo se nepodařilo spustit.',
    'demo.session.banner.title': 'Demo – Firma Ompli',
    'demo.session.banner.body': 'Úpravy platí jen v této session. Odhlášením se vše smaže.',
    'demo.session.switch': 'Role',
    'demo.session.reset': 'Obnovit data',
    'demo.session.switched': 'Demo role přepnuta.',
    'demo.session.switchFailed': 'Role se nepodařilo přepnout.',
    'demo.session.resetOk': 'Demo data znovu vygenerována.',
    'demo.session.resetFailed': 'Demo data se nepodařilo obnovit.',
    'tour.replay.title': 'Prohlídka aplikace',
    'tour.replay.description': 'Spusťte znovu průvodce s ukázkovými daty. Po dokončení se vrátíte do svého skutečného prostředí.',
    'tour.replay.cta': 'Spustit prohlídku znovu',
    'tour.replay.started': 'Spouštím prohlídku…',
    'tour.gate.title': 'Pokračovat na další stránku?',
    'tour.gate.body': 'Další: {page}. Můžete pokračovat stránku po stránce, nebo objevovat svým tempem.',
    'tour.gate.continue': 'Pokračovat na další stránku',
    'tour.gate.discover': 'Objevovat za chodu',
    'tour.gate.last.title': 'Prohlídka hotová',
    'tour.gate.last.body': 'Prošli jste všechny sekce. Kdykoli můžete přejít do skutečného pracovního prostoru.',
    'tour.next.progress': 'Další ({current} z {total})',
    'tour.ch.dashboard.filters.title': 'Filtry',
    'tour.ch.dashboard.filters.body': 'Zúžte dashboard podle období, stavu nebo osoby.',
    'tour.ch.dashboard.stats.title': 'Klíčové statistiky',
    'tour.ch.dashboard.stats.body': 'Rychlý přehled směn, lidí a naplněnosti.',
    'tour.ch.dashboard.activity.title': 'Nedávná aktivita',
    'tour.ch.dashboard.activity.body': 'Přihlášky, žádosti o odchod a další události týmu.',
    'tour.ch.dashboard.upcoming.title': 'Nadcházející směny',
    'tour.ch.dashboard.upcoming.body': 'Co má tým naplánované dál.',
    'tour.ch.dashboard.credits.title': 'Kredity',
    'tour.ch.dashboard.credits.body': 'Kredity platíte za obsazené směny. Zůstatek uvidíte tady nebo v Nastavení → Fakturace.',
    'tour.ch.reports.cats.title': 'Kategorie reportů',
    'tour.ch.reports.cats.body': 'Vyberte kategorii a prohlédněte dostupné typy reportů.',
    'tour.ch.reports.seg.title': 'Typ reportu',
    'tour.ch.reports.seg.body': 'Přepínejte mezi formáty reportů v této kategorii.',
    'tour.ch.reports.filters.title': 'Období',
    'tour.ch.reports.filters.body': 'Nastavte období, které chcete analyzovat.',
    'tour.ch.reports.export.title': 'Export CSV',
    'tour.ch.reports.export.body': 'Stáhněte aktuální report jako tabulku.',
    'tour.ch.shifts.view.title': 'Seznam nebo kalendář',
    'tour.ch.shifts.view.body': 'Přepínejte mezi seznamem a kalendářem směn.',
    'tour.ch.shifts.filters.title': 'Filtry',
    'tour.ch.shifts.filters.body': 'Filtrujte podle pracoviště, pozice nebo stavu.',
    'tour.ch.shifts.fill.title': 'Naplněnost',
    'tour.ch.shifts.fill.body': 'Na první pohled vidíte, jak jsou směny obsazené.',
    'tour.ch.shifts.presets.title': 'Šablony',
    'tour.ch.shifts.presets.body': 'Uložte si opakující se šablony směn pro rychlejší tvorbu.',
    'tour.ch.shifts.create.title': 'Vytvořit směnu',
    'tour.ch.shifts.create.body': 'Tímto tlačítkem naplánujete novou směnu.',
    'tour.ch.shifts.create.panel.title': 'Formulář nové směny',
    'tour.ch.shifts.create.panel.body': 'Tady vyplníte detaily. Během prohlídky se nic neukládá.',
    'tour.ch.ann.compose.panel.title': 'Napsat oznámení',
    'tour.ch.ann.compose.panel.body': 'Tady napíšete a cílíte zprávu. Během prohlídky se nic neodešle.',
    'tour.ch.employees.edit.panel.title': 'Úprava zaměstnance',
    'tour.ch.employees.edit.panel.body': 'V tomto panelu upravíte údaje. Během prohlídky se změny neukládají.',
    'tour.ch.contracts.send.panel.title': 'Odeslat smlouvu',
    'tour.ch.contracts.send.panel.body': 'Tady vyberete zaměstnance a detaily smlouvy. Během prohlídky se nic neodešle.',
    'tour.ch.workplaces.add.panel.title': 'Přidat pracoviště',
    'tour.ch.workplaces.add.panel.body': 'Tady vyplníte lokalitu. Během prohlídky se nic neukládá.',
    'tour.ch.positions.add.panel.title': 'Přidat pozici',
    'tour.ch.positions.add.panel.body': 'Tady definujete pozici. Během prohlídky se nic neukládá.',
    'tour.ch.jobs.create.panel.title': 'Zveřejnit nabídku',
    'tour.ch.jobs.create.panel.body': 'Tady vyplníte inzerát. Během prohlídky se nic nepublikuje.',
    'tour.ch.shifts.list.title': 'Karty směn',
    'tour.ch.shifts.list.body': 'Otevřete směnu a uvidíte přiřazení, odměnu a detaily.',
    'tour.ch.attendance.tabs.title': 'Typy žádostí',
    'tour.ch.attendance.tabs.body': 'Přepínejte mezi docházkou, odchody a výměnami.',
    'tour.ch.attendance.list.title': 'Čekající žádosti',
    'tour.ch.attendance.list.body': 'Prohlédněte, kdo čeká na vaše rozhodnutí.',
    'tour.ch.attendance.actions.title': 'Schválit nebo zamítnout',
    'tour.ch.attendance.actions.body': 'Těmito akcemi žádost potvrdíte nebo zamítnete.',
    'tour.ch.vacation.kpi.title': 'Nárok na dovolenou',
    'tour.ch.vacation.kpi.body': 'Sledujte nárok, vyčerpané a zbývající hodiny.',
    'tour.ch.vacation.form.title': 'Nová žádost',
    'tour.ch.vacation.form.body': 'Zadejte termín a hodiny dovolené.',
    'tour.ch.vacation.list.title': 'Žádosti o dovolenou',
    'tour.ch.vacation.list.body': 'Přehled otevřených a minulých žádostí; jako manažer je můžete schvalovat.',
    'tour.ch.ann.compose.title': 'Napsat oznámení',
    'tour.ch.ann.compose.body': 'Tímto tlačítkem napíšete oznámení pro tým.',
    'tour.ch.ann.tabs.title': 'Doručené a odeslané',
    'tour.ch.ann.tabs.body': 'Přepínejte mezi přijatými a odeslanými oznámeními.',
    'tour.ch.ann.list.title': 'Seznam oznámení',
    'tour.ch.ann.list.body': 'Otevřete položku a přečtěte si celou zprávu.',
    'tour.ch.messages.filters.title': 'Filtry kontaktů',
    'tour.ch.messages.filters.body': 'Filtrujte podle zdroje (kolegové, uchazeči) a role.',
    'tour.ch.messages.contacts.title': 'Kontakty',
    'tour.ch.messages.contacts.body': 'Vyberte osobu a otevřete konverzaci.',
    'tour.ch.messages.thread.title': 'Konverzace',
    'tour.ch.messages.thread.body': 'Čtěte vlákno a odpovídejte ve composeru dole.',
    'tour.ch.employees.invite.title': 'Pozvat zaměstnance',
    'tour.ch.employees.invite.body': 'Vygenerujte kód pozvánky, aby se noví lidé mohli připojit.',
    'tour.ch.employees.card.title': 'Karta zaměstnance',
    'tour.ch.employees.card.body': 'Stav, sazba a kontakt na jednom místě.',
    'tour.ch.employees.edit.title': 'Upravit zaměstnance',
    'tour.ch.employees.edit.body': 'Úpravou změníte sazbu, stav nebo pozice u této osoby.',
    'tour.ch.managers.invite.title': 'Pozvat manažery',
    'tour.ch.managers.invite.body': 'Vygenerujte pozvánku, aby se manažer připojil k firmě.',
    'tour.ch.managers.card.title': 'Karta manažera',
    'tour.ch.managers.card.body': 'Přiřazená pracoviště a úpravy oprávnění.',
    'tour.ch.contracts.filters.title': 'Filtry smluv',
    'tour.ch.contracts.filters.body': 'Filtrujte aktivní a neaktivní smlouvy.',
    'tour.ch.contracts.send.title': 'Odeslat smlouvu',
    'tour.ch.contracts.send.body': 'Tímto tlačítkem odešlete novou smlouvu.',
    'tour.ch.contracts.list.title': 'Smlouvy',
    'tour.ch.contracts.list.body': 'Sledujte stav, stahujte soubory a řešte podpisy.',
    'tour.ch.workplaces.add.title': 'Přidat lokalitu',
    'tour.ch.workplaces.add.body': 'Tímto tlačítkem přidáte nové pracoviště.',
    'tour.ch.workplaces.grid.title': 'Pracoviště',
    'tour.ch.workplaces.grid.body': 'Každá karta je místo, kde lidé mohou pracovat směny.',
    'tour.ch.positions.add.title': 'Přidat pozici',
    'tour.ch.positions.add.body': 'Tímto tlačítkem vytvoříte novou pracovní pozici.',
    'tour.ch.positions.grid.title': 'Pozice',
    'tour.ch.positions.grid.body': 'Procházejte a upravujte pozice firmy.',
    'tour.ch.jobs.create.title': 'Zveřejnit nabídku',
    'tour.ch.jobs.create.body': 'Tímto tlačítkem zveřejníte novou nabídku práce.',
    'tour.ch.jobs.apps.title': 'Přihlášky',
    'tour.ch.jobs.apps.body': 'Přejděte do schránky přihlášek a posuďte uchazeče.',
    'tour.ch.jobs.view.title': 'Seznam nebo mřížka',
    'tour.ch.jobs.view.body': 'Přepínejte zobrazení nabídek práce.',
    'tour.ch.jobapps.grid.title': 'Přihlášky',
    'tour.ch.jobapps.grid.body': 'Každá karta je uchazeč o jednu z vašich nabídek.',
    'tour.ch.jobapps.actions.title': 'Akce hodnocení',
    'tour.ch.jobapps.actions.body': 'Napište uchazeči, schvalte nebo zamítněte přihlášku.',
    'tour.ch.activity.view.title': 'Seznam nebo kalendář',
    'tour.ch.activity.view.body': 'Aktivitu zobrazíte jako feed nebo v kalendáři.',
    'tour.ch.activity.filters.title': 'Filtry aktivity',
    'tour.ch.activity.filters.body': 'Filtrujte podle typu - směny, dovolená, přihlášky a další.',
    'tour.ch.activity.feed.title': 'Feed aktivity',
    'tour.ch.activity.feed.body': 'Časová osa dění ve vaší firmě.',
    'tour.ch.settings.tabs.title': 'Záložky nastavení',
    'tour.ch.settings.tabs.body': 'Obecné, Preference, Osobní; u majitele i Funkce a Fakturace.',
    'tour.ch.settings.prefs.title': 'Preference',
    'tour.ch.settings.prefs.body': 'Jazyk, notifikace a znovuspuštění prohlídky.',
    'tour.ch.settings.billing.title': 'Fakturace a kredity',
    'tour.ch.settings.billing.body': 'Zůstatek kreditů a správa fakturace.',
    'tour.ch.help.title': 'Nápověda',
    'tour.ch.help.body': 'Dokumentaci otevřete kdykoli z postranního panelu.',
    'tour.ch.help.replay.title': 'Znovu spustit prohlídku',
    'tour.ch.help.replay.body': 'Prohlídku můžete znovu spustit v Nastavení → Preference.',
    'tour.ch.myjobs.list.title': 'Moje práce',
    'tour.ch.myjobs.list.body': 'Firmy a role, ke kterým patříte, plus připojení kódem.',
    'tour.ch.emp.dashboard.stats.body': 'Hodiny a výdělek za aktuální období.',
    'tour.ch.emp.earnings.title': 'Výdělek',
    'tour.ch.emp.earnings.body': 'Odhadovaný výdělek podle odpracované práce.',
    'tour.ch.emp.shifts.view.body': 'Procházejte volné směny v seznamu nebo kalendáři.',
    'tour.ch.emp.shifts.list.title': 'Volné směny',
    'tour.ch.emp.shifts.list.body': 'Přihlaste se na směny, které ještě potřebují lidi.',
    'tour.ch.emp.ann.list.body': 'Oznámení od zaměstnavatelů najdete tady.',
    'tour.ch.emp.myshifts.list.title': 'Moje směny',
    'tour.ch.emp.myshifts.list.body': 'Směny, na které jste už přiřazeni.',
    'tour.ch.emp.myshifts.leave.title': 'Odchod a docházka',
    'tour.ch.emp.myshifts.leave.body': 'Požádejte o odchod ze směny nebo řešte docházku.',
    'tour.ch.emp.vacation.form.body': 'Požádejte o dovolenou s daty a hodinami.',
    'tour.ch.emp.vacation.list.body': 'Sledujte stav svých žádostí o dovolenou.',
    'tour.ch.emp.contracts.list.title': 'Moje smlouvy',
    'tour.ch.emp.contracts.list.body': 'Prohlížejte a podepisujte smlouvy, které vám přišly.',
    'tour.ch.emp.cv.title': 'Správce CV',
    'tour.ch.emp.cv.body': 'Nahrajte a aktualizujte CV, které vidí zaměstnavatelé u přihlášek.',
    'tour.ch.emp.jobs.view.body': 'Procházejte nabídky práce a hlaste se.',
    'tour.owner.intro.nav.title': 'Navigace',
    'tour.owner.intro.nav.body': 'Tady najdete Směny, Zaměstnance, Job Board, Zprávy a další - vše pro řízení týmu.',
    'tour.owner.intro.upcoming.title': 'Nadcházející směny',
    'tour.owner.intro.upcoming.body': 'Zde uvidíte nadcházející směny. Teď ukazujeme ukázkové, abyste viděli, jak vypadá plný dashboard.',
    'tour.owner.intro.activity.title': 'Nedávná aktivita',
    'tour.owner.intro.activity.body': 'Přihlášky, žádosti o odchod ze směny a další události se zobrazují tady.',
    'tour.owner.intro.announcements.title': 'Oznámení',
    'tour.owner.intro.announcements.body': 'Důležité aktualizace pro tým najdete v Oznámeních. Kdykoli si můžete projít nepřečtené položky.',
    'tour.owner.intro.credits.title': 'Kredit',
    'tour.owner.intro.credits.body': 'Kreditem platíte za obsazené směny. Stav vidíte zde a dokoupit můžete v Nastavení → Fakturace.',
    'tour.owner.intro.create.title': 'Vytvořit směnu',
    'tour.owner.intro.create.body': 'Tímto tlačítkem naplánujete novou směnu a pozvete pracovníky k obsazení.',
    'tour.owner.intro.help.title': 'Nápověda',
    'tour.owner.intro.help.body': 'Nápovědu otevřete kdykoli. Prohlídku můžete znovu spustit v Nastavení → Preference.',
    'tour.employee.intro.nav.title': 'Navigace',
    'tour.employee.intro.nav.body': 'V postranním panelu najdete Moje směny, Zprávy, Job Board a nastavení profilu.',
    'tour.employee.intro.myshifts.title': 'Moje směny',
    'tour.employee.intro.myshifts.body': 'Tady vidíte nadcházející směny, na které jste přihlášeni, včetně místa pracoviště.',
    'tour.employee.intro.attendance.title': 'Odchod a docházka',
    'tour.employee.intro.attendance.body': 'Na kartě směny můžete požádat o odchod nebo spravovat účast. Check-in s polohou je na mobilu.',
    'tour.employee.intro.earnings.title': 'Hodiny a výdělky',
    'tour.employee.intro.earnings.body': 'Na dashboardu uvidíte odpracované hodiny a výdělky za měsíc, jakmile směny dokončíte.',
    'tour.employee.intro.announcements.title': 'Oznámení',
    'tour.employee.intro.announcements.body': 'Oznámení od zaměstnavatele a důležitá upozornění najdete tady.',
    'tour.mini.shifts.fill.title': 'Obsazenost',
    'tour.mini.shifts.fill.body': 'Na první pohled vidíte, jak jsou směny obsazené.',
    'tour.mini.shifts.create.title': 'Nová směna',
    'tour.mini.shifts.create.body': 'Vytvářejte směny pro pracoviště a sledujte, kdo se přihlásí.',
    'tour.mini.employees.invite.title': 'Pozvat zaměstnance',
    'tour.mini.employees.invite.body': 'Vygenerujte pozvánkový kód a sdílejte ho s novými členy týmu.',
    'tour.mini.employees.card.title': 'Karta zaměstnance',
    'tour.mini.employees.card.body': 'Každá karta ukazuje stav, sazbu a rychlé akce pro úpravu nebo odebrání.',
    'tour.mini.jobs.create.title': 'Vytvořit inzerát',
    'tour.mini.jobs.create.body': 'Publikujte nabídky na Job Boardu a přilákejte nové pracovníky.',
    'tour.mini.jobs.apps.title': 'Přihlášky',
    'tour.mini.jobs.apps.body': 'Projděte si přihlášky a pokračujte v konverzaci v Přihláškách.',
    'tour.mini.credits.buy.title': 'Nákup kreditů',
    'tour.mini.credits.buy.body': 'Tady dokoupíte kredity, abyste mohli dál plánovat a obsazovat směny.',
    'tour.mini.credits.notify.title': 'Preference',
    'tour.mini.credits.notify.body': 'Upravte jazyk, výchozí pohledy a další nastavení. Prohlídku můžete znovu spustit z této záložky.',
    'notfound.code': '404',
    'notfound.title': 'Tady zatím nic není',
    'notfound.description': 'Tato stránka neexistuje, nebo se na ní ještě pracuje.',
    'notfound.cta': 'Zpět domů',
    'cookies.title': 'Cookies',
    'cookies.body':
      'Používáme cookies kvůli přihlášení, preferencím a lepšímu zážitku. Kliknutím na Přijmout souhlasíte s našimi',
    'cookies.policy': 'zásadami cookies',
    'cookies.accept': 'Přijmout',
    'cookies.preferences': 'Preference',
    'cookies.close': 'Zavřít',
    'cookies.prefs.intro': 'Vyberte, které cookies smíme používat. Nezbytné cookies jsou vždy zapnuté, aby aplikace fungovala.',
    'cookies.prefs.necessary': 'Nezbytné',
    'cookies.prefs.necessary.desc': 'Přihlášení přes session, zabezpečení a základní preference v prohlížeči.',
    'cookies.prefs.optional': 'Volitelné',
    'cookies.prefs.optional.desc': 'Live chat a podobné nástroje, které zlepšují podporu na marketingových stránkách.',
    'cookies.prefs.save': 'Uložit preference',
    'cookies.prefs.back': 'Zpět',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => getPreferredLanguage());

  useEffect(() => {
    setLanguageState(getPreferredLanguage());

    const syncFromUser = () => setLanguageState(getPreferredLanguage());
    const onPreferenceUpdated = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail === 'cs' || detail === 'en') setLanguageState(detail);
      else syncFromUser();
    };

    window.addEventListener('storage', syncFromUser);
    window.addEventListener('userUpdated', syncFromUser);
    window.addEventListener('languagePreferenceUpdated', onPreferenceUpdated);
    return () => {
      window.removeEventListener('storage', syncFromUser);
      window.removeEventListener('userUpdated', syncFromUser);
      window.removeEventListener('languagePreferenceUpdated', onPreferenceUpdated);
    };
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    // Always scope to the currently logged-in user when present
    setPreferredLanguage(lang);
    window.dispatchEvent(new CustomEvent('languagePreferenceUpdated', { detail: lang }));
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    let text = translations[language][key] || key;
    
    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        text = text.replace(new RegExp(`{${param}}`, 'g'), String(value));
      });
    }
    
    return text;
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 